// Fundable — spike frontend. Plate long page on the Scribe backend.
// Proves: text+images+tables surface · the 6-element proposal template · a live Pressure Test.
// Backend is UNCHANGED — same Supabase project (JWT) + same /_coach proxy as scribe-l0.html.
import { createClient } from "@supabase/supabase-js";
import { parse as bestEffortParse, disableErrorLogging } from "best-effort-json-parser";
import MarkdownIt from "markdown-it";
import { TypeSafeClient, choice } from "@typesafe-ai/sdk";
import { printedBlocks, printedRuns, isPrintedList, plateNodeText } from "./plate/printed.mjs";
/* Carbon Design System web components — reskin (tokens: vendor/carbon-tokens.css).
   Register explicitly: Carbon's @customElement decorator registration does NOT survive
   esbuild bundling, so define each element ourselves (guarded against re-define). */
import CDSButton from "@carbon/web-components/es/components/button/button.js";
import CDSTextInput from "@carbon/web-components/es/components/text-input/text-input.js";
import CDSTextarea from "@carbon/web-components/es/components/textarea/textarea.js";
import CDSInlineLoading from "@carbon/web-components/es/components/inline-loading/inline-loading.js";
import CDSInlineNotification from "@carbon/web-components/es/components/notification/inline-notification.js";
import CDSSearch from "@carbon/web-components/es/components/search/search.js";
import CDSDropdown from "@carbon/web-components/es/components/dropdown/dropdown.js";
import CDSDropdownItem from "@carbon/web-components/es/components/dropdown/dropdown-item.js";
import CDSModal from "@carbon/web-components/es/components/modal/modal.js";
import CDSModalHeader from "@carbon/web-components/es/components/modal/modal-header.js";
import CDSModalHeading from "@carbon/web-components/es/components/modal/modal-heading.js";
import CDSModalLabel from "@carbon/web-components/es/components/modal/modal-label.js";
import CDSModalBody from "@carbon/web-components/es/components/modal/modal-body.js";
import CDSModalFooter from "@carbon/web-components/es/components/modal/modal-footer.js";
import CDSModalFooterButton from "@carbon/web-components/es/components/modal/modal-footer-button.js";
import CDSModalCloseButton from "@carbon/web-components/es/components/modal/modal-close-button.js";
import CDSLink from "@carbon/web-components/es/components/link/link.js";
import CDSIconButton from "@carbon/web-components/es/components/icon-button/icon-button.js";
import CDSTooltip from "@carbon/web-components/es/components/tooltip/tooltip.js";
import CDSTooltipContent from "@carbon/web-components/es/components/tooltip/tooltip-content.js";
import CDSTabs from "@carbon/web-components/es/components/tabs/tabs.js";
import CDSTab from "@carbon/web-components/es/components/tabs/tab.js";
import CDSTag from "@carbon/web-components/es/components/tag/tag.js";
import CDSToggle from "@carbon/web-components/es/components/toggle/toggle.js";
import CDSProgressIndicator from "@carbon/web-components/es/components/progress-indicator/progress-indicator.js";
import CDSProgressStep from "@carbon/web-components/es/components/progress-indicator/progress-step.js";
import CDSStructuredList from "@carbon/web-components/es/components/structured-list/structured-list.js";
import CDSStructuredListHead from "@carbon/web-components/es/components/structured-list/structured-list-head.js";
import CDSStructuredListHeaderRow from "@carbon/web-components/es/components/structured-list/structured-list-header-row.js";
import CDSStructuredListHeaderCell from "@carbon/web-components/es/components/structured-list/structured-list-header-cell.js";
import CDSStructuredListBody from "@carbon/web-components/es/components/structured-list/structured-list-body.js";
import CDSStructuredListRow from "@carbon/web-components/es/components/structured-list/structured-list-row.js";
import CDSStructuredListCell from "@carbon/web-components/es/components/structured-list/structured-list-cell.js";
for (const [tag, cls] of [
  ["cds-button", CDSButton],
  ["cds-text-input", CDSTextInput],
  ["cds-textarea", CDSTextarea],
  ["cds-inline-loading", CDSInlineLoading],
  ["cds-inline-notification", CDSInlineNotification],
  ["cds-search", CDSSearch],
  ["cds-dropdown", CDSDropdown],
  ["cds-dropdown-item", CDSDropdownItem],
  ["cds-modal", CDSModal],
  ["cds-modal-header", CDSModalHeader],
  ["cds-modal-heading", CDSModalHeading],
  ["cds-modal-label", CDSModalLabel],
  ["cds-modal-body", CDSModalBody],
  ["cds-modal-footer", CDSModalFooter],
  ["cds-modal-footer-button", CDSModalFooterButton],
  ["cds-modal-close-button", CDSModalCloseButton],
  ["cds-link", CDSLink],
  ["cds-icon-button", CDSIconButton],
  ["cds-tooltip", CDSTooltip],
  ["cds-tooltip-content", CDSTooltipContent],
  ["cds-tabs", CDSTabs],
  ["cds-tab", CDSTab],
  ["cds-tag", CDSTag],
  ["cds-toggle", CDSToggle],
  ["cds-progress-indicator", CDSProgressIndicator],
  ["cds-progress-step", CDSProgressStep],
  ["cds-structured-list", CDSStructuredList],
  ["cds-structured-list-head", CDSStructuredListHead],
  ["cds-structured-list-header-row", CDSStructuredListHeaderRow],
  ["cds-structured-list-header-cell", CDSStructuredListHeaderCell],
  ["cds-structured-list-body", CDSStructuredListBody],
  ["cds-structured-list-row", CDSStructuredListRow],
  ["cds-structured-list-cell", CDSStructuredListCell],
]) if (!customElements.get(tag)) customElements.define(tag, cls);

/* ---- backend (same as Scribe) ---- */
const SUPABASE_URL = "https://xbfnlamhwtvxxtofiwkc.supabase.co";
const SUPABASE_KEY = "sb_publishable_tRhQNQ2JQbxTPQ_NkcbsJw_Ce6FwBKS"; // publishable/anon; RLS protects data
// The id of the user a reset or invite link is FOR. The choose-a-password screen may only save for this
// user: if the link fails, supabase-js keeps any session already in this browser, and that would be
// someone else's account. Read from the access_token's `sub` in the hash before supabase-js clears it —
// unverified, so it is used ONLY to compare ids, never shown. Another tab learns it from the
// PASSWORD_RECOVERY event supabase-js broadcasts, whose session is the proof.
let linkUserId = (() => {
  const m = (location.hash || "").match(/[#&]access_token=([^&]+)/);
  if (!m) return "";
  try {
    const part = m[1].split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(part + "=".repeat((4 - part.length % 4) % 4))).sub || "";
  } catch { return ""; }
})();
const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

// ⛔ SHARE LINKS ARE GONE — 2026-09-07. Access is the roster and nothing else: the owner types a
// reviewer's email, claim_my_invites mints the membership on that person's next home load, and
// removing the email removes the access. There is no token, no #share= fragment, no "Set password"
// screen and no redeem-invite function.
// ⛔ NO SIGN-UP — 2026-10-14, his ruling: "sign ups are disabled. only i can make users for now." The
// browser has no way to create an account; he creates them in Supabase.

/* ---- the fundable-idea standard: the 8 sections ---- */
// Canonical section list (used by the importer's LLM mapping).
const SECTIONS = [
  { key: "goal", title: "1 · SMART goal", short: "Goal" },
  { key: "problem", title: "2 · The problem", short: "Problem" },
  { key: "stakeholders", title: "3 · Stakeholders & their key needs", short: "Stakeholders" },
  { key: "intervention", title: "4 · Intervention & evidence", short: "Intervention" },
  { key: "impact", title: "5 · Measurable impact (M&E)", short: "Impact" },
  { key: "budget", title: "6 · Budget — cost per outcome", short: "Budget" },
  { key: "people", title: "7 · People & backing", short: "People" },
  { key: "ask", title: "8 · The ask", short: "Ask" },
];

/* ---- cloud document state (autosave to Supabase `documents`) ---- */
// docState is the FULL `content` object of the open doc — the editor JSON is only ONE field of it.
// The cards (questions), their triage, the notes and the import's drawers live alongside `doc`, so saveDoc
// must MERGE (never rebuild from scratch, or it wipes them). Core shape: {format:"tiptap", type:"proposal",
// name, doc}. A document written before 2026-09-21 may still carry the coach AI's reflectCards, strengths
// and docGoal fields; nothing reads them now, and they ride along untouched.
let currentDocId = null;
let docState = null;
let saveTimer;
let lastSavedAt = null;      // documents.updated_at we last saw — optimistic-concurrency token for the versioned writer
let lastSavedContent = null; // stableStr() of the content we last wrote — to tell a benign updated_at bump (a trigger touching the row) from a real concurrent content change
// jsonb reorders keys, so a raw JSON.stringify comparison is unreliable — canonicalise (sort keys) both sides
function stableStr(o) {
  if (o === null || typeof o !== "object") return JSON.stringify(o);
  if (Array.isArray(o)) return "[" + o.map(stableStr).join(",") + "]";
  return "{" + Object.keys(o).sort().map((k) => JSON.stringify(k) + ":" + stableStr(o[k])).join(",") + "}";
}
function freshContent() { return { format: "tiptap", type: "proposal", name: "Untitled proposal", doc: null }; }
// Both sides of saveDoc's conflict check pass through here, so whatever it does it must do to both.
function normContent(c) { return c || {}; }
async function myUserId() { const s = await getSession(); return s ? s.user.id : null; }
let saveFailed = false;
let saveConflict = false; // true after a REAL concurrent-edit conflict: editing is locked to stop a keystroke→conflict→toast loop, and flushSave stops blocking navigation so the user can leave/reload
// AUDIT: #32 — saveTimer is a bare setTimeout id (never nulled), so it can't answer "is a save pending?" on
// its own. dirty tracks that explicitly: set on every edit, cleared only once a save round-trip succeeds.
let dirty = false;
function unsavedPending() { return (dirty || saveFailed) && !saveConflict; } // a real conflict has nothing left to save
// The quiet save sign beside the title (#saveSign; his decision and his words, 2026-10-14). It only reads: what the
// page itself believes about saving, never a variable the save code acts on. Empty until the open proposal's long page
// is up. Then "Locked" once a conflict has locked editing, "Retrying…" while a failed save retries, and "Saving…" while:
//   - an edit is unsaved (dirty: from the edit, through its delay, until its save lands);
//   - a call of the save (a push, or the re-read after a NULL push) is on its way;
//   - an input method is composing in the editor, or finished composing text the page has not taken as a change yet
//     (Slate applies a composition late: at compositionend, or on Android up to 5 s after it); a composition that left
//     its block's text as it was (cancelled) leaves nothing to wait for; a blur (300 ms on: Slate may still apply), the
//     page going, or a key pressed outside any composition (its compositionend never came) ends the wait;
//   - the editor changed after the latest save took its payload and, with nothing else pending, the page does not
//     equal the stored content (an edit made while an earlier save travelled, whose landing clears dirty before the
//     edit's own save starts; checked against the content, not a clock, so a late timer cannot make it "Saved" early).
//     That check runs at most once per change, at idle, no sooner than 300 ms after the latest change seen (Slate on
//     Android takes text up to 200 ms late), never inside a keystroke's handler; until it has run, "Saving…".
// "Saved" otherwise. Painted on a short interval, as each call starts and settles, and as the page changes; written only
// when its words change, so the polite live region is not re-announced. Only the editor counts as the page: the
// margin's own boxes save through their own buttons, which go through the save's calls.
// Display only (times from performance.now(), which never steps back): calls on their way per proposal id; when the
// latest save took its payload; the editor's last change (onDocUpdate) and the last change of any kind seen in it;
// the composition under way, on which page handle, and whether it changed its block.
const signPushes = new Map(); let signPayloadAt = 0, signChangedAt = 0, signTypedAt = 0, signTypedOn = null, signCompared = null, signComparing = false, signCompose = null;
function paintSaveSign() {
  const el = $("saveSign"); if (!el) return;
  const page = mountedPage();
  if (signCompose && (!page || page.handle !== signCompose.handle)) signCompose = null; // the page went (home, another proposal, sign-out)
  // A blur, past Slate's late apply — unless the composition changed its text and the page has not taken it yet (then,
  // as without a blur, the wait lasts until it has).
  if (signCompose && signCompose.blurAt && performance.now() - signCompose.blurAt >= 300
    && !(signCompose.changed && !signCompose.live && signCompose.endedAt > signChangedAt)) signCompose = null;
  let words = "";
  if (page) {
    const settling = !!signCompose && (signCompose.live || (signCompose.changed && signCompose.endedAt > signChangedAt)); // composed text the page has not taken yet
    let busy = dirty || !!signPushes.get(currentDocId) || settling;
    if (!busy && signTypedOn === currentDocId && signTypedAt > signPayloadAt) busy = signPageUnsaved(page);
    words = saveConflict ? "Locked" : saveFailed ? "Retrying…" : busy ? "Saving…" : "Saved";
  }
  if (el.textContent !== words) el.textContent = words;
}
// Whether the page differs from the content last stored, built as saveDoc builds what it sends (read only: a copy).
// Worked out once per change of the page or of what is stored, at idle; "unsaved" (true) until it has been.
function signPageUnsaved(page) {
  const key = signTypedAt, stored = lastSavedContent;
  if (signCompared && signCompared.key === key && signCompared.stored === stored) return signCompared.unsaved;
  if (!signComparing) {
    signComparing = true;
    (window.requestIdleCallback ? (f) => window.requestIdleCallback(f, { timeout: 500 }) : (f) => setTimeout(f, 0))(() => {
      // Slate on Android takes text up to 200 ms after its input event: no check sooner than 300 ms after the latest one.
      const wait = signTypedAt + 300 - performance.now();
      if (wait > 0) return void setTimeout(() => { signComparing = false; paintSaveSign(); }, wait);
      signComparing = false;
      const up = mountedPage();
      if (!up || signTypedAt !== key || lastSavedContent !== stored) return paintSaveSign(); // moved on: the next paint asks again
      if (dirty || signPushes.get(currentDocId)) return paintSaveSign(); // a save is coming or going anyway: no check needed now
      const now = { ...docState, format: "plate", doc: up.handle.getValue(), name: pageTitle(up) };
      delete now.sharedQuestions; delete now.sharedNotes;
      signCompared = { key, stored, unsaved: stableStr(now) !== stored };
      paintSaveSign();
    });
  }
  return true;
}
// A change seen in the editor, stamped for the sign (display only).
function signStamp() { signTypedAt = performance.now(); signTypedOn = currentDocId; }
// The page's own change (onDocUpdate's first statement): stamped, and painted once onDocUpdate has marked it unsaved.
function signChanged() { signStamp(); signChangedAt = signTypedAt; queueMicrotask(paintSaveSign); }
// A call of the save (the versioned writer, or its re-read), counted for the sign while it travels. payload: this call is the
// one that follows saveDoc taking its payload (the first push), not a re-read or retry of it. Passes its result through
// untouched; after it settles the sign is painted once the caller has done its bookkeeping (a task later).
// supabase.rpc returns a builder that sends the request each time it is awaited, so it is turned into one promise
// here, once, and that promise is what the caller awaits.
function paintWhile(id, push, payload = false) {
  const sent = Promise.resolve(push);
  if (payload) signPayloadAt = performance.now();
  signPushes.set(id, (signPushes.get(id) || 0) + 1); paintSaveSign();
  const settled = () => { signPushes.set(id, signPushes.get(id) - 1); setTimeout(paintSaveSign, 0); };
  sent.then(settled, settled);
  return sent;
}
// asItStands (hydrateFund only): with no page mounted, the row is sent as docState holds it — a fresh import's
// record, made before any page is up. Without it, a row with no page mounted writes nothing.
async function saveDoc(asItStands = false) {
  if (!currentDocId || !docState) return true; // nothing to save
  // Stage 6: the long page is the only editor. While it is up for this proposal, it is the record — its value
  // (getValue: no comment marks, normalised) and its first h1. A "tiptap" row becomes "plate" here; no TipTap copy
  // is kept (spec §11.21: Stage 8 is the point of no return). Only this save's landing changes the row, so a failed
  // or conflicting one converts nothing. docState always says what its doc is, so a retry sends the same conversion.
  // With no page mounted (its bundle still loading or failed, or the page already down) nothing is derived, for
  // any row: doc, name and every other key stay as loaded and nothing is written, so a "tiptap" row's later
  // conversion starts from the doc it was opened with. The TipTap editor is never read.
  const page = mountedPage(), value = page && page.handle.getValue();
  if (value) {
    docState.format = "plate";
    docState.doc = value;
    docState.name = pageTitle(page);
    // ⛔ The peer-review payload is no longer written, but a row saved before 2026-09-21 still carries
    // the last one, frozen — and the server's get_shared_doc still serves it to any account that was
    // invited then. Drop it on every save from the page, so that save clears it from the row.
    delete docState.sharedQuestions; delete docState.sharedNotes;
  } else if (!asItStands) return true;
  const id = currentDocId;                 // pin — the retry/conflict path must not save into a doc the user has since switched away from
  const payload = stableStr(docState);     // canonical snapshot of exactly what we're writing
  // Unchanged means no write: the content is what the row holds as last saved or opened. Not gated on `dirty`,
  // which the margin's writers never set. A margin choice that changes nothing therefore lands no save.
  if (payload === lastSavedContent && !saveFailed) { dirty = false; return true; }
  // The versioned writer retains push_document's saved-version check: a changed updated_at returns NULL.
  const { data, error } = await paintWhile(id, supabase.rpc("push_fundable_document", { p_id: id, p_name: docState.name, p_content: docState, p_expected_at: lastSavedAt }), true);
  if (error) {
    console.warn("saveDoc", error.message);
    if (!saveFailed) { saveFailed = true; toast("Not saved yet — retrying…"); }
    clearTimeout(saveTimer); saveTimer = setTimeout(() => { if (currentDocId === id) saveDoc(asItStands); }, 3000); // keep retrying, but only into the SAME doc
    return false;
  }
  if (data === null) { // updated_at moved under us — could be a real conflict, or just a trigger bumping the row (triggers touch updated_at, not content)
    const { data: row } = await paintWhile(id, supabase.from("documents").select("content, updated_at").eq("id", id).single());
    if (currentDocId !== id) return false; // user switched docs during the re-fetch
    if (row && stableStr(normContent(row.content)) === lastSavedContent) { // content is exactly what we last wrote → benign bump; adopt the fresh stamp and retry once (normContent to match how lastSavedContent was seeded — else empty reflectCards/strengths diverge and a benign reaction bump reads as a real conflict)
      lastSavedAt = row.updated_at;
      const { data: d2, error: e2 } = await paintWhile(id, supabase.rpc("push_fundable_document", { p_id: id, p_name: docState.name, p_content: docState, p_expected_at: lastSavedAt }));
      if (!e2 && d2) { lastSavedAt = d2; lastSavedContent = payload; dirty = false; plateSaveLanded(id); if (saveFailed) { saveFailed = false; toast("Saved"); } return true; }
    }
    // AUDIT: #32 rider — the underlying push_document WHERE clause (user_id = auth.uid()) silently excludes a doc the
    // CALLER doesn't own; a wrong-account re-login after a session death lands here too (verified against
    // the live RPC + RLS: it's a NULL return, not a thrown 401/403 — no infinite retry loop actually exists,
    // but the generic "changed somewhere else" message is flatly wrong for that case). row undefined means
    // the re-fetch found NOTHING readable under this account — distinguish that from a real content diff.
    saveConflict = true; // lock editing so keystrokes don't re-run debounce→conflict→toast forever; flushSave now lets the user leave/reload
    const up = mountedPage(); if (up) up.handle.setReadOnly(true); // the long page, read-only
    if (!row) toast("Couldn't save — you may be signed in as a different account than this proposal's owner. Sign in as the owner, or reload.", true);
    else toast("This proposal was changed somewhere else — reload the page to get the latest version before editing", true); // real conflict: don't clobber
    return false;
  }
  lastSavedAt = data; lastSavedContent = payload; dirty = false;
  plateSaveLanded(id); // the long page's draft (its crash buffer) follows the stored version
  if (saveFailed) { saveFailed = false; toast("Saved"); }
  return true;
}
// persist review-side changes (cards/goal) immediately, independent of the typing debounce
async function saveMeta() { clearTimeout(saveTimer); await saveDoc(); }
function onDocUpdate() { signChanged(); syncTitle(); dirty = true; clearTimeout(saveTimer); saveTimer = setTimeout(saveDoc, 800); }
// flush a pending debounced save BEFORE switching docs, so navigating mid-debounce doesn't drop edits
async function flushSave() {
  clearTimeout(saveTimer);
  if (saveConflict) return true; // in a real conflict the save will keep failing — don't trap the user; let them navigate away/reload
  if (!(currentDocId && docState)) return true; // nothing to flush
  const ok = await saveDoc();
  if (!ok) { toast("Couldn't save your latest edits — check your connection before leaving this proposal", true); return false; }
  return true;
}

/* ---- tiny helpers ---- */
const $ = (id) => document.getElementById(id);
const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
let toastTimer;
function toast(msg, hold) {
  const t = $("toast"); t.textContent = msg; t.classList.add("show");
  clearTimeout(toastTimer);
  if (hold !== true) toastTimer = setTimeout(() => t.classList.remove("show"), typeof hold === "number" ? hold : 2600); // hold=true stays until hideToast(); a number = ms
}
function hideToast() { clearTimeout(toastTimer); $("toast").classList.remove("show"); }

/* ---- the proposal's text, read off the Plate value ---- */
// The text of one block for sectionsDraft: applicant prose and honesty-marked
// additions alike. Table cells joined " · ", rows by newlines, so adjacent cell numbers never merge into one value;
// a list's lines run together, a break adds nothing, a diagram nothing — as v16 read the TipTap JSON of the same value.
// `e` is an entry of printedBlocks (plate/printed.mjs): a Plate block, or a list rebuilt from its lines.
function nodeSendText(e) {
  if (isPrintedList(e)) return e.items.map((it) => it.blocks.map(nodeSendText).join("")).join("");
  const runs = (children) => printedRuns(children).map((r) => r.text || "").join("");
  switch (e.type) {
    case "table":
      return (e.children || []).map((row) => (row.children || [])
        .map((cell) => printedBlocks(cell.children).map(nodeSendText).join("").trim()).filter(Boolean).join(" · "))
        .filter((r) => r.trim()).join("\n");
    case "code_block": return (e.children || []).map(plateNodeText).join("\n");
    case "blockquote": return printedBlocks(e.children).map(nodeSendText).join("");
    case "p": case "section_lead": case "h1": case "h2": case "h3": case "h4": case "h5": case "h6": return runs(e.children);
    case "hr": case "viz": case "systems_map": case "img": return "";
    default: return runs([{ text: plateNodeText(e) }]);
  }
}
// ---- Systems map (Meadows causal-loop diagram) ----
// A ring-layout SVG: nodes on a circle, signed curved edges (feedback loops read as arcs),
// the leverage point marked in the one accent colour. Data contract (from augmentor #1):
//   { nodes:[{id,label}], edges:[{from,to,sign:'+'|'-',label?}],
//     loops:[{type:'R'|'B',label}], leverage:<nodeId>, caption? }
// Monochrome via tokens; the leverage node is the ONLY coloured element (var(--accent)).
function wrapLabel(s, max) {
  const words = String(s || "").split(/\s+/).filter(Boolean); const lines = []; let cur = "";
  for (const w of words) { const t = cur ? cur + " " + w : w; if (t.length > max && cur) { lines.push(cur); cur = w; } else cur = t; }
  if (cur) lines.push(cur); return lines.length ? lines : [""];
}
function renderSystemsMap(data) {
  let d = data; if (typeof d === "string") { try { d = JSON.parse(d); } catch { d = null; } }
  if (!d || !Array.isArray(d.nodes) || !d.nodes.length) return '<div class="sysmap-empty">Systems map unavailable</div>';
  const nodes = d.nodes.slice(0, 8), edges = (Array.isArray(d.edges) ? d.edges : []).slice(0, 120);
  const idx = {}; nodes.forEach((n, i) => { idx[n.id] = i; });
  const N = nodes.length, CX = 300, CY = 210, R = N <= 3 ? 118 : N <= 5 ? 148 : 168;
  const charW = 6.4, lh = 14, padX = 11, padY = 8, fs = 12;
  // geometry per node
  const geo = nodes.map((n, i) => {
    const ang = -Math.PI / 2 + (i * 2 * Math.PI) / N;
    const lines = wrapLabel(n.label, 15);
    const w = Math.max(64, Math.round(Math.max(...lines.map((l) => l.length)) * charW) + padX * 2);
    const h = lines.length * lh + padY * 2;
    return { x: CX + R * Math.cos(ang), y: CY + R * Math.sin(ang), w, h, rx: w / 2 + 4, ry: h / 2 + 4, lines, lev: n.id === d.leverage };
  });
  // trim a ray from node centre A toward B to A's ellipse boundary
  const edgePt = (a, tx, ty) => {
    const dx = tx - a.x, dy = ty - a.y, L = Math.hypot(dx, dy) || 1, cs = dx / L, sn = dy / L;
    const t = 1 / Math.sqrt((cs / a.rx) ** 2 + (sn / a.ry) ** 2);
    return [a.x + cs * t, a.y + sn * t];
  };
  let paths = "", signs = "";
  edges.forEach((e) => {
    const a = geo[idx[e.from]], b = geo[idx[e.to]]; if (!a || !b) return;
    const [x1, y1] = edgePt(a, b.x, b.y), [x2, y2] = edgePt(b, a.x, a.y);
    const mx = (x1 + x2) / 2, my = (y1 + y2) / 2; // bow outward from ring centre so loops read as arcs
    let ox = mx - CX, oy = my - CY, oL = Math.hypot(ox, oy); if (oL < 1) { ox = -(y2 - y1); oy = x2 - x1; oL = Math.hypot(ox, oy) || 1; }
    const bow = Math.min(46, Math.hypot(x2 - x1, y2 - y1) * 0.22);
    const cpx = mx + (ox / oL) * bow, cpy = my + (oy / oL) * bow;
    paths += `<path d="M${x1.toFixed(1)} ${y1.toFixed(1)} Q${cpx.toFixed(1)} ${cpy.toFixed(1)} ${x2.toFixed(1)} ${y2.toFixed(1)}" class="sm-edge" marker-end="url(#sm-arrow)"/>`;
    const sgn = e.sign === "-" ? "−" : "+";
    signs += `<circle cx="${cpx.toFixed(1)}" cy="${cpy.toFixed(1)}" r="8" class="sm-sign-bg"/><text x="${cpx.toFixed(1)}" y="${(cpy + 3.5).toFixed(1)}" class="sm-sign">${sgn}</text>`;
  });
  let boxes = "";
  geo.forEach((g) => {
    const x = g.x - g.w / 2, y = g.y - g.h / 2;
    boxes += `<g class="sm-node"><rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${g.w}" height="${g.h}" rx="6"/>`;
    g.lines.forEach((ln, li) => { boxes += `<text x="${g.x.toFixed(1)}" y="${(g.y - ((g.lines.length - 1) * lh) / 2 + li * lh + fs / 3).toFixed(1)}" class="sm-lbl">${esc(ln)}</text>`; });
    if (g.lev) { const px = x + g.w - 15, py = y - 7.5; boxes += `<rect class="sm-lev-pill" x="${px.toFixed(1)}" y="${py.toFixed(1)}" width="18" height="15" rx="7.5"/><text class="sm-lev-pill-txt" x="${(px + 9).toFixed(1)}" y="${(py + 11).toFixed(1)}">L</text>`; } // 'L' pill marks the leverage point (was an accent-coloured box + star)
    boxes += `</g>`;
  });
  const loops = Array.isArray(d.loops) ? d.loops.filter((l) => l && l.label) : [];
  const loopLegend = loops.map((l) => `<span class="sm-loop"><b>${l.type === "B" ? "B" : "R"}</b> ${esc(l.label)}</span>`).join("");
  const levNode = nodes.find((n) => n.id === d.leverage);
  const legend = `<div class="sm-legend"><span><b>+</b> reinforces · <b>−</b> dampens</span><span><b>L</b> = leverage point${levNode ? ": " + esc(levNode.label) : ""}</span>${loopLegend}</div>`;
  const cap = d.caption ? `<figcaption class="sm-cap">${esc(d.caption)}</figcaption>` : "";
  return `<svg viewBox="0 0 600 420" class="sysmap-svg" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Systems map"><defs><marker id="sm-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 0L10 5L0 10z" class="sm-arrowhead"/></marker></defs>${paths}${signs}${boxes}</svg>${legend}${cap}`;
}
/* ---- section viz (fund/3): editable Carbon-charts nodes seeded by the import's `viz`. See fund/docs/fund-contract-v3.md.
   Phase 1 = ingest + live render (dendrogram first). The block JSON lives in the node's attrs (edit UI = Phase 2);
   nothing is baked, honoring the "viz must be editable" contract. Honesty `kind` → label prefix for now (colour
   refinement is a follow-up). ---- */
// honesty → a plain note; on the hand-SVG viz (flow) and the table renderers the label is truncated or
// terse, so this is where the full label + what the honesty kind MEANS actually lands.
const VIZ_KIND_NOTE = { read: "", asserted: "asserted — not yet evidenced", gap: "gap — not in the draft", gapbranch: "gap — not in the draft", target: "target — planned, not committed", fact: "", branch: "" }; // read = "" : "from the draft" is the default provenance → a note on every cell is noise; only the exceptions (asserted/target/gap) get one
function honestyTitle(label, kind, detail) {
  const note = VIZ_KIND_NOTE[kind];
  return String(label || "") + (detail ? " — " + detail : "") + (note ? " · " + note : "");
}
// legacy fallback (scatter/treemap/stackedbar — see renderLegacyTable below): a plain visible note, reusing
// the same VIZ_KIND_NOTE copy as everywhere else, in place of the removed Carbon-tooltip honesty line.
function legacyNoteHTML(kind) {
  const note = VIZ_KIND_NOTE[kind]; if (!note) return "";
  return `<span class="g-note${kind === "gap" || kind === "gapbranch" ? " g-gap" : ""}">${esc(note)}</span>`;
}
// LEGACY fallback for scatter/treemap/stackedbar — task #43 (2026-08-06): @carbon/charts removed entirely.
// Only dendrogram-table/flow/stackedbar are asked for now (the prompt library's D rules); these three
// Carbon-chart-only types are exactly the ones whose SVG had no print viewBox (clipped instead of scaling —
// see the removed LEGACY_CHART_TYPES/.viz-print-note). Mermaid was evaluated and rejected. So: ONE plain
// table renderer, on the shared .viz-table-wrap/.viz-grid/.g-* table classes (no new CSS, prints fine,
// never "renderer coming soon" for an older document's own real data).
function renderLegacyTable(holder, block) {
  const type = block && block.type;
  const unitPfx = (u) => (u ? esc(u) + " " : "");
  if (type === "stackedbar") {
    const series = (Array.isArray(block.series) ? block.series : []).filter((s) => s && s.label && Array.isArray(s.vals)).slice(0, 12);
    const years = ((block.years) || []).map(String).slice(0, 8);
    if (!series.length || !years.length) { holder.innerHTML = '<div class="viz-empty">Chart — no data</div>'; return; }
    const unit = unitPfx(block.unit || "₹");
    const head = `<th class="g-corner"></th>` + years.map((y) => `<th>${esc(y)}</th>`).join("");
    const body = series.map((s) => {
      const statusNote = s.status && s.status !== "requested" ? `<span class="g-note">${esc(s.status)}</span>` : "";
      const cells = years.map((_, i) => `<td><span class="g-val">${unit}${esc(String(s.vals[i] ?? ""))}</span></td>`).join("");
      return `<tr><td class="g-rowlabel">${esc(s.label)}${statusNote}</td>${cells}</tr>`;
    }).join("");
    const gaps = ((block.gaps) || []).filter(Boolean).slice(0, 8);
    const gapRow = gaps.length ? `<tr><td class="g-rowlabel">No line for</td><td colspan="${years.length}"><span class="g-val g-gap">${gaps.map((g) => esc(String(g))).join(", ")}</span></td></tr>` : "";
    holder.innerHTML = `<div class="viz-table-wrap"><table class="viz-grid"><thead><tr>${head}</tr></thead><tbody>${body}${gapRow}</tbody></table></div>`;
    return;
  }
  if (type === "treemap") {
    const groups = (Array.isArray(block.groups) ? block.groups : []).slice(0, 100);
    if (!groups.length) { holder.innerHTML = '<div class="viz-empty">Treemap — no data</div>'; return; }
    const unit = unitPfx(block.unit);
    const body = groups.map((g) => {
      const header = `<tr><td class="g-rowlabel" colspan="2">${esc(g.label || "")}${legacyNoteHTML(g.kind)}</td></tr>`;
      const rows = ((Array.isArray(g.tiles) ? g.tiles : []).slice(0, 200)).map((t) => `<tr><td>${esc(t.label || "")}</td><td><span class="g-val">${unit}${esc(String(t.value ?? ""))}</span></td></tr>`).join("");
      return header + rows;
    }).join("");
    holder.innerHTML = `<div class="viz-table-wrap"><table class="viz-grid"><thead><tr><th>Group / item</th><th>Value</th></tr></thead><tbody>${body}</tbody></table></div>`;
    return;
  }
  if (type === "scatter") {
    const points = (Array.isArray(block.points) ? block.points : []).slice(0, 200);
    if (!points.length) { holder.innerHTML = '<div class="viz-empty">Scatter — no data</div>'; return; }
    const xLabel = esc((block.xAxis && block.xAxis.label) || "X"), yLabel = esc((block.yAxis && block.yAxis.label) || "Y");
    const body = points.map((p) => `<tr><td class="g-rowlabel">${esc(p.label || "")}${legacyNoteHTML(p.kind)}</td><td>${esc(String(p.x ?? ""))}</td><td><span class="g-val">${esc(String(p.y ?? ""))}</span></td></tr>`).join("");
    holder.innerHTML = `<div class="viz-table-wrap"><table class="viz-grid"><thead><tr><th class="g-corner"></th><th>${xLabel}</th><th>${yLabel}</th></tr></thead><tbody>${body}</tbody></table></div>`;
    return;
  }
  holder.innerHTML = '<div class="viz-empty">Chart — no data</div>';
}
// timeline → a Carbon structured-list (When / Milestone / Detail). Was a cds-progress-indicator (collapsed),
// then a plain table; now the Carbon structured-list per house direction. Honesty: recurring steps tagged; kind note in detail.
function renderTimeline(holder, block) {
  const steps = (block && block.steps) || [];
  if (!steps.length) { holder.innerHTML = '<div class="viz-empty">Timeline — no data</div>'; return; }
  const rows = steps.map((s) => {
    const rec = s.recurring ? ` <span class="vt-tag">recurring</span>` : "";
    const note = VIZ_KIND_NOTE[s.kind];
    const honesty = note ? ` <span class="vt-note">· ${esc(note)}</span>` : "";
    return `<cds-structured-list-row>`
      + `<cds-structured-list-cell class="vt-when">${esc(s.when || "")}</cds-structured-list-cell>`
      + `<cds-structured-list-cell>${esc(s.label || "")}${rec}</cds-structured-list-cell>`
      + `<cds-structured-list-cell>${esc(s.what || "")}${honesty}</cds-structured-list-cell>`
      + `</cds-structured-list-row>`;
  }).join("");
  holder.innerHTML = `<cds-structured-list class="viz-slist">`
    + `<cds-structured-list-head><cds-structured-list-header-row>`
    + `<cds-structured-list-header-cell>When</cds-structured-list-header-cell>`
    + `<cds-structured-list-header-cell>Milestone</cds-structured-list-header-cell>`
    + `<cds-structured-list-header-cell>Detail</cds-structured-list-header-cell>`
    + `</cds-structured-list-header-row></cds-structured-list-head>`
    + `<cds-structured-list-body>${rows}</cds-structured-list-body>`
    + `</cds-structured-list>`;
}
// flow (ToC): columns L→R, nodes per column, edges; output→outcome dashed blue when the link is asserted/gap.
function renderFlow(holder, block) {
  const cols = ((block && block.columns) || []).slice(0, 40), nodes = ((block && block.nodes) || []).slice(0, 200), edges = ((block && block.edges) || []).slice(0, 400);
  if (!cols.length || !nodes.length) { holder.innerHTML = '<div class="viz-empty">Flow — no data</div>'; return; }
  // Layout MEASURES before it places: every label is wrapped first, then each column is stacked with a fixed
  // gap and H grows to the tallest column. The old even-fraction spacing ((H-40)*(k+1)/(m+1) against a
  // hardcoded H=340) gave a 6-node column 42.9px of pitch for 56px boxes, so boxes overlapped and the later
  // one painted over the earlier one's label.
  const W = 620, colW = W / cols.length, bw = 104, LH = 14, TOP = 30, BOT = 12, VGAP = 12, byId = {};
  const inCol = {}; cols.forEach((c) => (inCol[c.key] = []));
  const colOf = (n) => (inCol[n.col] ? n.col : cols[0].key); // reattach a node whose column was renamed/deleted → col 0 (else list is undefined → crash)
  nodes.forEach((n) => { inCol[colOf(n)].push(n); });
  // NO line cap: a box grows to hold its whole label, and H grows with it. A cap only ever bought a shorter
  // diagram, and it cost the text — the tooltip below is hidden in print (app.html's @media print), so a
  // capped label was cut on paper with the full version nowhere. His call, 2026-09-08.
  const wrap = (s, n) => { const w = String(s || "").split(/\s+/), out = []; let cur = ""; for (const word of w) { if (cur && (cur + " " + word).trim().length > n) { out.push(cur.trim()); cur = word; } else cur += " " + word; } if (cur.trim()) out.push(cur.trim()); return out; };
  const lineOf = new Map(); nodes.forEach((n) => lineOf.set(n, wrap(n.label, 14)));
  const boxH = (n) => lineOf.get(n).length * LH + 14;
  const colH = cols.map((c) => inCol[c.key].reduce((s, n) => s + boxH(n), 0) + VGAP * Math.max(0, inCol[c.key].length - 1));
  const contentH = Math.max(1, ...colH), H = TOP + contentH + BOT;
  cols.forEach((c, ci) => { // stack top-down; a short column centres against the tallest one
    let y = TOP + (contentH - colH[ci]) / 2;
    inCol[c.key].forEach((n) => { const bh = boxH(n); byId[n.id] = { x: ci * colW + colW / 2, y: y + bh / 2, bh, lines: lineOf.get(n), label: n.label, kind: n.kind }; y += bh + VGAP; });
  });
  let ePaths = "", boxes = "", hasDashed = false, hasGap = false;
  // Assumption compounds downstream: once a leap is assumed, every edge PAST it is assumed too (an impact built on
  // an unproven outcome is itself unproven — the "evidence frontier"). `tainted` = nodes reachable via an assumed
  // edge; an edge is dashed if it's intrinsically assumed OR its source is tainted. Fixed-point over the edges.
  const intrinsicAssumed = (e) => e.kind === "asserted" || e.kind === "gap";
  const tainted = new Set();
  edges.forEach((e) => { if (intrinsicAssumed(e) && byId[e.to]) tainted.add(e.to); });
  for (let pass = 0; pass < edges.length; pass++) { let changed = false;
    edges.forEach((e) => { if (byId[e.from] && byId[e.to] && tainted.has(e.from) && !tainted.has(e.to)) { tainted.add(e.to); changed = true; } });
    if (!changed) break; }
  edges.forEach((e) => {
    const a = byId[e.from], b = byId[e.to]; if (!a || !b) return;
    const assumed = intrinsicAssumed(e) || tainted.has(e.from); // dashed if the leap is assumed OR it's downstream of one
    if (assumed) hasDashed = true;
    ePaths += `<path class="fl-edge${assumed ? " assumed" : ""}" d="M${(a.x + bw / 2).toFixed(0)} ${a.y.toFixed(0)} L${(b.x - bw / 2).toFixed(0)} ${b.y.toFixed(0)}" marker-end="url(#${assumed ? "fl-arrow-assumed" : "fl-arrow"})"><title>${esc(honestyTitle(a.label + " → " + b.label, e.kind, e.label))}</title></path>`;
    if (e.label && !assumed) ePaths += `<text x="${((a.x + b.x) / 2).toFixed(0)}" y="${((a.y + b.y) / 2 - 5).toFixed(0)}" class="fl-elabel">${esc(e.label)}</text>`; // assumed edges carry 'ASSUMED' — but there's no room + the legend already says it, so skip the label
  });
  nodes.forEach((n) => {
    const g = byId[n.id]; if (!g) return; const lines = g.lines, bh = g.bh;
    const gap = n.kind === "gap" || n.kind === "gapbranch"; // a gap is a MISSING MEASURE, not an outcome → render as a dashed 'to-measure' chip, not a solid box
    if (gap) hasGap = true;
    // no <title> here — the label's tooltip is the cds-tooltip hit-layer below; two tooltips on one box compete
    boxes += `<g><rect class="fl-box${gap ? " gap" : ""}" x="${(g.x - bw / 2).toFixed(0)}" y="${(g.y - bh / 2).toFixed(0)}" width="${bw}" height="${bh}"/>`;
    if (gap) boxes += `<circle class="fl-measure" cx="${(g.x - bw / 2).toFixed(0)}" cy="${(g.y - bh / 2).toFixed(0)}" r="6"/>`; // dotted-circle 'to-measure' badge on the top-left corner — marks a gap to fill, not an outcome
    lines.forEach((ln, li) => { boxes += `<text x="${g.x.toFixed(0)}" y="${(g.y - ((lines.length - 1) * 14) / 2 + li * 14 + 4).toFixed(0)}" class="fl-lbl">${esc(ln)}</text>`; });
    boxes += `</g>`;
  });
  const colhead = cols.map((c, i) => `<text x="${(i * colW + colW / 2).toFixed(0)}" y="16" class="fl-col">${esc(c.label)}</text>`).join("");
  const legend = (hasDashed || hasGap) ? `<p class="fl-legend"><span class="lg-off"><b>Dashed line</b> = an assumed leap, not yet evidenced; a box marked [X] is a gap to fill.</span><span class="lg-on"><b>Green dashed</b> = an assumed leap; <b>green box</b> = a gap to fill.</span></p>` : "";
  // The label also reads through a real cds-tooltip rather than the SVG <title>. Carbon's tooltip
  // is a Lit custom element (cds-popover underneath) and cannot render inside <svg>, so it rides a transparent
  // HTML hit-layer laid over the diagram at the same coordinates — percentages of the viewBox, which map exactly
  // because the SVG is width:100%/height:auto with no letterboxing. The boxes stay SVG, so every .fl-box print
  // and Highlight-Gaps rule keeps working untouched.
  const tips = nodes.map((n) => {
    const g = byId[n.id]; if (!g) return "";
    const full = esc(honestyTitle(n.label, n.kind));
    return `<cds-tooltip class="fl-tip" align="top" autoalign style="left:${((g.x - bw / 2) / W * 100).toFixed(3)}%;top:${((g.y - g.bh / 2) / H * 100).toFixed(3)}%;width:${(bw / W * 100).toFixed(3)}%;height:${(g.bh / H * 100).toFixed(3)}%"><span class="fl-hit" tabindex="0" role="img" aria-label="${full}"></span><cds-tooltip-content>${full}</cds-tooltip-content></cds-tooltip>`;
  }).join("");
  // The node view gives every viz holder role="img", which makes its whole subtree presentational — the
  // tooltip triggers below would then be focusable but unannounced. The flow is a GROUP that contains a
  // picture: the <svg> keeps role="img", and the triggers sit outside it, so they keep their labels.
  holder.setAttribute("role", "group");
  holder.setAttribute("aria-label", "Theory-of-change flow diagram");
  holder.innerHTML = `<div class="fl-wrap"><svg viewBox="0 0 ${W} ${H}" class="fl-svg" role="img" aria-label="Section flow diagram" preserveAspectRatio="xMidYMid meet"><defs><marker id="fl-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 0L10 5L0 10z"/></marker><marker id="fl-arrow-assumed" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 0L10 5L0 10z"/></marker></defs>${colhead}${ePaths}${boxes}</svg><div class="fl-tips">${tips}</div></div>${legend}`;
}
/* ---- the problem (block type 'dendrogram') → a Mermaid mindmap ----
   His choice, 2026-09-24: the problem draws as a mindmap — the problem at the centre, its 2-3 drivers
   around it, each driver's evidence and [X] gaps beyond. Same {tree} data as before, same veTree form.

   ⛔ Mermaid is NOT in app.bundle.js — it would add 5.3 MB to every page load. build.mjs copies its
   chunked ESM build to vendor/mermaid/, and it is imported the first time a problem diagram paints,
   fetching only the chunks a mindmap needs. The URL is computed so esbuild leaves the import alone. */
let mermaidLoad = null;
function loadMermaid() {
  if (!mermaidLoad) {
    const url = new URL("vendor/mermaid/mermaid.esm.min.mjs", document.baseURI).href;
    mermaidLoad = import(url).then((m) => {
      m.default.initialize({ startOnLoad: false, securityLevel: "strict", theme: "base", themeVariables: MERMAID_THEME, themeCSS: MERMAID_CSS,
                             fontFamily: '"IBM Plex Sans", "Helvetica Neue", Arial, sans-serif' });
      return m.default;
    }).catch((e) => { mermaidLoad = null; throw e; });
    // ⚠️ A failed load lasts until the page is reloaded: the browser records the failed module in its
    // module map, so importing the same URL again rejects at once. Clearing mermaidLoad only lets a
    // failure in initialize() be retried.
  }
  return mermaidLoad;
}
/* ---- Plate spike (Stage 0) -------------------------------------------------------------------------
 * docs/superpowers/specs/2026-09-27-plate-editing-surface-design.md. React + Plate (vendor/plate/, built by
 * build.mjs) are loaded through mountPlate, which shows the long page, and by openDoc for a fresh import, which
 * the bundle makes into its record (hydrateFund). Stage 6: the long page is the only editor, and its value is the
 * record. Every proposal opens on it (there is no ?editor=plate switch any more; the TipTap editor is never given a
 * proposal). A row saved as content.format "plate" opens from its stored value; a fresh import is saved as "plate"
 * from the start; a "tiptap" row opens from tiptapToPlate(doc), and its first save from the page (issued once the
 * page is up) stores it as "plate", with no TipTap copy kept (saveDoc). The PDF prints from the long
 * page's value (exportPDF, platePage). The TipTap page and its chrome are gone (Stage 8). */
// plateGen moves on with every mountPlate and at the session's end, so a bundle load that resolves after
// either mounts nothing.
// A load that fails is not kept: the next open loads again. The browser keeps the failed module under its URL
// (see loadMermaid), so each retry asks for the bundle under a URL of its own (&retry=n). The stylesheet is
// added once.
let plateLoad = null, plateUnmount = null, plateGen = 0, plateRetries = 0, plateCss = false;
function loadPlate() {
  if (!plateLoad) {
    if (!plateCss) {
      const css = document.createElement("link");
      // PLATE_CSS_HASH and PLATE_BUNDLE_HASH are each file's sha256, defined by build.mjs: a new build is a new URL.
      css.rel = "stylesheet"; css.href = new URL(`vendor/plate/plate.css?v=${PLATE_CSS_HASH}`, document.baseURI).href;
      document.head.appendChild(css);
      plateCss = true;
    }
    const url = new URL(`vendor/plate/plate.bundle.js?v=${PLATE_BUNDLE_HASH}`, document.baseURI);
    if (plateRetries) url.searchParams.set("retry", plateRetries);
    plateLoad = import(url.href).catch((e) => { plateLoad = null; plateRetries++; throw e; });
  }
  return plateLoad;
}
// A diagram inside the long page is drawn by app.js's renderers, the ones the TipTap page used. The long page hands
// over a viz block ({type, …}) or a systems map ({nodes, edges, …}, no type — an object or its JSON
// string); a systems map draws as TipTap's node view drew it, in a .systems-map holder. What the renderer
// returns comes back (a promise for a diagram Mermaid draws), so the PDF export can wait for the drawing.
// Nothing reads it but the bundle.
window.__fundViz = (holder, block) => {
  let d = block;
  if (typeof d === "string") { try { d = JSON.parse(d); } catch { d = null; } }
  if (d && !d.type && Array.isArray(d.nodes)) { holder.classList.add("systems-map"); holder.innerHTML = renderSystemsMap(d); return; }
  return renderVizBlock(holder, d);
};
// The long page's session draft is stored as { basedOn, value }, basedOn being the stored proposal's
// updated_at as it was opened — so a draft never outlives the version it started from: once the row has
// moved on (re-imported, saved from elsewhere, even a trigger's bump), the draft is discarded and the
// page shows the stored text. Only a non-empty array of blocks counts as a value. Every sessionStorage
// touch sits in a try: in a browser that blocks storage, merely reading `sessionStorage` throws.
const PLATE_DRAFT = "fund-plate-draft:", PLATE_DRAFT_WAIT = 400; // ms of quiet before an edit is written
function readPlateDraft(key, basedOn) {
  try {
    const raw = sessionStorage.getItem(key);
    if (raw === null) return null;
    let d = null;
    try { d = JSON.parse(raw); } catch { d = null; }
    if (d && basedOn != null && d.basedOn === basedOn && Array.isArray(d.value) && d.value.length) return d.value;
    sessionStorage.removeItem(key); // stale or malformed: gone, so it cannot come back on a later mount
  } catch { /* storage blocked: no draft */ }
  return null;
}
// Writes wait for PLATE_DRAFT_WAIT ms of quiet (not every keystroke); flush() writes a pending edit at once.
function plateDraftWriter(key, basedOn) {
  let pending = null, timer = null;
  const flush = () => {
    clearTimeout(timer); timer = null;
    if (pending === null) return;
    const value = pending; pending = null;
    if (basedOn == null) return; // no version to tie it to: readPlateDraft would never take it back
    try { sessionStorage.setItem(key, JSON.stringify({ basedOn, value })); } catch { /* storage full or blocked: the draft is lost, the proposal is not */ }
  };
  // rebase(next): a save from the margin (Stage 5) moves the row's updated_at while the text stays unsaved, so
  // the draft moves with it — the stored one at once, a pending one when it is written. Its value is unchanged.
  const rebase = (next) => {
    const value = readPlateDraft(key, basedOn);
    basedOn = next;
    if (value && basedOn != null) try { sessionStorage.setItem(key, JSON.stringify({ basedOn, value })); } catch { /* storage full or blocked: as a write */ }
  };
  return { put(value) { pending = value; clearTimeout(timer); timer = setTimeout(flush, PLATE_DRAFT_WAIT); }, flush, rebase };
}
// One proposal's draft, gone (mountPlate: a "tiptap" row's draft was typed before its text was saved at all).
function dropPlateDraft(key) {
  try { sessionStorage.removeItem(key); } catch { /* storage blocked: nothing was stored */ }
}
// Sign-out takes every long-page draft in this tab with it. Keys are collected first: removing while
// walking key(i) can reorder the rest.
function clearPlateDrafts() {
  try {
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i++) { const k = sessionStorage.key(i); if (k && k.startsWith(PLATE_DRAFT)) keys.push(k); }
    for (const k of keys) sessionStorage.removeItem(k);
  } catch { /* storage blocked: nothing was stored */ }
}
// The session's end — sign-out from the File menu, or a SIGNED_OUT the auth library reports on its own:
// the long page comes down first, so a write still waiting out its delay lands before the clear instead of
// bringing a draft back after it; a bundle load still pending mounts nothing. Nothing can be saved once signed out,
// so a save still pending (a failed one's retry, the typing delay) goes too, and the unload warning with it.
function endPlateSession() {
  clearTimeout(saveTimer); dirty = false; saveFailed = false;
  plateGen++;
  plateMounted = null; // a mount whose bundle is still loading lets go of its draft writer too (mountPlate)
  if (plateUnmount) { plateUnmount(); plateUnmount = null; }
  clearPlateDrafts();
}
// While a long page is mounted, or its bundle is still loading, every save that lands for its proposal (a margin choice, Export PDF, going home,
// any saveDoc) moves the page's draft to the version it stored. From Stage 6 the draft is only a crash buffer:
// an edit typed after the save went out is in it and not in the row, and a draft left on the old version would
// be discarded at the next mount. saveDoc calls this with the id it pinned for that save, not the proposal open
// when it returns.
let plateMounted = null; // { docId, drafts } from mountPlate's call until the page comes down, or its load fails or is abandoned
// The long page while it is mounted (Stage 6): { docId, handle } — the bundle's mount handle (getValue, getTitle,
// setReadOnly, diagrams), which saveDoc and exportPDF read. Set when the page mounts, null once it
// comes down.
let platePage = null;
function plateSaveLanded(id) { if (plateMounted && plateMounted.docId === id) plateMounted.drafts.rebase(lastSavedAt); }
// The page mounted for the open proposal, or null (none, or one whose load outlived an open).
function mountedPage() { return platePage && platePage.docId === currentDocId ? platePage : null; }
// Its first h1.
function pageTitle(page) { return (page.handle.getTitle() || "").trim() || "Untitled proposal"; }
// The long page's margin (Stage 5) keeps, sets aside, restores and edits through app.js's own writers (the TipTap panel's, which Stage 8 removed),
// and resolves and reopens through setResolved — one writer per record. The writers do not say whether their
// save landed (they await saveMeta, which drops saveDoc's answer), and their records and saves are as the TipTap panel
// used them; so an action reads the answer off saveDoc's own bookkeeping. Each writer changes
// docState and starts its save in the tick it is called, so the content read just after the call is the
// content that save sends: the save landed when, for the same proposal and with no conflict, the stored
// version moved and the content last stored is that content. Anything else (a network error, a conflict,
// an empty text the writer refused, a suggestion that is not there, another proposal opened meanwhile)
// rejects. Either way the action settles with the four records as docState holds them then (copies, never
// docState's own objects): resolved with them, or rejected with them on the error as .records, since saveDoc
// retries a failed save on its own and the change may still land. (.records is null once another proposal
// is open: its records are not this page's.) The actions run one at a time, each starting when the one
// before it has settled, so two margin clicks never have two saves out at once.
function plateActions(docId) {
  const records = () => (currentDocId !== docId || !docState ? null : structuredClone({
    questionTriage: docState.questionTriage || {}, suggestionTriage: docState.suggestionTriage || {},
    questionResolved: docState.questionResolved || {}, questions: docState.questions || [], pair: docState.pair || null }));
  const attempt = async (write, args) => {
    const before = lastSavedAt;
    let landed = false, thrown;
    try {
      const done = write(...args), held = stableStr(docState);
      await done;
      landed = currentDocId === docId && !saveConflict && lastSavedAt !== before && lastSavedContent === held;
    } catch (e) { thrown = { cause: e }; /* the writer threw or refused: nothing saved; its own error goes on as .cause */ }
    if (landed) return records();
    throw Object.assign(new Error("not saved", thrown), { records: records() });
  };
  let queue = Promise.resolve();
  const saving = (write) => (...args) => {
    const run = queue.then(() => attempt(write, args));
    queue = run.catch(() => {});
    return run;
  };
  // A suggestion's key is "<qid>#<si>", split at its last "#" as the TipTap panel splits it. A key that is not
  // one, or names no suggestion of that question, is refused before anything is written.
  const suggestion = (key, status) => {
    const m = typeof key === "string" ? /^(.+)#(\d+)$/.exec(key) : null, si = m ? +m[2] : -1;
    const q = m && ((docState && docState.questions) || []).find((x) => x.id === m[1]);
    if (!(q && q.suggestions && q.suggestions[si])) return Promise.reject(new Error("no such suggestion"));
    return setSuggestionTriage(m[1], si, status);
  };
  return {
    setTriage: saving(setTriage),
    setSuggestionTriage: saving(suggestion),
    setResolved: saving(setResolved),
    saveQuestionEdit: saving(saveQuestionEdit),
    saveSuggestionEdit: saving(saveSuggestionEdit),
    setPair: saving(setPair),
  };
}
// The long page's place put away, on the way home (showHome): #plateRoot hidden, its loading line gone. mountPlate
// shows it again for the next proposal.
function hidePlateRoot() { $("plateRoot").hidden = true; hidePlateLoading(); }
// While the bundle loads, #plateRoot shows the home list's loading line, in its words and look (.home-loading).
// mountPlate puts it only into an empty #plateRoot: a page still mounted there is React's, and stays until the next
// one replaces it. It goes before a page mounts and on the way home (a failed load goes home too); a later mount's
// own line is never taken by an earlier load.
function showPlateLoading() { $("plateRoot").innerHTML = `<div class="home-loading">Loading…</div>`; }
function hidePlateLoading() { const line = $("plateRoot").querySelector(":scope > .home-loading"); if (line) line.remove(); }
function mountPlate() {
  // The document and the version it was opened at (openDoc seeds lastSavedAt from the row's updated_at) are fixed here, not read again when the bundle arrives or a draft is written, so a
  // proposal opened while this one loads never receives this one's page or edits.
  const docId = currentDocId, key = `${PLATE_DRAFT}${docId}`, basedOn = lastSavedAt, gen = ++plateGen;
  const plateRow = docState.format === "plate";
  // Both stored formats can have unsaved long-page text after a rejected legacy card save.
  // Restore only a draft tied to this exact saved version; readPlateDraft discards stale drafts.
  const drafts = plateDraftWriter(key, basedOn);
  // A save can land while the bundle loads (Export PDF, going home, a failed save's retry), so the writer follows
  // saves from here, not only once the page is up: a draft left on the old version would be discarded at the
  // next mount. It lets go when the load fails, when the mount is abandoned (another proposal, home, the
  // session's end, another mount) and when the page comes down. By then a later mount may hold its own
  // entry, and release() never clears that one.
  const mine = { docId, drafts }, release = () => { if (plateMounted === mine) plateMounted = null; };
  plateMounted = mine;
  const props = {
    docId, format: plateRow ? "plate" : "tiptap", doc: docState.doc, draft: readPlateDraft(key, basedOn),
    sections: SECTIONS.map((s) => ({ key: s.key, title: s.title })),
    questions: docState.questions || [], questionTriage: docState.questionTriage || {}, evidence: docState.evidence || [],
    suggestionTriage: docState.suggestionTriage || {}, questionResolved: docState.questionResolved || {}, pair: docState.pair || null,
    actions: plateActions(docId), // the margin's controls (Stage 5)
    onDraft: (value) => drafts.put(value),
    // Every change of the page's value: the topbar title, the unload warning and the same debounced save
    // onDocUpdate starts (TipTap's onUpdate started it). Only while this proposal is the open one.
    onChange: () => { if (currentDocId === docId) onDocUpdate(); },
    onExport: () => exportPDF(), // the contents list's Export PDF: the same export as the File menu's
    askImage: askImageUrl, // the toolbar's Insert image: app.js's image modal (#imgInsert)
    editDiagram: openVizEditor, // a diagram's Edit button: app.js's diagram editor (#vizEdit)
  };
  $("plateRoot").hidden = false;
  if (!plateUnmount) showPlateLoading();
  loadPlate().then((m) => {
    if (currentDocId !== docId) return release(); // another proposal was opened meanwhile, or home; its own mountPlate call mounts it
    if (gen !== plateGen) return release(); // the session ended, or another mount began, while the bundle loaded
    if (plateUnmount) plateUnmount(); // the page before this one; its release() leaves this mount's entry alone
    hidePlateLoading();
    const page = m.mount($("plateRoot"), props);
    platePage = { docId, handle: page };
    // The last edit is written when the page goes — home, another proposal, sign-out — and on a reload
    // or tab close, rather than lost inside the write delay. Unmount first: an edit it reports is caught.
    addEventListener("pagehide", drafts.flush);
    plateUnmount = () => { release(); platePage = null; removeEventListener("pagehide", drafts.flush); page.unmount(); drafts.flush(); };
    syncTitle();
    // The page is the record from here: a "tiptap" row is saved as "plate" now, not at the first edit (saveDoc
    // converts it; the row changes only if that save lands), and a "plate" row opened from its draft saves it.
    // A page that opened as stored writes nothing: unchanged means no write.
    saveMeta();
  }, (e) => {
    console.error("[fund] plate spike failed to load", e);
    release();
    if (gen !== plateGen) return; // the session ended, or another mount began: that screen is not this one's to change
    // There is no other page, never TipTap's, for any row: the open fails as a failed read does, and goes home.
    if (currentDocId === docId) { toast("Couldn't open that proposal"); showHome(); }
  });
}
// The mindmap source for a problem tree. Every label is quoted — tested 2026-09-24: a quoted label keeps
// [X], parentheses, apostrophes, ₹ and commas as written. A double quote or backtick would end the label,
// and Mermaid reads `#word;` anywhere in its source as an entity ("Ward #3; north" drew a control
// character), so # " ` go in as Mermaid's own entity codes #35; #quot; #96; — checked in Chrome: all
// three print exactly as the writer wrote them. Whitespace collapses to one line, since indentation IS
// the mindmap's structure.
// Classes go on their own line under the node (on the node's line they are a parse error): fund-driver
// marks the ring round the centre — Mermaid marks no depth on a node — and fund-gap marks an [X] gap.
function problemMindmapText(tree) {
  // The LLM sometimes buries the drivers under single-child wrapper nodes (root → "Drivers (stated)" →
  // [the drivers]). Descend through any such pure wrapper so the drivers sit around the centre.
  let top = tree;
  while (top && Array.isArray(top.children) && top.children.length === 1
         && Array.isArray(top.children[0].children) && top.children[0].children.length
         && top.children[0].children.some((c) => Array.isArray(c.children) && c.children.length)) {
    top = top.children[0];
  }
  // A label is plain text, never markdown: a backslash before punctuation is a markdown escape the writer
  // added (IDAF run 4 sent "Option B \- Founding Partner") and is dropped, as nbUnescapeMd does.
  const clean = (v) => String(v == null ? "" : v).replace(/\\([!-/:-@[-`{-~])/g, "$1")
    .replace(/\s+/g, " ").trim()
    .replace(/#/g, "#35;").replace(/"/g, "#quot;").replace(/`/g, "#96;"); // # first: the codes carry one
  const isGap = (n) => n && (n.kind === "gap" || n.kind === "gapbranch");
  const lines = ["mindmap", `  n0(("${clean(tree.label) || "Problem"}"))`];
  let n = 0;
  const add = (raw, depth) => {
    // Run 4 also sent some children as bare strings: a string is its own label.
    const node = typeof raw === "string" ? { label: raw } : raw;
    if (!node || typeof node !== "object" || n >= 150 || depth > 6) return;
    const text = clean(node.label);
    if (!text) return; // a node with nothing to say is not drawn, and neither is anything under it
    const pad = "  ".repeat(depth + 1), id = "n" + (++n);
    lines.push(depth === 1 ? `${pad}${id}("${text}")` : `${pad}${id}["${text}"]`);
    const cls = [depth === 1 ? "fund-driver" : "", isGap(node) ? "fund-gap" : ""].filter(Boolean).join(" ");
    if (cls) lines.push(`${pad}:::${cls}`);
    for (const c of Array.isArray(node.children) ? node.children : []) add(c, depth + 1);
  };
  for (const d of (top && Array.isArray(top.children)) ? top.children : []) add(d, 1);
  return lines.join("\n");
}
let mindmapSeq = 0;
// Draws Mermaid source into a holder. Returns a promise: the node view waits on it, so the PDF export
// never captures a half-drawn diagram.
/* One grey scheme for every Mermaid diagram, in the editor and in the PDF (proposalData passes the
   same theme and CSS to merman). His call, 2026-09-27: colour looked unprofessional — diagrams use only
   Carbon's grey steps (@carbon/colors gray 10–100), which print on a black-and-white printer. The roles
   are Carbon White-theme tokens: the centre background-inverse #393939 under text-inverse #ffffff; boxes
   layer-01 #f4f4f4 under text-primary #161616 with a text-secondary #525252 outline (#8d8d8d measured
   3.0:1 on the box fill — too faint on paper); lines and arrows #525252; the page #ffffff.
   ⚠️ Mermaid's base theme gives pie, sequence, state, gantt and xychart colours of their own (#fff4dd,
   #f4ddff, #ff0000 — measured in both renderers, 2026-09-27), so each is set here explicitly. */
const MERMAID_THEME = {
  ...Object.fromEntries(Array.from({ length: 13 }, (_, i) => [`cScale${i}`, "#f4f4f4"])),
  ...Object.fromEntries(Array.from({ length: 13 }, (_, i) => [`cScaleLabel${i}`, "#161616"])),
  git0: "#393939", gitBranchLabel0: "#ffffff", rootLabelColor: "#ffffff",
  background: "#ffffff", mainBkg: "#f4f4f4", secondBkg: "#e0e0e0", nodeBkg: "#f4f4f4", nodeBorder: "#525252", nodeTextColor: "#161616",
  primaryColor: "#f4f4f4", primaryTextColor: "#161616", primaryBorderColor: "#525252",
  secondaryColor: "#e0e0e0", secondaryTextColor: "#161616", secondaryBorderColor: "#525252",
  tertiaryColor: "#f4f4f4", tertiaryTextColor: "#161616", tertiaryBorderColor: "#525252",
  lineColor: "#525252", textColor: "#161616", titleColor: "#161616", edgeLabelBackground: "#ffffff",
  clusterBkg: "#ffffff", clusterBorder: "#525252", labelBackground: "#ffffff",
  // pie: eight distinct steps, light to dark; slice labels stay readable on each
  ...Object.fromEntries(["#f4f4f4", "#c6c6c6", "#8d8d8d", "#525252", "#e0e0e0", "#a8a8a8", "#6f6f6f", "#393939", "#f4f4f4", "#c6c6c6", "#8d8d8d", "#525252"].map((c, i) => [`pie${i + 1}`, c])),
  pieStrokeColor: "#161616", pieOuterStrokeColor: "#161616", pieTitleTextColor: "#161616", pieLegendTextColor: "#161616", pieSectionTextColor: "#161616", pieOpacity: "1",
  // sequence
  actorBkg: "#f4f4f4", actorBorder: "#525252", actorTextColor: "#161616", actorLineColor: "#525252", signalColor: "#161616", signalTextColor: "#161616",
  labelBoxBkgColor: "#f4f4f4", labelBoxBorderColor: "#525252", labelTextColor: "#161616", loopTextColor: "#161616",
  noteBkgColor: "#e0e0e0", noteBorderColor: "#525252", noteTextColor: "#161616", activationBkgColor: "#c6c6c6", activationBorderColor: "#525252", sequenceNumberColor: "#ffffff",
  // state
  stateBkg: "#f4f4f4", stateBorder: "#525252", stateLabelColor: "#161616", transitionColor: "#525252", transitionLabelColor: "#161616", specialStateColor: "#393939", innerEndBackground: "#393939", border1: "#525252", border2: "#525252", compositeBackground: "#ffffff", compositeBorder: "#525252", altBackground: "#e0e0e0",
  // gantt
  sectionBkgColor: "#f4f4f4", altSectionBkgColor: "#ffffff", sectionBkgColor2: "#e0e0e0", gridColor: "#c6c6c6", excludeBkgColor: "#e0e0e0",
  taskBkgColor: "#c6c6c6", taskBorderColor: "#525252", taskTextColor: "#161616", taskTextLightColor: "#161616", taskTextDarkColor: "#161616", taskTextOutsideColor: "#161616",
  activeTaskBkgColor: "#e0e0e0", activeTaskBorderColor: "#161616", doneTaskBkgColor: "#8d8d8d", doneTaskBorderColor: "#525252",
  critBkgColor: "#393939", critBorderColor: "#161616", todayLineColor: "#161616",
  // journey
  ...Object.fromEntries(Array.from({ length: 8 }, (_, i) => [`fillType${i}`, i % 2 ? "#e0e0e0" : "#f4f4f4"])),
  // quadrant
  quadrant1Fill: "#f4f4f4", quadrant2Fill: "#e0e0e0", quadrant3Fill: "#f4f4f4", quadrant4Fill: "#e0e0e0",
  quadrantPointFill: "#393939", quadrantExternalBorderStrokeFill: "#525252", quadrantInternalBorderStrokeFill: "#8d8d8d",
  // xychart
  xyChart: { backgroundColor: "#ffffff", titleColor: "#161616", xAxisLabelColor: "#161616", yAxisLabelColor: "#161616", xAxisLineColor: "#525252", yAxisLineColor: "#525252",
    xAxisTickColor: "#525252", yAxisTickColor: "#525252", xAxisTitleColor: "#161616", yAxisTitleColor: "#161616", plotColorPalette: "#8d8d8d, #393939, #c6c6c6, #6f6f6f" },
};
const MERMAID_CSS = [
  ".mindmap-node rect, .mindmap-node path, .mindmap-node circle { stroke: #525252; stroke-width: 1px; }",
  ".section-root rect, .section-root path, .section-root circle { fill: #393939 !important; stroke: #393939; }",
  ".section-root text, .section-root .nodeLabel, .section-root tspan { fill: #ffffff !important; color: #ffffff !important; font-weight: 600; }",
  "path.edge { stroke: #525252 !important; stroke-width: 1.5px !important; }",
  "[class*='node-line'] { stroke: #525252 !important; }",
  // journey draws its faces in a fixed colour no theme value reaches. ⚠️ Its actor dots stay green
  // in both renderers — neither theme, CSS nor journey.actorColours reached them
  // (measured 2026-09-27). The rules never ask NotebookLM for a journey diagram.
  "circle.face { fill: #f4f4f4 !important; }",
].join(" ");
function drawMermaid(holder, text, look) {
  const mine = String(++mindmapSeq);
  holder.dataset.mm = mine; // a later render of the same holder wins; an older one finishing late is dropped
  // Mermaid loads on first use and takes 2–5 s on a first tab; say so rather than show a blank block.
  holder.innerHTML = '<div class="viz-empty">Drawing the diagram…</div>';
  return loadMermaid()
    .then((mm) => mm.render("fund-mm-" + mine, text))
    .then(({ svg }) => { if (holder.dataset.mm === mine) holder.innerHTML = `<div class="mm-wrap${look ? " " + look : ""}">${svg}</div>`; })
    .catch((e) => {
      console.warn("mermaid", e);
      if (holder.dataset.mm === mine) holder.innerHTML = '<div class="viz-empty">viz failed to render</div>';
    });
}
// An older document's problem tree (type 'dendrogram'), drawn as a mindmap from the tree.
function renderProblemMindmap(holder, block) {
  const tree = block && block.tree;
  if (!tree || !Array.isArray(tree.children) || !tree.children.length) {
    holder.innerHTML = '<div class="viz-empty">Problem — no data</div>';
    return null;
  }
  // An older document's problem tree keeps its own look (app.html .mm-classic): its dashed [X] gap
  // outline is an honesty mark, and the shared colours would erase it.
  return drawMermaid(holder, problemMindmapText(tree), "mm-classic");
}
// A diagram NotebookLM wrote as Mermaid (type 'mermaid', {code}) — any kind it chose.
function renderMermaidCode(holder, block) {
  const code = block && typeof block.code === "string" ? block.code.trim() : "";
  if (!code) { holder.innerHTML = '<div class="viz-empty">Diagram — no data</div>'; return null; }
  // Cleaned here as well as at import, so a proposal imported before the clean-up draws right too. It
  // changes nothing the second time.
  return drawMermaid(holder, nbFixMermaid(code));
}
const VIZ_RENDERERS = { mermaid: renderMermaidCode, dendrogram: renderProblemMindmap, scatter: renderLegacyTable, treemap: renderLegacyTable, stackedbar: renderLegacyTable, timeline: renderTimeline, flow: renderFlow };
function renderVizBlock(holder, block) {
  try {
    const fn = block && VIZ_RENDERERS[block.type];
    if (!fn) { holder.innerHTML = `<div class="viz-empty">${esc(block ? block.type : "viz")} — renderer coming soon</div>`; return null; }
    return fn(holder, block); // what a renderer returns (a Carbon chart, Mermaid's promise) goes back to the long page's painter, which waits on it
  } catch (e) { holder.innerHTML = '<div class="viz-empty">viz failed to render</div>'; console.warn("viz render", e); return null; }
}
/* ---- viz edit UI (Phase 2): a Carbon modal with a structured form per block type. Edits mutate a deep
   clone (the working model); Save writes it back to the diagram's `block` (the long page: a Plate setNodes, saved
   with the page).
   Owner-editable per the fund/3 contract. ---- */
const VIZ_TYPE_LABEL = { mermaid: "Diagram", dendrogram: "Mindmap", flow: "Flow (ToC)", scatter: "Quadrant", treemap: "Treemap", timeline: "Timeline", stackedbar: "Stacked bar" };
// scatter/treemap are no longer asked for — kept here only as VIZ_EDITORS entries + VIZ_RENDERERS'
// renderLegacyTable fallback, so an OLDER document that still carries one stays editable and renders
// honestly, not blank. The goal grid was removed outright on 2026-09-24, at his instruction.
const VIZ_KINDS = [["fact", "Fact / measured"], ["branch", "Group"], ["gapbranch", "Group (is a gap)"], ["read", "Inferred read"], ["asserted", "Asserted"], ["gap", "Gap [X]"], ["target", "Target (planned)"]];
function veEl(tag, props, kids) {
  const e = document.createElement(tag);
  if (props) for (const k in props) { const v = props[k]; if (v == null) continue; if (k === "class") e.className = v; else if (k === "text") e.textContent = v; else if (k.slice(0, 2) === "on") e.addEventListener(k.slice(2).toLowerCase(), v); else e.setAttribute(k, v === true ? "" : v); }
  (Array.isArray(kids) ? kids : kids ? [kids] : []).forEach((c) => e.appendChild(typeof c === "string" ? document.createTextNode(c) : c));
  return e;
}
function veField(label, input) { return veEl("label", { class: "ve-field" }, [veEl("span", { class: "ve-flab" }, [label]), input]); }
function veRow(kids) { return veEl("div", { class: "ve-row" }, kids); }
function veText(val, on, ph) { const i = veEl("cds-text-input", { size: "sm", "hide-label": true, label: " ", value: val == null ? "" : String(val), placeholder: ph || "" }); i.addEventListener("input", (e) => on(e.target.value)); return i; }
function veNum(val, on) { const i = veEl("cds-text-input", { size: "sm", type: "number", "hide-label": true, label: " ", value: val == null ? "" : String(val) }); i.addEventListener("input", (e) => { const v = e.target.value; if (v === "") return; const n = Number(v); if (!isNaN(n)) on(n); }); return i; } // ignore empty/NaN so a cleared field keeps its prior value instead of silently becoming 0
function veKind(val, on) { const d = veEl("cds-dropdown", { size: "sm", "hide-label": true, label: " ", value: val || "fact" }); VIZ_KINDS.forEach(([v, t]) => d.appendChild(veEl("cds-dropdown-item", { value: v, text: t }))); d.addEventListener("cds-dropdown-selected", () => on(d.value)); return d; }
function vePick(val, on, opts) { const d = veEl("cds-dropdown", { size: "sm", "hide-label": true, label: " ", value: val || (opts[0] && opts[0][0]) || "" }); opts.forEach(([v, t]) => d.appendChild(veEl("cds-dropdown-item", { value: v, text: t }))); d.addEventListener("cds-dropdown-selected", () => on(d.value)); return d; }
function veBool(val, on, label) { const t = veEl("cds-toggle", { size: "sm", "label-text": label || "", ...(val ? { toggled: true } : {}) }); t.addEventListener("cds-toggle-changed", (e) => on(!!e.detail.toggled)); return t; }
function veIcoBtn(icon, on, title) { const b = veEl("cds-icon-button", { kind: "ghost", size: "sm", align: "left", onclick: on }); b.setAttribute("aria-label", title || ""); b.innerHTML = `<svg slot="icon" class="ico"><use href="#c-${icon}"/></svg>`; return b; }
// a titled, add/remove-able list section bound to an array (rows mutate items in place)
function veList(title, items, makeEmpty, renderRow) {
  const body = veEl("div", { class: "ve-rows" });
  const rerender = () => { body.innerHTML = ""; items.forEach((it, i) => body.appendChild(renderRow(it, i, () => { items.splice(i, 1); rerender(); }))); };
  rerender();
  return veEl("div", { class: "ve-list" }, [veEl("div", { class: "ve-list-h" }, [title]), body, veEl("cds-button", { kind: "ghost", size: "sm", onclick: () => { items.push(makeEmpty()); rerender(); } }, ["+ add"])]);
}
// dendrogram: a recursive node editor (indent by depth; label + honesty kind + add-child + remove)
function veTree(b) {
  if (!b.tree) b.tree = { label: "" };
  const wrap = veEl("div", { class: "ve-tree" });
  const rerender = () => { wrap.innerHTML = ""; walk(b.tree, 0, null, 0); };
  function walk(node, depth, parentArr, idx) {
    const row = veEl("div", { class: "ve-trow", style: `margin-left:${depth * 16}px` }, [
      veText(node.label, (v) => (node.label = v), "label"),
      veKind(node.kind || "fact", (v) => (node.kind = v)),
      veEl("cds-button", { kind: "ghost", size: "sm", onclick: () => { (node.children = node.children || []).push({ label: "" }); rerender(); } }, ["+ child"]),
    ]);
    if (parentArr) row.appendChild(veIcoBtn("trash", () => { parentArr.splice(idx, 1); rerender(); }, "Remove node"));
    wrap.appendChild(row);
    (node.children || []).forEach((c, i) => walk(c, depth + 1, node.children, i));
  }
  rerender();
  return wrap;
}
const VIZ_EDITORS = {
  // A Mermaid diagram is edited as its code. A save that no longer parses shows 'viz failed to render',
  // and the previous code is one Undo away.
  mermaid: (b) => {
    const box = veEl("cds-textarea", { rows: "14", "hide-label": true, label: " ", value: b.code == null ? "" : String(b.code) });
    box.addEventListener("input", (e) => (b.code = e.target.value));
    return veEl("div", {}, [veField("Mermaid code", box)]);
  },
  dendrogram: (b) => veTree(b),
  scatter: (b) => {
    b.xAxis = b.xAxis || { label: "", ticks: [] }; b.yAxis = b.yAxis || { label: "", lo: "", hi: "" }; b.points = b.points || [];
    return veEl("div", {}, [
      veField("X axis label", veText(b.xAxis.label, (v) => (b.xAxis.label = v))),
      veField("X ticks (comma-separated, low → high)", veText((b.xAxis.ticks || []).join(", "), (v) => (b.xAxis.ticks = v.split(",").map((s) => s.trim()).filter(Boolean)))),
      veField("Y axis label", veText(b.yAxis.label, (v) => (b.yAxis.label = v))),
      veRow([veField("Y low", veText(b.yAxis.lo, (v) => (b.yAxis.lo = v))), veField("Y high", veText(b.yAxis.hi, (v) => (b.yAxis.hi = v)))]),
      veList("Points (x, y are 0–1)", b.points, () => ({ label: "", x: 0.5, y: 0.5, kind: "fact" }), (p, i, del) => veRow([veText(p.label, (v) => (p.label = v), "label"), veNum(p.x, (v) => (p.x = v)), veNum(p.y, (v) => (p.y = v)), veKind(p.kind, (v) => (p.kind = v)), veIcoBtn("trash", del, "Remove point")])),
    ]);
  },
  treemap: (b) => {
    b.groups = b.groups || [];
    return veEl("div", {}, [
      veField("Unit", veText(b.unit, (v) => (b.unit = v))),
      veList("Groups", b.groups, () => ({ label: "", kind: "branch", tiles: [] }), (g, gi, delG) => veEl("div", { class: "ve-group" }, [
        veRow([veText(g.label, (v) => (g.label = v), "group label"), veKind(g.kind, (v) => (g.kind = v)), veIcoBtn("trash", delG, "Remove group")]),
        veList("Tiles (value = area)", g.tiles = g.tiles || [], () => ({ label: "", value: 0 }), (t, ti, delT) => veRow([veText(t.label, (v) => (t.label = v), "tile label"), veNum(t.value, (v) => (t.value = v)), veIcoBtn("trash", delT, "Remove tile")])),
      ])),
    ]);
  },
  timeline: (b) => {
    b.steps = b.steps || [];
    return veList("Steps", b.steps, () => ({ when: "", label: "", what: "", recurring: false }), (s, i, del) => veEl("div", { class: "ve-group" }, [
      veRow([veText(s.when, (v) => (s.when = v), "when"), veText(s.label, (v) => (s.label = v), "label"), veIcoBtn("trash", del, "Remove step")]),
      veField("What", veText(s.what, (v) => (s.what = v))),
      veBool(s.recurring, (v) => (s.recurring = v), "Recurring (dashed)"),
    ]));
  },
  flow: (b) => {
    b.columns = b.columns || []; b.nodes = b.nodes || []; b.edges = b.edges || [];
    const nodeOpts = () => b.nodes.map((n) => [n.id, n.label || n.id]);
    const colOpts = () => b.columns.map((c) => [c.key, c.label || c.key]);
    return veEl("div", {}, [
      veList("Columns (stages, left → right)", b.columns, () => ({ key: "", label: "" }), (c, i, del) => veRow([veText(c.key, (v) => (c.key = v), "key"), veText(c.label, (v) => (c.label = v), "label"), veIcoBtn("trash", del, "Remove column")])),
      veList("Nodes", b.nodes, () => ({ id: "n" + Math.random().toString(36).slice(2, 7), label: "", col: (b.columns[0] && b.columns[0].key) || "", kind: "fact" }), (n, i, del) => veRow([veText(n.id, (v) => (n.id = v), "id"), veText(n.label, (v) => (n.label = v), "label"), vePick(n.col, (v) => (n.col = v), colOpts()), veKind(n.kind, (v) => (n.kind = v)), veIcoBtn("trash", del, "Remove node")])),
      veList("Edges", b.edges, () => ({ from: "", to: "", kind: "fact", label: "" }), (e, i, del) => veRow([vePick(e.from, (v) => (e.from = v), nodeOpts()), vePick(e.to, (v) => (e.to = v), nodeOpts()), veKind(e.kind, (v) => (e.kind = v)), veText(e.label, (v) => (e.label = v), "edge label"), veIcoBtn("trash", del, "Remove edge")])),
    ]);
  },
  stackedbar: (b) => {
    b.years = b.years || []; b.series = b.series || []; b.gaps = b.gaps || []; b.colorby = b.colorby || "ramp";
    const nums = (v) => v.split(",").map((s) => parseFloat(s.trim())).map((n) => (isFinite(n) ? n : 0));
    return veEl("div", {}, [
      veRow([veField("Unit", veText(b.unit, (v) => (b.unit = v))),
             veField("Colour by", vePick(b.colorby, (v) => (b.colorby = v), [["ramp", "Category (budget)"], ["status", "Status (ask)"]]))]),
      veField("Years (comma-separated, left → right)", veText((b.years || []).join(", "), (v) => (b.years = v.split(",").map((s) => s.trim()).filter(Boolean)))),
      veList("Series — values comma-separated, one per year", b.series, () => ({ label: "", vals: [], status: "committed" }), (s, i, del) => veRow([
        veText(s.label, (v) => (s.label = v), "label"),
        veText((s.vals || []).join(", "), (v) => (s.vals = nums(v)), "vals per year"),
        vePick(s.status || "committed", (v) => (s.status = v), [["committed", "Committed"], ["requested", "Requested"], ["target", "Target"]]),
        veIcoBtn("trash", del, "Remove series"),
      ])),
      veField("Budget gaps — categories with no line (comma-separated)", veText((b.gaps || []).join(", "), (v) => (b.gaps = v.split(",").map((s) => s.trim()).filter(Boolean)))),
    ]);
  },
};
// The diagram editor: #vizEdit on a copy of `block`, as a promise of the edited block (Save) or of null (Cancel, the
// modal's own × or Escape — Carbon's cds-modal-closed — or another ask taking the modal over). The long page asks it,
// and writes the answer to its diagram with a Plate setNodes (props.editDiagram, plate/schema.jsx).
let vizAsk = null;
function openVizEditor(block) {
  if (vizAsk) vizAsk(null);
  const model = JSON.parse(JSON.stringify(block || {}));
  const body = $("vizEditBody"); body.innerHTML = "";
  $("vizEditTitle").textContent = "Edit " + (VIZ_TYPE_LABEL[model.type] || "diagram");
  // the insight/caption heading (the import writes v1; the coach edits it here) — saved into block.caption
  body.appendChild(veField("Insight / caption", veText(model.caption || "", (v) => (model.caption = v), "The takeaway this diagram makes")));
  const build = VIZ_EDITORS[model.type];
  body.appendChild(build ? build(model) : veEl("div", { class: "ve-none" }, ["No editor for this diagram type."]));
  const modal = $("vizEdit");
  return new Promise((resolve) => {
    const closed = () => finish(null);
    const finish = (edited) => {
      if (vizAsk !== finish) return;
      vizAsk = null;
      modal.removeEventListener("cds-modal-closed", closed);
      modal.open = false;
      resolve(edited);
    };
    vizAsk = finish;
    $("vizEditSave").onclick = () => finish(model);
    $("vizEditCancel").onclick = () => finish(null);
    modal.addEventListener("cds-modal-closed", closed);
    modal.open = true;
  });
}
// The topbar title: the long page's first h1 while it is up; the stored name while its page loads.
function syncTitle() {
  let title = "";
  const page = mountedPage();
  if (page) title = pageTitle(page);
  else if (docState) title = docState.name || "";
  const sid = currentDocId ? " · " + String(currentDocId).slice(0, 8) : ""; // short doc id — disambiguates same-named versions in the topbar
  $("doctitle").textContent = (title.trim() || "Untitled proposal") + sid;
}

// The image modal, for the long page's Insert image (through mountPlate's askImage):
// a promise of the https address typed, or null once it closes without one (Cancel, an empty field, its own × or
// Escape — Carbon's cds-modal-closed — or another ask taking the modal over).
let imageAsk = null; // the open ask's finish, while the modal is open for one
function askImageUrl() {
  if (imageAsk) imageAsk(null);
  const modal = $("imgInsert"), input = $("imgUrl");
  input.value = "";
  return new Promise((resolve) => {
    const closed = () => finish(null);
    const finish = (url) => {
      if (imageAsk !== finish) return;
      imageAsk = null;
      modal.removeEventListener("cds-modal-closed", closed);
      modal.open = false;
      resolve(url);
    };
    imageAsk = finish;
    const done = () => {
      const url = String(input.value || "").trim();
      if (!url) { finish(null); return; }
      // https only — blocks javascript:/data: schemes; a bare host still leaks the viewer's IP on load
      if (!/^https:\/\//i.test(url)) { toast("Only https image URLs are allowed"); return; } // keep the modal open so the user can fix it
      finish(url);
    };
    $("imgInsertBtn").onclick = done;
    $("imgCancel").onclick = () => finish(null);
    input.onkeydown = (e) => { if (e.key === "Enter") { e.preventDefault(); done(); } };
    modal.addEventListener("cds-modal-closed", closed);
    modal.open = true;
    setTimeout(() => input.focus(), 0);
  });
}

/* ---- auth (email + password, same project as Scribe) + view switching ----
   The email one-time code that used to live here is GONE, along with its resend button and cooldown
   ticker. Owner's ask-screen answers, 2026-08-20: "Stock Supabase", "Assume it is capped" (login
   email treated as limited to Supabase's default 2 messages per hour, project-wide), and — asked
   with the consequence spelled out — "Accept it", accepting that anyone the WhatsApp link is
   forwarded to can claim that reviewer's account. Possession of the link is the credential now. */
async function getSession() { const { data } = await supabase.auth.getSession(); return data.session; }

// three views: signin gate · home (proposal list) · app (editor). Topbar tools show only in the editor.
function setView(v) {
  $("signin").classList.toggle("hidden", v !== "signin");
  $("home").classList.toggle("hidden", v !== "home");
  $("app").classList.toggle("hidden", v !== "app");
  $("about").classList.toggle("hidden", v !== "about");
  const inApp = v === "app";
  $("doctitle").classList.toggle("hidden", !inApp);
  $("saveSign").classList.toggle("hidden", !inApp); paintSaveSign();
  // The Highlight and Show-visuals switches (#tglStack) are never shown: the long page drops them (spec §13.12).
  $("brand").classList.toggle("hidden", v === "signin"); // no menu until signed in
  $("acctWrap").classList.toggle("hidden", v === "signin"); // account widget hidden on the sign-in gate
  closeFileMenu(); closeAcctMenu();
  // (the resend-code cooldown ticker that used to be cleared here went with the one-time code itself)
  if (v !== "signin") renderAccount(); // refresh the avatar/email (or Sign in) on every home/app/admin entry
}
/* ---- sign-in: stock Supabase email + password ----------------------------------------------
   Owner's ask-screen answers, 2026-08-20, in order:
     · "Stock Supabase"      — signInWithPassword / resetPasswordForEmail / admin create. Nothing
                               invented. He rejected the previous hand-rolled flow with "use proven
                               methods for these. these are problems thats already solved."
     · "Assume it is capped" — treat login email as limited to Supabase's default 2 messages an
                               hour, project-wide. So the everyday paths must send NO email at all.
     · "Accept it"           — asked with the consequence written on the card, he accepted that
                               ANYONE THE WHATSAPP LINK IS FORWARDED TO CAN CLAIM THAT REVIEWER'S
                               ACCOUNT. Possession of the share link is the credential.

   Two modes, one button. setSigninMode() owns every visibility toggle, so the states cannot drift
   apart the way scattered classList calls did:
     password — email + password                   → "Sign in"       (everyday, NO email)
     recover  — a new password, after a reset or invite link → "Save password" (rare, ONE email for a reset)

   Email is sent on exactly one path — a forgotten password. */
const SI_MIN_PW = 8; // the number the screen promises. Supabase's own password policy is the only other check.
let siMode = "password";
// ⛔ The wording below is the owner's, adopted wholesale from the design review on his ask-screen
// answer "Adopt all ten" (2026-08-20). Words on screen are his decision — change them only on his
// say-so, not to tidy them.
function siIntroText(mode) {
  // Arriving from a reset email with no other context: say WHOSE password is being changed.
  if (mode === "recover") return recoverEmail
    ? `Choose a new password for ${recoverEmail}.`
    : `Choose a new password — at least ${SI_MIN_PW} characters.`;
  return "Sign in with your email and password.";
}
let recoverEmail = ""; // filled from the recovery session, so the recover screen can name the account
// Field-level error, Carbon's own idiom (invalid + invalid-text on cds-text-input), for problems
// that belong TO a field. Design review: the form used a bottom-anchored auto-dismissing toast for
// everything, including prerequisites — and a fixed-bottom toast can sit behind a phone's on-screen
// keyboard, which is the "I tapped it and nothing happened" case. An inline error stays until fixed.
function setFieldError(id, msg) {
  const el = $(id); if (!el) return;
  el.setAttribute("invalid", "");
  el.setAttribute("invalid-text", msg);
}
function clearFieldError(id) {
  const el = $(id); if (!el) return;
  el.removeAttribute("invalid");
  el.removeAttribute("invalid-text");
}
// A CONFIRMATION on the field — Carbon's helper-text, not invalid-text, because these are not
// errors and must not render red. Same reason as setFieldError though: measured on an iPhone 13
// profile, a bottom-anchored toast sits behind the keyboard, and BOTH of the messages using this
// are shown moments after typing, with the keyboard still up. The forgotten-password confirmation
// matters most — someone who cannot see it taps "Email me a reset link" again, spending another
// message from a quota we are told to assume is 2 an hour for the entire project.
function setFieldNote(id, msg) {
  const el = $(id); if (!el) return;
  el.setAttribute("helper-text", msg);
}
function clearFieldNote(id) { const el = $(id); if (el) el.removeAttribute("helper-text"); }
function clearSigninErrors() {
  for (const id of ["siEmail", "siPass", "siNew"]) { clearFieldError(id); clearFieldNote(id); }
}
// ⛔ Focusing a field in the SAME tick it is unhidden does not take. Tester measured it: after
// $("siNew").focus() on the recovery screen, document.activeElement was still BODY at 188ms, 341ms,
// 746ms, 1951ms and 4956ms — while an explicit .focus() at any of those moments worked. On a phone
// that means the keyboard never opens and the person sees a form they must tap before typing.
// (revealSignin's own $("siEmail").focus() DOES land, because that field was never hidden.)
// Two frames: one for the class change to apply, one for Carbon's element to be ready to take it.
function focusSoon(id) {
  requestAnimationFrame(() => requestAnimationFrame(() => { const el = $(id); if (el) el.focus(); }));
}
function setSigninMode(mode) {
  siMode = mode;
  clearSigninErrors(); // an inline error from the previous mode must not follow the person into this one
  const show = (id, on) => { const el = $(id); if (el) el.classList.toggle("hidden", !on); };
  show("siEmail", mode === "password");
  show("siPass", mode === "password");
  show("siForgot", mode === "password");
  show("siNew", mode === "recover");
  const btn = $("siBtn");
  if (btn) { btn.disabled = false; btn.textContent = mode === "password" ? "Sign in" : "Save password"; }
  const intro = $("siIntro"); if (intro) intro.textContent = siIntroText(mode);
}
function resetSigninForm() {
  for (const id of ["siEmail", "siPass", "siNew"]) { const el = $(id); if (el) el.value = ""; }
  setSigninMode("password");
}
function revealSignin() { // landing hero → the sign-in form
  $("lpCta").classList.add("hidden");
  // The header's own "Sign in" stays visible behind the open form, where it does nothing (its click
  // just re-runs this function) — and it is FIRST in tab order, ahead of the email field. Design
  // review measured both. Hide it once the form it opens is already on screen.
  $("lpTopSignin").classList.add("hidden");
  $("siForm").classList.remove("hidden");
  $("siEmail").focus();
}
function showSignin(reveal) {
  resetSigninForm(); // sets the intro line too, share-aware, via siIntroText
  $("lpCta").classList.remove("hidden"); // collapse to the hero CTA
  $("lpTopSignin").classList.remove("hidden"); // restored alongside the CTA; revealSignin hides it again
  $("siForm").classList.add("hidden");
  setView("signin");
  if (reveal) revealSignin(); // a mid-action session drop → straight to the form
}

/* ---- cloud documents (Supabase `documents`, tagged content.format "tiptap" or, from Stage 6, "plate") ---- */
async function listDocs() {
  const userId = await myUserId(); if (!userId) return [];
  const { data, error } = await supabase.from("documents")
    .select("id,name,updated_at,created_at").eq("user_id", userId).in("content->>format", ["tiptap", "plate"]).order("updated_at", { ascending: false }); // filter server-side + never pull the full content jsonb just to render names
  if (error) { console.warn("listDocs", error.message); return null; } // null = load error (distinct from [] = empty)
  return data || [];
}
function fmtDate(iso) {
  // date + time in IST (all users are India-based); e.g. "18 Jul 2026, 7:25 pm"
  try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" }); }
  catch { return ""; }
}
// Home list state — the loaded docs plus the client-side search + sort applied over them.
// (Ghost-style manageable list; not a §5.5 consequence-ordered review surface, so user sort is expected.)
let homeDocs = [];
let homeQuery = "";
let homeSort = "updated";
const HOME_EMPTY = `<div class="home-empty"><p>No proposals yet.</p><cds-button kind="primary" size="md" id="btnNewEmpty">New proposal</cds-button></div>`;
// The home list for the current homeDocs: the controls show only when there is something to control.
function paintHome() {
  $("homeControls").classList.toggle("hidden", !homeDocs.length);
  if (homeDocs.length) renderHomeList(); else paintHomeEmpty();
}
function paintHomeEmpty() { $("homeList").innerHTML = HOME_EMPTY; $("btnNewEmpty").onclick = () => openNewProp(); } // one shared handler with the topbar's #btnNew (spec §1)
function homeSortCmp(a, b) {
  if (homeSort === "name") return (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
  if (homeSort === "created") return new Date(b.created_at || b.updated_at) - new Date(a.created_at || a.updated_at);
  return new Date(b.updated_at) - new Date(a.updated_at); // updated-desc default
}
function renderHomeList() {
  const list = $("homeList");
  const q = homeQuery.trim().toLowerCase();
  let rows = q ? homeDocs.filter((d) => (d.name || "").toLowerCase().includes(q)) : homeDocs.slice();
  rows.sort(homeSortCmp);
  if (!rows.length) {
    if (q) list.innerHTML = `<div class="home-empty">No proposals match “${esc(homeQuery.trim())}”.</div>`;
    else paintHomeEmpty(); // genuinely empty (not a search miss) — consistent with the other two empty-state call sites
    return;
  }
  list.innerHTML = rows.map((d) => {
    const nm = d.name || "Untitled proposal";
    return `<div class="doc-row" data-id="${esc(d.id)}"><svg class="ico"><use href="#c-document"/></svg>` +
      `<span class="doc-name">${esc(nm)}</span>` +
      `<span class="doc-id">${esc(String(d.id).slice(0, 8))}</span>` +
      `<span class="doc-meta">${esc(fmtDate(d.updated_at))}</span>` +
      `<cds-button class="doc-del" size="sm" kind="ghost" has-icon-only data-id="${esc(d.id)}" data-name="${esc(nm)}" title="Delete proposal" aria-label="Delete proposal"><svg slot="icon" class="ico"><use href="#c-trash"/></svg></cds-button>` +
      `</div>`;
  }).join("");
  list.querySelectorAll(".doc-row").forEach((r) => (r.onclick = () => openDoc(r.dataset.id)));
  list.querySelectorAll(".doc-del").forEach((b) => (b.onclick = (e) => { e.stopPropagation(); askDelete(b.dataset.id, b.dataset.name); }));
}
function showAbout() { setView("about"); } // signed-in About view (account menu → About Fundable); setView closes the menu + paints the account widget
async function showHome() {
  if (!(await flushSave())) return; // failed save → stay on this doc, don't drop edits
  currentDocId = null;
  if (plateUnmount) { plateUnmount(); plateUnmount = null; } // the long page goes with the document view
  hidePlateRoot(); // and its place, with any loading line (also after a failed load, which comes here)
  setView("home");
  const author = await canAuthor(); // account role: reviewers get a review-only home (no controls)
  $("btnNew").classList.toggle("hidden", !author); // spec §1 — text-only entry point, hidden for reviewers
  const list = $("homeList");
  $("homeControls").classList.add("hidden");
  list.innerHTML = `<div class="home-loading">Loading…</div>`;
  const docs = await listDocs();
  if (docs === null) {
    homeDocs = [];
    list.innerHTML = `<div class="home-empty">Couldn't load your proposals — check your connection and <cds-button class="home-admin" id="homeRetry" size="sm" kind="ghost">try again</cds-button>.</div>`;
    const r = $("homeRetry"); if (r) r.onclick = showHome;
    return;
  }
  homeDocs = docs;
  homeQuery = ""; $("homeSearch").value = "";
  if (!homeDocs.length && !author) { // his decision, 2026-09-21: no text — nothing is shared with anyone now
    $("homeControls").classList.add("hidden"); list.innerHTML = ""; return;
  }
  paintHome();
}
// Delete a proposal — destructive + irreversible, so confirm first (RLS `documents_owner_all`
// is the real owner gate; this button is only UX). The .select() makes success honest: an empty
// result means RLS deleted nothing (not the owner / already gone) → report failure, no false toast.
let pendingDelId = null;
function askDelete(id, name) {
  pendingDelId = id;
  $("cfName").textContent = name || "Untitled proposal";
  $("confirmDel").open = true;
  $("cfDelete").focus();
}
function closeConfirm() { $("confirmDel").open = false; pendingDelId = null; }
async function doDelete() {
  const id = pendingDelId; if (!id) return;
  const btn = $("cfDelete"); btn.disabled = true; btn.textContent = "Deleting…";
  const { data, error } = await supabase.from("documents").delete().eq("id", id).select("id");
  btn.disabled = false; btn.textContent = "Delete proposal";
  if (error || !data || !data.length) { toast("Couldn't delete the proposal"); return; }
  closeConfirm();
  homeDocs = homeDocs.filter((d) => d.id !== id);
  paintHome();
  toast("Proposal deleted");
}
// ⛔ There is no blank "new proposal" path (Stage 6): a proposal starts from the import modal (openNewProp). newDoc,
// which only the debug hook (window.__fund) reached, made a "tiptap" row from TEMPLATE on the TipTap page; it went
// with the switch.

/* ---- New proposal — the NotebookLM JSON paste -----------------------------------------------
   The eight sections, their summaries and the diagram data are written outside Fundable, against
   the prompt library, and arrive as ONE JSON object. This parses that paste straight into the
   `fundPending` payload the mini used to push, so `hydrateFund` and everything below it are
   unchanged — the document is created here and materialized on first open exactly as before.

   The paste comes out of a chat UI, so the object is unwrapped before parsing: a fenced code
   block, and any prose sitting before or after it. Both runs measured so far were valid JSON
   inside that envelope. A paste the chat UI truncated is recovered with `best-effort-json-parser`
   rather than rejected — losing the whole document because the last section was cut mid-sentence
   is the worse failure — and the reader is told which sections actually arrived.

   ⛔ Nothing is repaired silently. A recovered paste says so, and a section that did not arrive is
   named rather than left to surface as an empty page in the export. ---- */
const NB_SCHEMA = "sections, sectionSummaries, viz, sourceFigures, addedOrCalculated, stillNeeded, movedOut, notUsed";
const NP_MIN = 40, NP_MAX = 2000000; // a JSON object, not prose: the floor only catches an empty paste
// ⚠️ A lenient parse is the NORMAL case here, not an error: the helper line runs one on every
// debounced keystroke while a paste is still incomplete, and the library reports each one to
// console.error. Its own switch turns that off; the outcome is reported to the reader instead.
disableErrorLogging();
const nbMd = new MarkdownIt({ html: false, linkify: false }); // html:false — raw HTML in the paste never reaches the editor

// ⛔ A section body may carry its own `#`/`##` headings, and the PDF walker treats EVERY level-2
// heading as a section boundary (proposalData) — one `## Sub-heading` would force a page break, open
// an untagged section and strand the rest of that section's question column. h1 is dropped from the
// export outright. So every heading inside a section body is demoted to h3, below the boundary.
for (const rule of ["heading_open", "heading_close"]) {
  nbMd.renderer.rules[rule] = (tokens, idx, options, env, self) => {
    const t = tokens[idx];
    if (Number(String(t.tag).slice(1)) < 3) t.tag = "h3";
    return self.renderToken(tokens, idx, options);
  };
}

// The marks being placed during the current render, or null. Set by nbRenderSection only.
let nbMarkState = null;

// A figure must not be flanked by another digit, nor by a separator sitting between digits —
// otherwise the item "50" matches inside "1,500" and marks the wrong number.
function nbFindFigure(hay, needle) {
  const digit = /[0-9]/;
  let i = hay.indexOf(needle);
  while (i !== -1) {
    const end = i + needle.length;
    const before = i > 0 ? hay[i - 1] : undefined;
    const after = end < hay.length ? hay[end] : undefined;
    const sepBefore = before !== undefined && /[.,]/.test(before) && i > 1 && digit.test(hay[i - 2]);
    const sepAfter = after !== undefined && /[.,]/.test(after) && digit.test(hay[end + 1] || "");
    if (!(before !== undefined && digit.test(before)) && !(after !== undefined && digit.test(after))
        && !sepBefore && !sepAfter) return i;
    i = hay.indexOf(needle, i + 1);
  }
  return -1;
}

// One text node, with every mark that fits it placed left to right. Everything outside a mark goes
// through the app's esc(), which escapes what markdown-it's default text rule does, plus `'`.
function nbMarkText(content) {
  let out = "", rest = content;
  for (;;) {
    let at = -1, mark = null;
    for (const m of nbMarkState) {
      if (m.placed && nbMarkState.some((o) => !o.placed && o.item === m.item)) continue; // let the duplicate take its turn
      // ⛔ Not `if (m.placed) continue`. A figure the writer declared derived is derived EVERY time
      // it appears: marking only the first occurrence left the rest bare, and numberCheck — which
      // strips fund-sug before computing what was invented — then reported that same figure as
      // fabricated. `placed` records that the mark found a home, not that it is spent.
      const i = nbFindFigure(rest, m.item);
      // At the SAME position the longer item wins: with "50" listed before "50 schools", taking
      // the shorter one consumed the digits and left "50 schools" for ever unmatched.
      if (i !== -1 && (at === -1 || i < at || (i === at && m.item.length > mark.item.length))) { at = i; mark = m; }
    }
    if (at === -1) return out + esc(rest);
    mark.placed = true;
    // A shorter item that this mark's text begins with describes the SAME figure — "50" against
    // "50 schools". The longer mark covers it, so it has been placed too; reporting it as a figure
    // missing from the text would name a figure the reader can see inside the mark just drawn.
    for (const m of nbMarkState) if (!m.placed && m !== mark && mark.item.startsWith(m.item)) m.placed = true;
    out += esc(rest.slice(0, at))
      + `<span class="fund-sug" data-kind="suggest" data-basis="${esc(mark.basis)}">${esc(mark.item)}</span>`;
    rest = rest.slice(at + mark.item.length);
  }
}
/* A whole block — a paragraph, a list item's line, a table row — marked because Jev placed a note on
   it (judgeMarks). Set by nbRenderSection only: [{line, basis, mark}], `line` a line of the section's
   markdown. A block is marked when one of those lines falls inside it.
   ⛔ The mark goes on the <p> or <tr> itself, the block-level form FundSugAttr, numberCheck's count and
   the PDF walker already read — so a row is ONE mark: counted once, filled once, one glyph. A span
   per run of text counted a five-cell row five times and printed a glyph in every cell. */
let nbBlockMarks = null;
/* ⛔ A tight list's paragraphs are hidden — markdown-it renders no <p>, so attributes set on one are
   never written. There the mark goes on the <li>, matched by the lines of that hidden paragraph (the
   item's own text, not a nested list below it); the hidden paragraph itself is then skipped. */
for (const type of ["paragraph", "tr", "list_item"]) {
  nbMd.renderer.rules[type + "_open"] = (tokens, idx, options, env, self) => {
    const t = tokens[idx];
    const inner = tokens[idx + 1];
    const map = type === "list_item" ? (inner && inner.type === "paragraph_open" && inner.hidden ? inner.map : null)
      : (t.hidden ? null : t.map);
    const hits = (nbBlockMarks && map) ? nbBlockMarks.filter((b) => b.line >= map[0] && b.line < map[1]) : [];
    if (hits.length) {
      for (const h of hits) h.placed = true;
      // Two notes on one block: both bases are kept, so neither note is placed without a trace.
      const basis = [...new Set(hits.map((h) => h.basis).filter(Boolean))].join("; ");
      t.attrJoin("class", "fund-sug");
      t.attrSet("data-kind", "suggest");
      if (basis) t.attrSet("data-basis", basis);
    }
    return self.renderToken(tokens, idx, options);
  };
}
nbMd.renderer.rules.text = (tokens, idx) =>
  (nbMarkState && nbMarkState.length ? nbMarkText(tokens[idx].content) : esc(tokens[idx].content));

/* A ```mermaid block in a section is a DIAGRAM, not code: it becomes a diagram block (type 'mermaid',
   {code}) in place, the element the editor's VizBlock parses. NotebookLM writes these itself (prompt:
   "where a diagram would help a reader understand a section"), choosing the kind of diagram. Each one
   the render emits is recorded in nbMermaidSeen, so the import can check it parses (nbCheckMermaid)
   and take out, by its exact HTML, one Mermaid refuses. Any other fence renders as code, as before. */
let nbMermaidSeen = null;
const nbFenceDefault = nbMd.renderer.rules.fence;
/* NotebookLM's two measured slips in a mindmap (Opus review of the IDAF import, 2026-09-27):
   - the centre written as `root Label`, or `root` on a line of its own above the label — Mermaid prints
     the word "root" in the centre box;
   - a placeholder in a label, `Annual loss rate [X]` — Mermaid reads `[X]` as a square node whose
     whole text is "X", so the label's words vanish.
   The centre becomes root((Label)). A placeholder keeps the label's words and says the figure is
   missing — "Annual loss rate — not yet known" (his wording and his choice, 2026-09-27). Runs at import,
   when a diagram is drawn and when it is exported, so earlier imports come out right; a second pass
   changes nothing. Other diagram kinds pass through untouched. */
function nbFixMermaid(code) {
  // A placeholder in brackets: [X], [x], [X%], [X lakh] — never a word that merely starts with X.
  const MM_PLACEHOLDER = /\[[xX](?:[^\]\w][^\]]*)?\]/;
  // Colour a diagram's author writes — an %%{init}%% theme, style / linkStyle / classDef with a CSS
  // property — would bypass the grey scheme (measured: pink and green came through both renderers). A
  // line counts only when it carries a property with a colon, so a label reading "style guide" stays.
  const colour = /^\s*(%%\{.*\}%%|(style|linkStyle)\s+\S+\s+[a-z-]+\s*:.*|classDef\s+\S+\s+[a-z-]+\s*:.*)\s*$/;
  const lines = code.replace(/\r\n?/g, "\n").split("\n").filter((l) => !colour.test(l));
  // The diagram's kind is the first line that is not a %% directive or comment.
  const kind = lines.findIndex((l) => l.trim() && !/^\s*%%/.test(l));
  if (kind < 0 || !/^\s*mindmap\b/.test(lines[kind])) return lines.join("\n");
  const unknown = (l) => {
    if (!MM_PLACEHOLDER.test(l)) return l;
    const ind = l.match(/^\s*/)[0];
    const words = l.slice(ind.length).split(new RegExp(MM_PLACEHOLDER.source, "g")).join(" ").replace(/\s+/g, " ").trim();
    return ind + (words ? `${words} — not yet known` : "Not yet known");
  };
  const body = lines.slice(kind + 1).map(unknown);
  const at = body.findIndex((l) => l.trim());
  if (at >= 0) {
    const m = body[at].match(/^(\s*)root\b\s*(.*)$/);
    // A node written with a shape — c((Rapid loss)) — gives its label; bare text is the label itself.
    const labelOf = (t) => { const x = t.trim().match(/^\w*[([{]+\s*(.+?)\s*[)\]}]+$/); return (x ? x[1] : t).replace(/[()[\]{}"']/g, "").trim(); };
    const indent = (l) => l.match(/^\s*/)[0].length;
    // The rules allow the word root only as root((…)), so any other text after it is the centre's label.
    if (m && m[2] && !/^[([{]/.test(m[2])) body[at] = `${m[1]}root((${labelOf(m[2])}))`;
    else if (m && !m[2]) {
      // `root` alone. The next line is the centre's label only when nothing else sits at its indent —
      // otherwise it is the first branch (Who it affects, How big, Drivers…), and promoting it would
      // turn a branch into the headline. Left as written, Mermaid prints "root" but keeps the structure.
      const next = body.findIndex((l, i) => i > at && l.trim());
      if (next > 0 && !body.some((l, i) => i > next && l.trim() && indent(l) === indent(body[next]))) {
        body[at] = `${m[1]}root((${labelOf(body[next])}))`; body.splice(next, 1);
      }
    }
  }
  return [...lines.slice(0, kind + 1), ...body].join("\n");
}


nbMd.renderer.rules.fence = (tokens, idx, options, env, self) => {
  const t = tokens[idx];
  if (String(t.info || "").trim().toLowerCase() !== "mermaid") return nbFenceDefault(tokens, idx, options, env, self);
  const code = nbFixMermaid(String(t.content || "").trim());
  if (!code) return "";
  // Recorded WITHOUT the line break after it: a section's html is trimmed, so a diagram that ends the
  // section has none, and matching on it would leave a refused last diagram in place.
  const html = `<figure data-viz="${esc(JSON.stringify({ type: "mermaid", code }))}"></figure>`;
  if (nbMermaidSeen) nbMermaidSeen.push({ code, html });
  return html + "\n";
};

/* Render one section, placing that section's honesty marks as it goes.
   ⛔ Marks are placed in text nodes only, and never by guesswork: an item that does not appear as a
   standalone figure is returned unplaced and reported to the reader. A figure wrongly stamped
   "derived" is worse than one left unmarked. */
function nbRenderSection(mdText, wanted, blocks) {
  nbMarkState = wanted.map((w) => ({ item: w.item, basis: w.basis, src: w, placed: false }));
  nbBlockMarks = blocks && blocks.length ? blocks.map((b) => ({ ...b, placed: false })) : null;
  nbMermaidSeen = [];
  try {
    const html = nbMd.render(mdText).trim();
    const unplaced = nbMarkState.filter((m) => !m.placed).map((m) => m.src);
    const blocksPlaced = (nbBlockMarks || []).filter((b) => b.placed);
    return { html, placed: nbMarkState.length - unplaced.length, unplaced, blocksPlaced, mermaid: nbMermaidSeen };
  } finally {
    nbMarkState = null; // cleared even if the render throws, so the next section starts clean
    nbBlockMarks = null;
    nbMermaidSeen = null;
  }
}

// A fenced block wins when there is one: a chat reply often closes with a sentence of its own, and
// that sentence can contain a brace.
/* Every fenced block in the paste, in order, plus the tail of an unclosed one.

   ⛔ A closing fence is only a closing fence at the start of a line. A section body can itself
   contain a markdown code fence — a budget table, a sample — and inside a JSON string that fence
   is never preceded by a newline, because a raw newline cannot appear in a JSON string at all.
   Matching ``` anywhere ended the envelope inside such a section and silently lost the rest. */
function nbFencedBlocks(t) {
  const out = [];
  const closed = /(?:^|\n)```[\w-]*[ \t]*\r?\n([\s\S]*?)\r?\n```[ \t]*(?=\r?\n|$)/g;
  let m, lastEnd = 0;
  while ((m = closed.exec(t))) { out.push(m[1]); lastEnd = closed.lastIndex; }
  // A truncated paste never reaches its closing fence, so its opener is still worth stripping.
  const open = /(?:^|\n)```[\w-]*[ \t]*\r?\n/g;
  open.lastIndex = lastEnd;
  const o = open.exec(t);
  if (o) out.push(t.slice(o.index + o[0].length));
  return out;
}

/* Every plausible object in the paste, best first: each fenced block, then each `{` in the raw
   text. The caller takes the first that actually parses into a proposal, so a brace in the chat's
   intro sentence, or a command sample in an earlier fenced block, cannot win over the real object
   simply by coming first. Bounded, because this runs on every keystroke. */
function nbCandidates(text) {
  const t = String(text || "").trim();
  const out = [];
  const add = (chunk) => { const o = nbObjectSlice(chunk); if (o && !out.some((c) => c.text === o.text)) out.push(o); };
  for (const b of nbFencedBlocks(t).slice(0, 8)) add(b);
  let from = 0;
  // A reply can carry several braces in its prose before the object — a sentence with a placeholder,
  // a quoted key, an example. Bounded still, because this runs on every keystroke, but not so tight
  // that an ordinary reply exhausts it and the paste gets blamed for the scan stopping early.
  for (let n = 0; n < 12; n++) {
    const i = t.indexOf("{", from);
    if (i === -1) break;
    add(t.slice(i));
    from = i + 1;
  }
  return out;
}
// ⛔ Scan from the first `{` to ITS matching `}`, honouring strings and escapes. Taking the last
// brace in the paste instead cut the object short at a brace sitting inside section prose, and
// swallowed a trailing sentence that happened to contain one. A truncated paste never closes its
// object, so the scan runs off the end and the remainder is returned for best-effort recovery.
// `closed` says which of the two happened: a truncated paste and a malformed-but-complete one need
// different words to the reader, and nothing else tells them apart once the lenient parser has
// made sense of either.
function nbObjectSlice(t) {
  const i = t.indexOf("{");
  if (i === -1) return null;
  let depth = 0, inStr = false, escNext = false;
  for (let k = i; k < t.length; k++) {
    const c = t[k];
    if (escNext) { escNext = false; continue; }
    if (inStr) {
      if (c === "\\") escNext = true;
      else if (c === '"') inStr = false;
      continue;
    }
    if (c === '"') inStr = true;
    else if (c === "{") depth++;
    else if (c === "}" && --depth === 0) return { text: t.slice(i, k + 1), closed: true };
  }
  return { text: t.slice(i), closed: false };
}
/* Which section the paste stopped inside, or "" when it did not stop inside one.

   Walks the same way nbObjectSlice does and remembers the key most recently read at each depth. If
   the text runs out while still inside a string, that string is the value of the key at the current
   depth — and it is a section only when the key one level up is `sections`. Anything else (a cut in
   stillNeeded, a missing closing brace, an unescaped newline) leaves no unterminated string here
   and returns "". */
function nbCutSectionKey(t) {
  const keyAt = [];
  let depth = 0, inStr = false, escNext = false, buf = "", atKeyPos = false;
  for (let k = 0; k < t.length; k++) {
    const c = t[k];
    if (escNext) { escNext = false; buf += c; continue; }
    if (inStr) {
      if (c === "\\") { escNext = true; continue; }
      if (c === '"') {
        inStr = false;
        // A string followed by `:` is a key at this depth; anything else is a value.
        const rest = t.slice(k + 1);
        const next = rest.match(/^\s*(.)/);
        if (next && next[1] === ":") keyAt[depth] = buf;
        continue;
      }
      buf += c;
      continue;
    }
    if (c === '"') {
      inStr = true; buf = "";
      // Remember whether this string opened in key position, i.e. directly after `{` or `,`. A
      // paste that ends inside a half-written KEY has cut no section body: the previous key belongs
      // to a value that already closed.
      // ⛔ Walk back over the whitespace instead of slicing the prefix. Slicing made this quadratic
      // in the paste, and npHelperFor calls it on every keystroke while strict parsing fails: a
      // 1.26 MB paste of many short strings took 17 s per input event.
      let b = k - 1;
      while (b >= 0 && (t[b] === " " || t[b] === "\t" || t[b] === "\n" || t[b] === "\r")) b--;
      atKeyPos = b >= 0 && (t[b] === "{" || t[b] === ",");
      continue;
    }
    else if (c === "{" || c === "[") depth++;
    else if (c === "}" || c === "]") { keyAt.length = depth + 1; depth--; }
  }
  if (!inStr || atKeyPos) return "";
  return keyAt[depth - 1] === "sections" ? String(keyAt[depth] || "") : "";
}

// ⛔ best-effort-json-parser treats ``` as a markdown fence WHEREVER it meets one, including
// inside a string it is part-way through reading, and then throws "Unterminated string". A code
// fence inside an already-complete section thus refused the whole paste on the recovery path — the
// loss recovery exists to prevent. Hide the fences for that pass and restore them afterwards.
// ⚠️ The sentinel must be PRINTABLE. NUL was the obvious choice and is wrong: a raw control
// character in a string literal is itself a parse error ("Bad control character in string
// literal"), so hiding the fences that way simply traded one refusal for another. Private-use
// code points are ordinary string characters, and nothing a person pastes contains them.
const NB_FENCE = "";
function nbHideFences(t) { return t.split("```").join(NB_FENCE); }
function nbRestoreFences(v) {
  if (typeof v === "string") return v.split(NB_FENCE).join("```");
  if (Array.isArray(v)) return v.map(nbRestoreFences);
  if (v && typeof v === "object") {
    const o = {};
    for (const k of Object.keys(v)) o[nbRestoreFences(k)] = nbRestoreFences(v[k]);
    return o;
  }
  return v;
}

// A paste cut mid-way through a \uXXXX escape cannot be repaired as written — both parsers throw on
// it, losing every section that DID arrive. Drop the partial escape before the recovery attempt.
function nbTrimBrokenEscape(t) {
  return t.replace(/\\u[0-9a-fA-F]{0,3}$/, "").replace(/\\+$/, "");
}

// `sourceFigures` is what the writer read out of the source document. It stands in for the source
// itself so numberCheck can still ask "is this figure in the source?" — the one question it asks.
// A figure may be written as a JSON number as well as a string — both are figures. Anything else
// (an object, an array, null) carries no figure and is dropped.
function nbFigureText(v) { return typeof v === "string" ? v : (typeof v === "number" && isFinite(v) ? String(v) : ""); }
function nbSourceRows(figures) {
  return figures.map((f) => {
    // A bare string or number entry is the figure itself; an object carries the figure and its context.
    const figure = nbFigureText(f && typeof f === "object" ? f.figure : f);
    return figure ? { figure, context: nbFigureText(f && typeof f === "object" ? f.context : "") } : null;
  }).filter(Boolean);
}
/* Every figure written with no currency sign, in the sections and the source figures, each with the
   line it sits in: a sentence, a list item, or a table row with that table's header row. One entry
   per figure and line — a table repeating "1,00,000" in the same row asks once. */
// A code-fence line — ``` or ~~~, the two markdown-it opens a fence with. Lines inside a fence are a
// diagram's code (or other code), not prose.
const nbFenceLine = (t) => /^(```|~~~)/.test(t);
function nbFigureQuestions(mdSections, figureRows) {
  const out = [], seen = new Set();
  const add = (figure, text, header) => {
    const k = figureKey(figure) + "\u0000" + text;
    if (!seen.has(k)) { seen.add(k); out.push(header ? { figure, text, header } : { figure, text }); }
  };
  for (const md of Object.values(mdSections)) {
    let header = "";
    for (const line of md.split("\n")) {
      const t = line.trim();
      // A diagram's lines stay in: a figure drawn in a diagram is judged like any other (its line is
      // its context), because numberCheck reads diagram text too (diagramText).
      if (nbFenceLine(t)) { header = ""; continue; }
      if (!t || /^\|?[\s:|-]+\|?$/.test(t)) continue; // blank, or a table's --- separator row
      const row = t.startsWith("|");
      if (!row) header = "";
      else if (!header) header = t;
      for (const f of bareFigures(t)) add(f, t, row && t !== header ? header : "");
    }
  }
  for (const r of figureRows) for (const f of bareFigures(r.figure)) add(f, r.context ? `${r.figure} — ${r.context}` : r.figure, "");
  return out;
}
/* The passages of one section's markdown a note can be placed on: each paragraph line, list item and
   table body row, with its line number and, for a row, that table's header row. Headings are not
   passages — a mark goes on what the heading introduces. */
function nbPassages(md) {
  const out = [];
  let header = "", inFence = false;
  String(md || "").split("\n").forEach((line, i) => {
    const t = line.trim();
    if (nbFenceLine(t)) { inFence = !inFence; return; } // a diagram's code is not a passage
    if (inFence) return;
    if (!t || /^\|?[\s:|-]+\|?$/.test(t)) return; // blank, or a table's --- separator row
    if (/^#{1,6}\s/.test(t)) { header = ""; return; }
    if (t.startsWith("|")) {
      if (!header) { header = t; return; } // the header row names the columns: context, not a passage
      out.push({ line: i, passage: t, table_header: header });
    } else {
      header = "";
      out.push({ line: i, passage: t });
    }
  });
  return out;
}
function nbSourceHtml(rows) {
  return rows.map((r) => `<p>${esc(r.figure)}${r.context ? " — " + esc(r.context) : ""}</p>`).join("\n");
}

// Carries something: a non-empty array or object, a non-empty string, or any other non-null value.
function nbNonEmpty(v) {
  if (v == null || v === "") return false;
  if (Array.isArray(v)) return v.length > 0;
  return typeof v === "object" ? Object.keys(v).length > 0 : true;
}

/* The diagram blocks that carry something to draw, and a count of those that do not.

   A paste cut inside `viz` leaves a half block — `{"type":"flow","caption":""}` with its nodes
   gone — and ingestViz seeds that into the section as an empty diagram. Every other part of the
   payload says when it did not arrive whole, so this one does too, and the empty blocks are
   dropped rather than planted in the proposal. */
function nbViz(vizIn) {
  if (!vizIn || typeof vizIn !== "object" || Array.isArray(vizIn)) return { viz: null, dropped: 0, orphanKeys: [] };
  const hasData = (b) => b && typeof b === "object" && typeof b.type === "string" && b.type
    && Object.keys(b).some((k) => k !== "type" && k !== "caption" && nbNonEmpty(b[k]));
  const viz = {};
  let dropped = 0;
  const orphanKeys = [];
  for (const key of Object.keys(vizIn)) {
    const blocks = Array.isArray(vizIn[key]) ? vizIn[key] : [];
    const kept = blocks.filter((b) => { const ok = hasData(b); if (!ok) dropped++; return ok; });
    if (!kept.length) continue;
    viz[key] = kept;
    // ingestViz parks a diagram whose key matches no section at the END of the document rather
    // than dropping it (AUDIT #8), and says so only in the console. Name the key here, so a
    // diagram keyed "Budget" or "6" is known before the import rather than found after section 8.
    if (!SECTIONS.some((sec) => sec.key === key)) orphanKeys.push(key);
  }
  return { viz: Object.keys(viz).length ? viz : null, dropped, orphanKeys };
}

// The cards ingestCards will actually keep. A bare string is a note rather than something to
// discard, and anything carrying no text at all is dropped here so the count cannot promise cards
// that vanish on import.
function nbCards(list) {
  return (Array.isArray(list) ? list : [])
    .map((c) => {
      if (typeof c === "string") return { note: c };
      if (!c || typeof c !== "object") return null;
      // ⚠️ The writer does not always use the same key. One measured run put the card text under
      // `note`, another under `suggestion`; ingestCards reads `question` or `note`, so a card
      // written the other way was counted on import and then silently dropped. Normalise instead
      // of discarding — the text is the card.
      if (c.question === undefined && c.note === undefined && typeof c.suggestion === "string") {
        const { suggestion, ...rest } = c;
        return { ...rest, note: suggestion };
      }
      return c;
    })
    .filter((c) => c && cardText(c));
}

// One candidate, parsed strictly and then leniently: {raw, recovered, err}, raw null when neither
// yields an object. A candidate always starts at `{`, so a strict parse is an object or a throw.
function nbParseCandidate(body) {
  try {
    return { raw: JSON.parse(body), recovered: false, err: "" };
  } catch (e) {
    const err = String((e && e.message) || e);
    let raw = null;
    try { raw = nbRestoreFences(bestEffortParse(nbHideFences(nbTrimBrokenEscape(body)))); } catch (e2) { raw = null; }
    return { raw: raw && typeof raw === "object" ? raw : null, recovered: true, err };
  }
}

/* Returns {ok, payload, report} or {ok:false, error}. `report` is what the reader is shown before
   the document is created: which sections arrived, which did not, and what could not be placed. */
/* idaf-notebooklm-run4.json: every section had no line break, only the two characters `\n`, and every
   markdown mark was escaped (`\#\#\#`, `| \--- |`). Imported as written that was 0 tables and 74 visible
   "\n". Judged on the sections together — a section of one line is normal, eight of them each
   carrying `\n` is not — so a paste with real line breaks keeps every escape its writer meant. */
function nbEscapedTwice(sections) {
  const texts = Object.values(sections).filter((v) => typeof v === "string");
  return texts.some((v) => v.includes("\\n")) && !texts.some((v) => v.includes("\n"));
}
// CommonMark: a backslash before ASCII punctuation is an escape. Undone once, with `\n` a line break.
function nbUnescapeMd(s) { return s.replace(/\\n/g, "\n").replace(/\\([!-\/:-@[-`{-~])/g, "$1"); }

function parseNotebookJson(text) {
  const cands = nbCandidates(text);
  if (!cands.length) return { ok: false, error: "No JSON object found in that paste. Expected one object with: " + NB_SCHEMA + "." };
  // The first candidate that yields a proposal wins. Anything else that parses is remembered only
  // so the message can say what was wrong with the paste rather than with a command sample in it.
  let raw = null, recovered = false, body = "", closed = true, firstErr = "", bestErr = "", sawObject = false;
  for (const cand of cands) {
    const got = nbParseCandidate(cand.text);
    if (!firstErr) firstErr = got.err;
    // The candidate that mentions `sections` is the one the reader meant. Its parse error is the
    // one worth quoting; a placeholder in the intro prose has its own, unrelated, error.
    if (!bestErr && got.err && cand.text.includes('"sections"')) bestErr = got.err;
    if (!got.raw) continue;
    sawObject = true;
    const sec = got.raw.sections;
    if (sec && typeof sec === "object" && !Array.isArray(sec)) {
      raw = got.raw; recovered = got.recovered; body = cand.text; closed = cand.closed; break;
    }
  }
  const truncated = recovered && !closed;
  if (!raw) {
    // A fragment that parsed is not the proposal parsing. If the candidate that names `sections`
    // failed, say why it failed rather than reporting the fragment's shape as the whole story.
    if (bestErr) return { ok: false, error: "That paste is not valid JSON — " + bestErr };
    if (!sawObject && firstErr) return { ok: false, error: "That paste is not valid JSON — " + firstErr };
    return { ok: false, error: "That JSON has no `sections` object. Expected one object with: " + NB_SCHEMA + "." };
  }
  const secIn = raw.sections;
  // A paste escaped once too often arrives with `\n` for every line break and a backslash before
  // every markdown mark; read as written it imports as one paragraph per section with no table.
  const escapedTwice = nbEscapedTwice(secIn);
  // ⛔ Only a string is prose. Coercing an object or an array with String() shipped "[object Object]"
  // or "a,b" into the proposal as if it were the author's text, and counted the section as arrived.
  const str = (v) => (typeof v === "string" ? (escapedTwice ? nbUnescapeMd(v) : v) : "");
  // Not text, and carrying something — an empty array or object loses nothing by being skipped, and
  // naming it would be noise. Both real runs emit `movedOut: []` when there is nothing to move out.
  const carriesText = (v) => typeof v !== "string" && nbNonEmpty(v);
  const badShape = Object.keys(secIn).filter((k) => carriesText(secIn[k]));
  // The two drawers get the same treatment as a section: named when they are not text, rather than
  // silently arriving empty. An array of moved-out items is a natural shape for a writer to emit.
  for (const k of ["movedOut", "notUsed"]) if (carriesText(raw[k])) badShape.push(k);

  // `addedOrCalculated` carries the figures the writer added or worked out, each with its basis.
  // Grouped by section so each section's body is rendered and marked in the same pass.
  const added = Array.isArray(raw.addedOrCalculated) ? raw.addedOrCalculated : [];
  const wantBySection = {};
  const strayMarks = [];    // a mark naming no figure at all — there is nothing to look for
  const noSectionMarks = []; // a mark naming a figure but no section — a key problem, not a missing figure
  for (const a of added) {
    const key = String((a && a.section) || "");
    const item = String((a && a.item) || "").trim();
    if (!item) { strayMarks.push(a); continue; }
    if (!key) { noSectionMarks.push(a); continue; }
    (wantBySection[key] = wantBySection[key] || []).push({ item, basis: String((a && a.basis) || "").trim(), src: a });
  }

  const htmlSections = {}, mdSections = {}, arrived = [], missing = [];
  const mermaidBlocks = []; // every ```mermaid block, for nbCheckMermaid at submit
  let marksPlaced = 0;
  const marksUnplaced = strayMarks.slice();
  const unplacedBySection = []; // [section key, mark] — the notes judgeMarks can still place on a block
  for (const sec of SECTIONS) {
    const mdText = str(secIn[sec.key]).trim();
    if (!mdText) {
      // A section already named in badShape arrived — in a shape this cannot read. Calling it
      // missing as well had one toast say it was both absent and present-but-skipped.
      if (badShape.indexOf(sec.key) === -1) missing.push(sec.title);
      for (const w of wantBySection[sec.key] || []) marksUnplaced.push(w.src);
      continue;
    }
    const r = nbRenderSection(mdText, wantBySection[sec.key] || []);
    htmlSections[sec.key] = r.html;
    mdSections[sec.key] = mdText;
    for (const m of r.mermaid) mermaidBlocks.push({ section: sec.key, ...m });
    marksPlaced += r.placed;
    for (const u of r.unplaced) { marksUnplaced.push(u); unplacedBySection.push([sec.key, u]); }
    arrived.push(sec.key);
  }
  // A mark naming a section that is not one of the eight never reaches the loop above. Reported as
  // a wrong key, not as a figure missing from the text — the figure may well be there.
  const marksBadSection = noSectionMarks.length ? ["(none named)"] : [];
  for (const key of Object.keys(wantBySection)) {
    if (!SECTIONS.some((sec) => sec.key === key) && marksBadSection.indexOf(key) === -1) marksBadSection.push(key);
  }
  if (!arrived.length) return { ok: false, error: "None of the eight sections carried any text." };

  // A section is called partial only when the paste actually ended INSIDE its string value, which
  // nbCutSectionKey determines from the text. Recovery on its own says nothing about where the cut
  // fell: `sections` comes first in the schema, so a truncated paste usually stops in a later key,
  // and a paste recovered for some other reason (an unescaped newline, a missing closing brace) is
  // not cut mid-sentence at all.
  const partial = [];
  if (recovered) {
    const cut = nbCutSectionKey(body);
    if (cut && arrived.includes(cut)) partial.push((SECTIONS.find((sec) => sec.key === cut) || {}).title || cut);
  }

  const vizIn = nbViz(raw.viz);
  const figuresIn = Array.isArray(raw.sourceFigures) ? raw.sourceFigures : [];
  const figureRows = nbSourceRows(figuresIn);
  const sourceHtml = nbSourceHtml(figureRows);
  // ⛔ Rupees only. numberCheck compares money; the percentages extractNums also returns never
  // reach its `fabricated` set, so a source of nothing but percentages is no source for this check.
  const srcReadable = extractNums(sourceHtml).rupees.size > 0;
  const summariesIn = (raw.sectionSummaries && typeof raw.sectionSummaries === "object") ? raw.sectionSummaries : {};
  const summaries = {};
  for (const sec of SECTIONS) { const v = str(summariesIn[sec.key]).trim(); if (v) summaries[sec.key] = v; }

  const payload = {
    // The organisation and project, as NotebookLM names them — used when the Title field is left blank.
    title: str(raw.title).trim().slice(0, 120),
    sections: htmlSections,
    sectionSummaries: summaries,
    viz: vizIn.viz,
    cards: nbCards(raw.stillNeeded),
    sourceHtml,
    // AUDIT: #12, same rule the mini followed. With no source figures there is nothing to validate
    // against, and numberCheck("") reports every figure in the proposal as invented. Say the check
    // is unavailable rather than running one that is pure noise. Judged on the rendered source, not
    // the entry count: entries of the wrong shape leave it empty just as surely as no entries.
    // ⛔ Judged on what extractNums can actually READ, not on how many rows rendered. numberCheck
    // compares the proposal's figures against this source through extractNums; a source it cannot
    // parse yields srcCount 0, and every amount in the eight sections is then listed as fabricated.
    numberCheckUnavailable: !srcReadable,
    // The reason the check cannot run, recorded with it (docState.numberCheck.reason) rather than the mini's.
    // ⛔ The paste cannot carry the source's links — sourceHtml here is a rendered list of figures
    // and nothing else. Left alone, numberCheck's link half reports every citation URL in the
    // proposal as an unknown link added, in the danger style, permanently. There is no comparison
    // to make, so the link line is withheld rather than answered wrongly.
    linkCheckUnavailable: true,
    // Three different states, and they are not interchangeable: nothing was sent, something was
    // sent in a shape this does not read, or figures rendered but extractNums cannot parse them.
    numberCheckReason: srcReadable ? ""
      : (!figuresIn.length ? "no source figures arrived with the paste"
         : !figureRows.length ? "the source figures are not written as `figure` and `context`"
                              : "the source figures are not in a form the check can read"),
    // The trimmed view (renderTrimmed, removed in Stage 8) was written for the mini's HTML: it sanitized, and
    // counted `<li` to say how many items moved out. The writer sends markdown, which collapsed into one run of text and always
    // read "1 item". Render it the same way a section is rendered.
    trimmed: str(raw.movedOut).trim() ? nbMd.render(str(raw.movedOut)).trim() : "",
    unused: str(raw.notUsed),
    model: "NotebookLM",
    // importFormat is the mini's fund/1|2|3 degrade tier. There is no tier here, so leave it unset
    // rather than inventing a value the degrade note would then try to explain.
    format: null,
  };
  return {
    ok: true,
    payload,
    // The figures with no currency sign, each with the line it sits in — what judgeFigures asks about.
    figureQuestions: nbFigureQuestions(mdSections, figureRows),
    mermaidBlocks,
    // The notes no figure match placed, with their section's passages — what judgeMarks asks about —
    // and what re-rendering a section needs once they are placed.
    markQuestions: unplacedBySection.map(([key, w]) => ({ section: key, item: w.item, basis: w.basis, mark: w, passages: nbPassages(mdSections[key]) })),
    rerender: { md: mdSections, want: wantBySection },
    report: { recovered, truncated, arrived: arrived.length, missing, partial, badShape,
              vizDropped: vizIn.dropped, vizOrphans: vizIn.orphanKeys, marksBadSection,
              figures: figureRows.length,
              marksPlaced, marksUnplaced, cards: payload.cards.length },
  };
}
// Code points, not UTF-16 units: a surrogate pair (an emoji) is one character. Counted without
// spreading the string into an array — the paste can be 2,000,000 characters and this runs per keystroke.
function cpLen(s) { s = String(s || ""); return s.length - (s.match(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g) || []).length; }
function npPlural(n) { return n === 1 ? "character" : "characters"; } // "1 more character", not "1 more characters"
// The last paste parsed and its result. The helper line parses on every settled keystroke and
// Import parses the same text a moment later; at NP_MAX that is 70-120 ms of work done twice.
let npLast = { text: null, result: null };
function npParse(text) {
  if (npLast.text !== text) npLast = { text, result: parseNotebookJson(text) };
  return npLast.result;
}
// Reads the paste as it is typed. A character count means little for JSON, so this reports the
// shape instead: how many of the eight sections are present, and what is wrong if it will not parse.
function npHelperFor(n) {
  if (n === 0) return { text: "Paste the JSON from NotebookLM.", cls: "" };
  if (n > NP_MAX) { const d = n - NP_MAX; return { text: `Too long by about ${d} ${npPlural(d)}.`, cls: "np-danger" }; }
  if (n < NP_MIN) return { text: "That is too short to be the JSON object.", cls: "np-danger" };
  const r = npParse($("npBody").value);
  if (!r.ok) return { text: r.error, cls: "np-danger" };
  return { text: npReportText(r.report), cls: "np-ok" };
}
// One line stating what the paste carries, so nothing missing is discovered later in the export.
function npReportText(rep) {
  const bits = [`${rep.arrived} of ${SECTIONS.length} sections`];
  if (rep.figures) bits.push(`${rep.figures} source ${rep.figures === 1 ? "figure" : "figures"}`);
  if (rep.cards) bits.push(`${rep.cards} ${rep.cards === 1 ? "card" : "cards"}`);
  if (rep.marksPlaced) bits.push(`${rep.marksPlaced} marked ${rep.marksPlaced === 1 ? "figure" : "figures"}`);
  let text = bits.join(", ") + ".";
  if (rep.missing.length) text += ` Missing: ${rep.missing.join(", ")}.`;
  if (rep.badShape.length) text += ` Not text, so skipped: ${rep.badShape.join(", ")}.`;
  if (rep.vizDropped) text += ` ${rep.vizDropped} diagram${rep.vizDropped === 1 ? "" : "s"} arrived with no data and ${rep.vizDropped === 1 ? "was" : "were"} left out.`;
  if (rep.vizRejected) text += ` ${rep.vizRejected} diagram${rep.vizRejected === 1 ? "" : "s"} could not be read and ${rep.vizRejected === 1 ? "was" : "were"} left out.`;
  if (rep.vizOrphans.length) text += ` Diagram${rep.vizOrphans.length === 1 ? "" : "s"} keyed ${rep.vizOrphans.join(", ")} match no section and will sit at the end.`;
  if (rep.marksUnplaced.length) text += ` ${rep.marksUnplaced.length} marked ${rep.marksUnplaced.length === 1 ? "figure" : "figures"} could not be located in the text.`;
  if (rep.marksBadSection.length) text += ` Marked figures name no section of the proposal: ${rep.marksBadSection.join(", ")}.`;
  // `recovered` only means strict JSON.parse refused it. A paste that never closed its object was
  // cut short — usually AFTER the sections, in the summaries, diagrams or figures, which is a real
  // loss even though every section arrived whole. One that closed was merely malformed.
  if (rep.recovered) {
    text += rep.truncated
      ? " The paste was cut short and was read as far as it goes — anything after the point of the cut is missing."
      : " The JSON was malformed and was read as far as it could be.";
  }
  if (rep.partial.length) text += ` ${rep.partial.join(", ")} stops mid-sentence.`;
  return text;
}
let npCountTimer = null;
// Called from the input event. The direct npUpdateCount stays for open and submit, where the
// answer is needed immediately and there is no typing to outrun.
function npUpdateCountSoon() {
  clearTimeout(npCountTimer);
  npCountTimer = setTimeout(npUpdateCount, 200);
}
function npUpdateCount() {
  clearTimeout(npCountTimer);
  hideNpErr(); // clears a stale validation/network error as the text changes underneath it
  const n = cpLen($("npBody").value.trim());
  const { text, cls } = npHelperFor(n);
  $("npCount").textContent = text;
  $("npCount").className = "ty-label-01 np-count" + (cls ? " " + cls : "");
  return n;
}
function showNpErr(msg) { const el = $("npErr"); el.title = msg; el.open = true; } // Carbon default is open=true — held closed until this fires
function hideNpErr() { $("npErr").open = false; }
function openNewProp(prefill) {
  hideNpErr();
  $("npTitle").value = (prefill && prefill.title) || "";
  $("npBody").value = (prefill && prefill.body) || "";
  npUpdateCount();
  const btn = $("npSubmit"); btn.disabled = false; btn.textContent = "Import";
  $("newProp").open = true; // autofocus on open (spec §2): #npBody carries data-modal-primary-focus (app.html) — Carbon's own dialog focus logic targets it directly on every open, deterministically (no JS timing race, which lost on reopen to Carbon's own fallback-to-primary-button focus)
}
function closeNewProp() { $("newProp").open = false; } // Cancel/ESC/backdrop all land here — the NEXT open always starts from `prefill` (default empty), so nothing is kept
/* ---- Jev (TypeSafe) — judgements for the import, through the /_judge route ----
   Caddy adds the API key on the way out (Caddyfile, /_judge), so the key here is a placeholder that
   never leaves the page as anything real. */
let jevClient = null;
function jev() {
  return jevClient || (jevClient = new TypeSafeClient({
    apiKey: "added-by-the-judge-route", baseURL: "/_judge", dangerouslyAllowBrowser: true,
    // maxRetryAfterMs: a Retry-After past 0.5 s falls back to the SDK's own backoff (0.5 s at most for one
    // retry), so a 429 cannot push Jev's second attempt past judgeMarks's 20 s (see CLEF_WITHIN_MS).
    timeout: 8000, retry: { maxRetries: 1, maxRetryAfterMs: 500 }, logLevel: "off",
  }));
}
/* ---- One judgement, Clef first (his rulings, 2026-10-14) ----
   The System One request goes to Cloudflare's Clef on /_clef/1 (Folktaler's account), /_clef/2
   (aikyamhq), /_clef/3 (T4GLabs), then Jev; the first full answer wins. Caddy adds each account's key
   and fixes the path to the one model. All three accounts are on the Workers Free plan: past a day's
   free neurons Cloudflare refuses the call (429, code 3036), it never bills, and the next route answers.
   Clef wraps its answer under `result`; what is inside is Jev's shape, so a caller reads `answers`
   either way. If the caller's own signal ends, Jev is not asked and the judgement fails, so the caller
   leaves it unjudged.
   ⛔ Every caller passes a judgeBatch(): judge() refuses to run without one, so no call site can skip
   Clef by forgetting it.
   ⛔ The Clef routes need a signed-in user: Caddy asks Supabase for the session the request carries
   before it adds a Cloudflare key. With no session (or none within the Clef window), or once Supabase
   refuses the session on any route, Jev answers for the rest of the batch.
   ⛔ One Clef window per batch, CLEF_WITHIN_MS from judgeBatch(), covering the session lookup and every
   route. Jev's worst case is then 8 s, a retry delay capped at 0.5 s, and 8 s again: 16.5 s, leaving
   0.5 s of judgeMarks's 20 s. Measured 2026-10-14: 156 run-4 passages at once straight to Cloudflare,
   every answer inside 2.01 s (median 1.15 s); 150 session checks at once through Caddy to Supabase,
   inside 0.68 s cold and 0.23 s warm (forged sessions, so without Supabase's user lookup; from Node
   straight to Supabase, without Caddy's pooled connections, the same burst took 1.68 s).
   ⛔ A route is put aside for the rest of the batch only when it cannot be reached or Cloudflare refuses
   it (401, 403, 429); one odd answer, a 5xx or the window closing does not. ⚠️ In an import's first
   burst every passage is already on its way before any refusal arrives; the memory helps the passages
   that come after, and a spent allowance (3036) is remembered by the page until 00:00 UTC. */
const CLEF_ROUTES = ["/_clef/1", "/_clef/2", "/_clef/3"];
const CLEF_WITHIN_MS = 3000;
const CLEF_REFUSED = [401, 403, 429];
const clefSpentUntil = {}; // route → the time its day's free neurons come back (00:00 UTC)
/* What one batch of judgements shares: the session token the Clef routes check, the end of the Clef
   window, and the routes that refused, which are not asked again in this batch. The session lookup is
   bounded by the window: supabase-js can stall refreshing an expired token. */
async function judgeBatch() {
  const clefUntil = Date.now() + CLEF_WITHIN_MS;
  let timer;
  const s = await Promise.race([
    getSession().catch(() => null),
    new Promise((resolve) => { timer = setTimeout(() => resolve(null), CLEF_WITHIN_MS); }),
  ]);
  clearTimeout(timer);
  return { token: s ? s.access_token : null, clefUntil, down: new Set() };
}
async function judge(request, { signal, batch } = {}) {
  if (!batch) throw new TypeError("judge() needs a judgeBatch()");
  const asked = Object.keys(request.questions); // a request with no questions is the caller's error, thrown here
  for (const route of CLEF_ROUTES) {
    if (signal && signal.aborted) throw signal.reason;
    const left = batch.clefUntil - Date.now();
    if (!batch.token || batch.down.has(route) || (clefSpentUntil[route] || 0) > Date.now() || left <= 0) continue;
    // The window's end and the caller's signal, joined by hand (AbortSignal.any is too new: Safari 17.4).
    const stop = new AbortController();
    const timer = setTimeout(() => stop.abort(), left);
    const onCallerStop = () => stop.abort();
    if (signal) signal.addEventListener("abort", onCallerStop, { once: true });
    let res = null, body = null;
    try {
      res = await fetch(route, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: "Bearer " + batch.token },
        body: JSON.stringify({ model: "clef", ...request }), signal: stop.signal,
      });
      body = await res.json().catch(() => null);
    } catch {
      if (!stop.signal.aborted) batch.down.add(route); // could not be reached; the window or the caller ending is not the route's fault
    } finally {
      clearTimeout(timer);
      if (signal) signal.removeEventListener("abort", onCallerStop);
    }
    if (signal && signal.aborted) throw signal.reason;
    if (!res) continue;
    if (CLEF_REFUSED.includes(res.status)) {
      // Supabase's refusals carry `error_code` (no_authorization, bad_jwt, over_request_rate_limit…): the
      // session, not the route, so no Clef route can be asked with it. Cloudflare's carry `errors`.
      if (body && typeof body.error_code === "string") batch.token = null;
      else batch.down.add(route);
      const errors = body && Array.isArray(body.errors) ? body.errors : [];
      if (res.status === 429 && errors.some((e) => e && e.code === 3036)) {
        const d = new Date(Date.now());
        clefSpentUntil[route] = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1);
      }
      continue;
    }
    const answers = body && body.success && body.result && body.result.answers;
    if (res.ok && answers && asked.every((k) => answers[k])) return body.result;
  }
  return jev().systemOne(request, { signal });
}
const FIGURE_KIND = choice("In `text`, what does the figure `figure` measure? For a table row, `header` names the columns.", {
  money: "An amount of money: a cost, price, grant, budget line, contribution or total in a currency",
  count: "A number of things: pages, people, books, centres, jobs, items",
  other: "Anything else: a percentage, a year, a duration, an identifier, a rate that is not money",
});
// ⚠️ Set from one proposal (IDAF run 4): its 7 page counts scored P(money) 0.00–0.19, and every ₹
// amount 0.96 or more. A figure leaves the money check only when EVERY place it appears scores under this.
const NOT_MONEY_BELOW = 0.25;
const FIGURE_QUESTIONS_MAX = 40; // with MARK_PASSAGES_MAX, under the route's 200 a minute per address; past this, the rest count as money
/* The figures with no currency sign that are not money, as figureKey strings, for numberCheck's
   `notMoney`. ⛔ Anything unjudged counts as money — a failed request, a timeout, the cap — so a
   judgement that did not happen leaves the check exactly as it was before Jev. */
async function judgeFigures(questions) {
  const asked = questions.slice(0, FIGURE_QUESTIONS_MAX);
  if (!asked.length) return [];
  const stop = new AbortController();
  const timer = setTimeout(() => stop.abort(), 20000);
  try {
    const pMoney = await Promise.all(asked.map((q) =>
      jev().systemOne({ state: q, questions: { kind: FIGURE_KIND } }, { signal: stop.signal })
        .then((r) => r.answers.kind.probabilities.money, () => null)));
    const money = new Set(questions.slice(FIGURE_QUESTIONS_MAX).map((q) => figureKey(q.figure)));
    asked.forEach((q, i) => { if (typeof pMoney[i] !== "number" || pMoney[i] >= NOT_MONEY_BELOW) money.add(figureKey(q.figure)); });
    return [...new Set(asked.map((q) => figureKey(q.figure)))].filter((k) => !money.has(k));
  } finally {
    clearTimeout(timer);
  }
}
// ⚠️ Set from one proposal (IDAF run 2, probe 3): at 0.90 no passage was marked wrongly and 17 rows
// were placed across 10 of 11 notes; at 0.85 one wrong row came in. A wrong mark is worse than none.
// Kept for Clef (2026-10-14): on probe 3's recorded passages it marked what Jev marked, nothing wrong.
const MARK_AT = 0.90;
// One request per passage; with FIGURE_QUESTIONS_MAX this stays under the route's 200 a minute per
// address. Past it, the remaining passages are not asked and cannot be marked.
const MARK_PASSAGES_MAX = 150;
const MARK_ABOUT = {
  about: {
    type: "noul",
    instructions: "The writer attached the note `note.item` (reason: `note.basis`) to part of a proposal. Is `passage` part of what the note is about?",
    criteria: {
      true: "The note names this passage, or a set of rows or a table that this passage is one of",
      false: "The note is about something else in the proposal, not this passage",
    },
  },
};
/* For each note the figure match could not place, the passages judge() puts it on: [{q, lines}].
   ⛔ One request per passage, and the state is the note and that one passage — plus its table's
   header row — and nothing else. Measured on IDAF run 2: every passage of a note in one request
   (the passages in the state, or each inside its own question) marked 12 rows across 8 notes, one
   request each marked 17 across 10, none wrong either way. The rows lost were whole columns marked
   on one row of four. ⛔ When the note says [X] and some passages carry [X], only those are
   candidates: the writer's [X] is the gap it is pointing at. A passage whose request fails is not
   marked; a note nothing reaches MARK_AT on stays unplaced and is reported, as before. */
async function judgeMarks(questions) {
  let budget = MARK_PASSAGES_MAX;
  const asked = questions.map((q) => {
    const gaps = q.passages.filter((p) => p.passage.includes("[X]"));
    const pool = (q.item.includes("[X]") && gaps.length ? gaps : q.passages).slice(0, Math.max(0, budget));
    budget -= pool.length;
    return { q, pool };
  }).filter((a) => a.pool.length);
  if (!asked.length) return [];
  const stop = new AbortController();
  const timer = setTimeout(() => stop.abort(), 20000);
  try {
    const batch = await judgeBatch();
    return await Promise.all(asked.map(async ({ q, pool }) => {
      const yes = await Promise.all(pool.map((p) => {
        const state = { note: { item: q.item, basis: q.basis }, passage: p.passage, ...(p.table_header ? { table_header: p.table_header } : {}) };
        return judge({ state, questions: MARK_ABOUT }, { signal: stop.signal, batch }).then((r) => r.answers.about.noul, () => 0);
      }));
      return { q, lines: pool.filter((p, i) => yes[i] >= MARK_AT).map((p) => p.line) };
    }));
  } finally {
    clearTimeout(timer);
  }
}
/* The sections re-rendered with each placed note marking its passages, and the import report with
   those notes moved from unplaced to placed. The parse itself is cached (npParse) and left as it was. */
function nbPlaceBlockMarks(parsed, placements) {
  const blocksBySection = {}, placedNotes = new Set();
  for (const { q, lines } of placements) {
    for (const line of lines) (blocksBySection[q.section] = blocksBySection[q.section] || []).push({ line, basis: q.basis, mark: q.mark });
  }
  const sections = { ...parsed.payload.sections };
  for (const [key, blocks] of Object.entries(blocksBySection)) {
    const r = nbRenderSection(parsed.rerender.md[key], parsed.rerender.want[key] || [], blocks);
    sections[key] = r.html;
    // ⛔ Placed means DRAWN: a chosen line that falls in no paragraph or row (a code block, a rule)
    // marks nothing, and its note stays in the report as unplaced.
    for (const b of r.blocksPlaced) placedNotes.add(b.mark);
  }
  const report = { ...parsed.report,
    marksPlaced: parsed.report.marksPlaced + placedNotes.size,
    marksUnplaced: parsed.report.marksUnplaced.filter((m) => !placedNotes.has(m)) };
  return { sections, report };
}
/* The ```mermaid blocks Mermaid's own parser refuses: a Set of their exact HTML, to be taken out of
   the sections before the proposal is saved — a diagram that cannot be drawn is left out and said so,
   never planted to fail later. If Mermaid cannot be loaded at all, nothing is judged and every block
   is kept: it then shows 'viz failed to render' in the editor, and Typst's own placeholder in the PDF. */
async function nbCheckMermaid(blocks) {
  const bad = new Set();
  if (!blocks || !blocks.length) return bad;
  let mm;
  try { mm = await loadMermaid(); } catch { return bad; }
  for (const b of blocks) { try { await mm.parse(b.code); } catch { bad.add(b.html); } }
  return bad;
}
// A card's public page, built from the slug so a link in the panel or the PDF can only ever point at
// aikyam.school.
const CARD_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const cardUrl = (slug) => `https://aikyam.school/cards/${slug}/`;

/* ---- Evidence cards: Fundable picks research findings for each section --------------------------
 * His design, 2026-09-27, after NotebookLM picked stretched cards on three runs. For each section:
 *   1. search — kb-retrieval (cards.folktaler.com, the Weaviate index of every aikyam.school card),
 *      the whole section as the query, 15 candidates. Through /_evidence/search, which adds the token.
 *   2. rerank — Voyage rerank-2.5 through /_evidence/rerank; a card stays at 0.45 or more, at most 5.
 *      Measured 2026-09-27: IDAF (a heritage archive the library has nothing on) peaked at 0.30 in
 *      every section; a job-training control proposal scored 0.46–0.55 on the cards that fit.
 *   3. relation — Jev (/_judge) picks how the finding bears on the section, or says it does not.
 *   4. the proposal's sentence — the section's own sentence the reranker ranks closest to the card,
 *      word for word. Nothing here is generated text.
 * The card's text is what the index holds — the same fields aikyam.school prints.
 */
const EVIDENCE_FLOOR = 0.45;
const EVIDENCE_PER_SECTION = 5;
// One question per card, all of a section's cards in one request: the /_judge route allows 200 a
// minute per address, and an import already spends up to 190 on figures and marks.
const EVIDENCE_ASK = "A search found this research finding for one section of a grant proposal (`section`, text in `text`). How does the finding bear on that section?";
const EVIDENCE_CRITERIA = {
  supports: "The study is about the same kind of work, people or outcome, and its finding backs what the section says or plans",
  challenges: "The study is about the same kind of work, people or outcome, and its finding cuts against what the section says or plans",
  design: "The finding shows how to design or deliver the kind of work this section describes",
  method: "The finding shows a proven way to do what this section is for: setting goals, measuring impact, costing, reaching people",
  none: "It fits only by analogy: a different field or outcome that shares words or a general idea with the section",
};
// What "Why this card" says for each relation. His wording to decide.
const EVIDENCE_WHY = { supports: "Supports this", challenges: "Challenges this", design: "Shapes the design", method: "A proven method for this section" };

// Join cancellation using the existing judge() pattern, including older Safari support.
function evidenceWindow(parent, milliseconds) {
  const stop = new AbortController();
  const onParent = () => stop.abort(parent.reason);
  if (parent?.aborted) onParent();
  else parent?.addEventListener("abort", onParent, { once: true });
  const timer = setTimeout(() => stop.abort(new Error("evidence request timed out")), milliseconds);
  return { signal: stop.signal, close: () => {
    clearTimeout(timer); parent?.removeEventListener("abort", onParent);
  } };
}
async function evidencePost(path, body, signal) {
  if (signal?.aborted) throw signal.reason;
  const headers = { "Content-Type": "application/json" };
  if (path === "/_evidence/search") headers["X-School-Evidence-Capability"] = "preserve-long-sections-v1";
  const window = evidenceWindow(signal, 20000);
  try {
    const res = await fetch(path, { method: "POST", headers, body: JSON.stringify(body), signal: window.signal });
    if (!res.ok) {
      const detail = await res.json().catch(() => null);
      const error = new Error(`${path} ${res.status}`);
      if (res.status === 422 && detail?.code === "query_too_long") error.code = "query_too_long";
      throw error;
    }
    return await res.json();
  } finally { window.close(); }
}
// What the reranker and Jev read of a card: what it is about and what it found.
// ⚠️ The same shape the 0.45 floor was measured on (2026-09-27) — change it and re-measure the floor.
const evidenceDoc = (h) => `${h.title}. Problem: ${h.problemContext || ""} Finding: ${h.exactFindings || ""} Studied: ${h.sampleSize || ""}`;

const SENTENCES = new Intl.Segmenter("en", { granularity: "sentence" }); // e.g. "Rs. 20 per page" stays one sentence
async function pickEvidence(key, query, sentences, signal) {
  if (signal?.aborted) throw signal.reason;
  if (query.length < 40) return { cards: [], unlabelled: false };
  const hits = ((await evidencePost("/_evidence/search", { query, limit: 15 }, signal)).hits || [])
    .filter((h) => h && CARD_SLUG.test(String(h.slug || "")) && typeof h.title === "string");
  if (!hits.length) return { cards: [], unlabelled: false };
  const docs = hits.map(evidenceDoc);
  const ranked = ((await evidencePost("/_evidence/rerank", { query, documents: docs, model: "rerank-2.5" }, signal)).data || [])
    .filter((d) => d.relevance_score >= EVIDENCE_FLOOR)
    .sort((a, b) => b.relevance_score - a.relevance_score).slice(0, EVIDENCE_PER_SECTION);
  if (!ranked.length) return { cards: [], unlabelled: false };
  const questions = Object.fromEntries(ranked.map((d, i) => [`c${i}`, choice({ question: EVIDENCE_ASK, finding: docs[d.index] }, EVIDENCE_CRITERIA)]));
  const judging = evidenceWindow(signal, 20000);
  const [relations, saids] = await Promise.all([
    // Bounded like judgeFigures: the SDK would otherwise wait out a Retry-After of up to 60 s.
    jev().systemOne({ state: { section: secTitle(key), text: query }, questions }, { signal: judging.signal })
      .then((r) => ranked.map((_, i) => (r.answers[`c${i}`] || {}).choice || null), () => ranked.map(() => null)).finally(() => judging.close()),
    Promise.all(ranked.map((d) => (sentences.length
      ? evidencePost("/_evidence/rerank", { query: docs[d.index], documents: sentences, model: "rerank-2.5", top_k: 1 }, signal)
        .then((r) => sentences[((r.data || [])[0] || {}).index] || "", () => "")
      : ""))),
  ]);
  const kept = ranked.map((d, i) => {
    const h = hits[d.index], relation = relations[i], said = saids[i];
    // Measured 2026-09-27 with this question: Jev kept all 8 control cards over the floor ("none" at most
    // 0.19) and called every IDAF candidate "none" (0.87–1.00). A Jev failure leaves the card in with no
    // label — the reranker already passed it — and the import report says so.
    if (relation === "none") return null;
    const text = (v) => (typeof v === "string" ? v.trim() : "");
    return { slug: h.slug, section: key, proposalSays: said, why: EVIDENCE_WHY[relation] || "", relation, score: d.relevance_score,
      card: { title: (typeof h.displayTitle === "string" && h.displayTitle.trim() ? h.displayTitle : h.title).trim(), exactFindings: text(h.exactFindings), sampleSize: text(h.sampleSize), studyDesign: text(h.studyDesign),
        evidenceLevel: text(h.evidenceLevel), source: text(h.source), verbatimProof: text(h.verbatimProof) } };
  });
  return { cards: kept.filter(Boolean), unlabelled: relations.some((r) => r === null) };
}
// Two sections at a time. Each active section has 45 seconds; queued waiting uses none of that budget.
// inputs: { <section key>: { query, sentences } }, from sectionInputs.
async function findEvidence(inputs) {
  const keys = SECTIONS.map((s) => s.key).filter((k) => inputs[k]);
  const got = new Array(keys.length);
  let next = 0;
  const work = async (key) => {
    const { query, sentences } = inputs[key];
    if (query.length > 6000) return { failed: true, tooLong: true };
    const controller = new AbortController();
    let timer;
    const deadline = new Promise((_, reject) => {
      timer = setTimeout(() => {
        const error = new Error("evidence timed out");
        controller.abort(error); reject(error);
      }, 45000);
    });
    try { return await Promise.race([pickEvidence(key, query, sentences, controller.signal), deadline]); }
    catch (error) { return { failed: true, ...(error?.code === "query_too_long" ? { tooLong: true } : {}) }; }
    finally { clearTimeout(timer); }
  };
  const worker = async () => {
    while (next < keys.length) { const index = next++; got[index] = await work(keys[index]); }
  };
  await Promise.all(Array.from({ length: Math.min(2, keys.length) }, worker));
  // A card that fits two fresh sections prints once, in the section it fits best.
  const best = new Map();
  for (const e of got.flatMap((g) => g.cards || [])) if (!best.has(e.slug) || best.get(e.slug).score < e.score) best.set(e.slug, e);
  const evidence = got.flatMap((g) => g.cards || []).filter((e) => best.get(e.slug) === e);
  return { evidence, searched: keys.length, failed: got.filter((g) => g.failed).length,
    unlabelled: got.filter((g) => g.unlabelled).length, tooLongSections: keys.filter((_, i) => got[i].tooLong) };
}
// Only too-long sections are protected during a partial success. Ordinary mixed failures retain
// the existing replacement rule. Preserve original objects, including legacy duplicate copies.
function evidenceAfterSearch(result, previous = []) {
  const protectedSections = new Set(result.tooLongSections || []);
  const kept = (Array.isArray(previous) ? previous : []).filter(e => protectedSections.has(e.section));
  const keptSlugs = new Set(kept.map(e => e.slug));
  const fresh = result.evidence.filter(e => !keptSlugs.has(e.slug));
  const order = new Map(SECTIONS.map((s, i) => [s.key, i]));
  const evidence = [...kept, ...fresh].sort((a, b) => (order.get(a.section) ?? 99) - (order.get(b.section) ?? 99));
  return { evidence, found: fresh.length, kept: kept.length };
}
// The search's input for each section of a Plate value: the query is the section's text (diagrams add nothing),
// the candidate sentences come from its lead, paragraphs, list lines and quotes — never a table, heading or diagram.
function sectionInputs(value) {
  const prose = (e) => (isPrintedList(e) ? e.items.flatMap((it) => it.blocks.flatMap(prose))
    : e.type === "p" || e.type === "section_lead" ? [nodeSendText(e)] : e.type === "blockquote" ? printedBlocks(e.children).flatMap(prose) : []);
  const res = {};
  for (const { key, blocks } of sectionBlocks(value)) {
    const query = [res[key] && res[key].query, ...blocks.map(nodeSendText)].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
    if (!query) continue;
    const sentences = [...new Set([...(res[key] ? res[key].sentences : []), ...blocks.flatMap(prose)
      .flatMap((t) => [...SENTENCES.segment(t.replace(/\s+/g, " ").trim())].map((g) => g.segment.trim()))
      .filter((t) => t.length >= 20)])].slice(0, 60);
    res[key] = { query, sentences };
  }
  return res;
}
// File menu › Find Card: a fresh search on the proposal as it now reads. The cards found REPLACE the
// proposal's cards (his choice, 2026-10-11); when every section's search fails, the cards it had are kept.
let findingCards = false;
const FINDING_CARDS = "Finding evidence cards\u2026";
async function findCards() {
  const page = mountedPage();
  if (findingCards || !page || !docState) return;
  if (saveConflict) { toast("This proposal is locked by a conflicting edit — reload the page first"); return; }
  const inputs = sectionInputs(page.handle.getValue());
  // Nothing to search is not a search that found nothing: the cards the proposal has are kept.
  if (!Object.keys(inputs).length) { toast("No evidence cards matched"); return; }
  findingCards = true; toast(FINDING_CARDS, true);
  // Our own hold toast only — after the wait another screen may be showing its own message.
  const dropMine = () => { if ($("toast").textContent === FINDING_CARDS) hideToast(); };
  try {
    const r = await findEvidence(inputs);
    // After the wait: the same page still up for the same proposal, and no conflict since. Otherwise nothing is written.
    if (currentDocId !== page.docId || mountedPage() !== page || saveConflict) { dropMine(); return; }
    const tooLong = r.tooLongSections || [];
    const tooLongMessage = tooLong.length ? `Section too long: ${tooLong.map(secTitle).join(", ")}` : "";
    if (r.failed === r.searched) {
      dropMine(); toast(tooLongMessage ? `No cards changed · ${tooLongMessage}` : "Couldn't reach the card search — try again", 6000); return;
    }
    const next = evidenceAfterSearch(r, docState.evidence);
    docState.evidence = next.evidence;
    page.handle.setEvidence(next.evidence);
    dropMine();
    clearTimeout(saveTimer);
    if (!(await saveDoc())) return; // saveDoc shows its own conflict or retry message
    const n = next.found;
    let msg = n ? `${n} evidence ${n === 1 ? "card" : "cards"} found` : "No evidence cards matched";
    const failed = r.failed - tooLong.length;
    if (failed) msg += ` · ${failed} ${failed === 1 ? "section's" : "sections'"} search failed`;
    if (tooLongMessage) msg += ` · ${tooLongMessage}`;
    if (next.kept) msg += ` · ${next.kept} existing ${next.kept === 1 ? "card" : "cards"} kept`;
    toast(msg, 6000);
  } catch { dropMine(); toast("Couldn't reach the card search — try again"); }
  finally { findingCards = false; }
}
async function submitNewProp() {
  const title = $("npTitle").value.trim();
  const body = $("npBody").value;
  const n = cpLen(body);
  if (n < NP_MIN || n > NP_MAX) { npUpdateCount(); $("npBody").focus(); return; } // the helper line states it; don't insert what it just refused
  const parsed = npParse(body);
  if (!parsed.ok) { showNpErr(parsed.error); $("npBody").focus(); return; }
  hideNpErr();
  const btn = $("npSubmit"); btn.disabled = true; btn.textContent = "Importing\u2026"; // lock BEFORE the first await — two fast clicks must not create two proposals
  if (!(await canAuthor())) { btn.disabled = false; btn.textContent = "Import"; showNpErr("This account can't create proposals."); return; }
  const userId = await myUserId(); if (!userId) { closeNewProp(); showSignin(); return; } // an expired session mid-submit must not leave the modal overlaying the sign-in gate
  const content = freshContent();
  content.name = title || parsed.payload.title || "Untitled proposal";
  // Jev, both judgements at once: which figures with no currency sign are not money (read by
  // numberCheck at hydrate), and which passages the notes no figure match placed are about.
  // Evidence cards are not searched here: File menu › Find Card does it, on request (2026-10-11).
  const [notMoney, placements, badDiagrams] = await Promise.all([judgeFigures(parsed.figureQuestions || []),
    judgeMarks(parsed.markQuestions || []), nbCheckMermaid(parsed.mermaidBlocks)]);
  const { sections, report: placedReport } = nbPlaceBlockMarks(parsed, placements);
  if (badDiagrams.size) for (const k of Object.keys(sections)) for (const h of badDiagrams) sections[k] = sections[k].split(h).join("");
  // Counted per diagram: two identical refused blocks share one html string, and both are gone.
  const report = { ...placedReport, vizRejected: (parsed.mermaidBlocks || []).filter((b) => badDiagrams.has(b.html)).length };
  // The import report is kept with the proposal: the toast is gone in 12 seconds, and a question like
  // "were two diagrams refused, or never sent?" (27 Sep 2026) could not be answered after it.
  const fundPending = { ...parsed.payload, sections, notMoney, importReport: npReportText(report) };
  // The source was judged readable before this. If every amount it had was a figure now judged not
  // money, numberCheck would list each amount in the proposal as invented — say it cannot run instead.
  if (!fundPending.numberCheckUnavailable && extractNums(fundPending.sourceHtml, notMoney).rupees.size === 0) {
    fundPending.numberCheckUnavailable = true;
    fundPending.numberCheckReason = "none of the source figures is an amount of money";
  }
  content.fundPending = fundPending; // materialized by hydrateFund on first open, same as the mini's push
  const { data, error } = await supabase.from("documents")
    .insert({ user_id: userId, name: content.name, content }).select("id").single(); // .select() so success is honest — empty result = RLS refused
  btn.disabled = false; btn.textContent = "Import";
  if (error || !data) {
    const notPermitted = error && (error.code === "42501" || /row-level security/i.test(error.message || ""));
    showNpErr(notPermitted
      ? "This account can't create proposals yet."
      : "Couldn't save the proposal. Check the connection and try again — the text below is still here.");
    return; // modal stays open, text intact
  }
  closeNewProp();
  $("npTitle").value = ""; $("npBody").value = ""; npUpdateCount();
  npLast = { text: null, result: null }; // the paste is imported; don't hold up to 2 M characters for the page's life
  // hydrateFund runs inside openDoc: sections assembled, viz seeded, numberCheck re-run. On a false
  // return the proposal IS saved — say so, or the obvious next move is to paste again, which would
  // insert a second copy of the same proposal.
  if (!(await openDoc(data.id))) {
    // The row exists, so the list has to be re-read before the message below can claim it is there.
    const docs = await listDocs();
    // A first import is exactly the case that moves the home screen out of its empty state.
    if (docs) { homeDocs = docs; paintHome(); }
    toast("Imported, but it couldn't be opened just now — it is in the proposal list.");
    return;
  }
  const rep = report;
  // ⚠️ Held for 12 seconds, not the default 2.6. This is the only statement of what did not arrive
  // that survives the import — the helper line is gone once the modal closes — and a report naming
  // three missing sections runs to about 150 characters, which cannot be read in 2.6 seconds.
  const anything = rep.missing.length || rep.marksUnplaced.length || rep.recovered || rep.badShape.length
    || rep.vizDropped || rep.vizRejected || rep.vizOrphans.length || rep.marksBadSection.length;
  toast(anything ? npReportText(rep) : "Imported", anything ? 12000 : undefined);
}

// A new proposal arrives as a "pending" row (content.fundPending = {sections, sourceHtml, unused, model},
// doc=null) — written by submitNewProp from a pasted NotebookLM JSON, and before 2026-09-21 by the mini's
// push. The browser is the only place that can make the document, so we
// materialize it HERE on first open: assemble the 8 sections, re-run numberCheck (prod never trusts the
// upstream numbers), then save — after which it's an ordinary proposal.
// Stage 6: `plate` is the loaded bundle (openDoc loads it first). The document is its importToPlate of the assembled
// HTML and viz{} — the import's one parse; TipTap never sees it — saved as "plate" the way the long page's
// getValue() gives a value (toRecord: no comment marks, normalised), so the page opened on it writes nothing more.
// There never was a TipTap document, so there is no docTiptap. The save runs before any page is up (asItStands).
async function hydrateFund(plate) {
  const fp = docState.fundPending || {};
  const parsed = { sections: fp.sections || {}, unused_source_notes: fp.unused || "", sectionSummaries: fp.sectionSummaries || {} };
  const name = docState.name || "Imported proposal";
  docState.name = name; // the h1 assembled below, as TipTap's save named it
  const asm = assembleSynthDoc(name, parsed);
  docState.trimmed = fp.trimmed || "";                                     // compress-out drawer (v3+)
  docState.unused = fp.unused || "";                                       // quarantined source notes — read-only, never sent to AI
  // AUDIT: #12 — the payload says when there is nothing to check against: the mini flagged an oversized
  // push it had to drop sourceHtml from, and a paste flags source figures that are absent or unreadable. Without
  // the source we CAN'T re-validate figures — running numberCheck on "" would flag every output number as
  // "invented". So mark it unavailable instead of running a check that would be pure noise.
  docState.numberCheck = fp.numberCheckUnavailable
    ? { unavailable: true, reason: fp.numberCheckReason || "" }
    : numberCheck(String(fp.sourceHtml || ""), fp.sections || {}, docState.trimmed, fp.notMoney);  // re-validate, don't trust
  // A null linkSrcCount records that no link check ran. See linkCheckUnavailable: this source has no links to
  // compare against.
  if (fp.linkCheckUnavailable && docState.numberCheck && !docState.numberCheck.unavailable) {
    docState.numberCheck.linkSrcCount = null;
    delete docState.numberCheck.linksAdded;
    delete docState.numberCheck.linksDropped;
  }
  docState.importModel = fp.model || "claude-sonnet-5";
  docState.importFormat = (typeof fp.format === "string" && fp.format) ? fp.format : null; // fund/1|2|3 degrade tier — absent or "" on pre-tier pushes/re-pushes → null, no degrade note
  docState.notes = (fp.notes && typeof fp.notes === "object") ? fp.notes : null; // fund/3: per-section human-only gaps (footer collapse)
  docState.faithfulness = (fp.faithfulness && typeof fp.faithfulness === "object") ? fp.faithfulness : null; // verify+correct audit — present only when drift was found; owner-only transparency, never sent to AI
  // Reflection cards ride in on the import alongside the sections; `notes` above stays separate.
  ingestCards(Array.isArray(fp.cards) ? fp.cards : []);
  // The record, with the questions ingestCards just wrote (their marks are left out of it: the page re-marks them on open).
  docState.doc = plate.toRecord(plate.importToPlate({ html: asm.html, viz: fp.viz, questions: docState.questions }));
  docState.format = "plate";
  if (!docState.sectionsDraft) docState.sectionsDraft = Object.fromEntries(sectionMap(docState.doc).map((s) => [s.key, s.text])); // #41 — immutable snapshot of the imported ORIGINAL section prose (built from sectionMap so it's the SAME shape later diffs compare against). Never overwritten on re-open → inline edits stay diffable.
  // The research findings picked for this proposal (File menu › Find Card); a row imported before
  // 2026-10-11 carries the ones its import fetched.
  docState.evidence = Array.isArray(fp.evidence) ? fp.evidence : [];
  docState.importReport = typeof fp.importReport === "string" ? fp.importReport : "";
  delete docState.fundPending;                                             // one-time — never hydrate twice
  await saveDoc(true);
}
async function openDoc(id) {
  if (!(await flushSave())) return; // failed save → stay on this doc, don't drop edits
  const was = currentDocId;
  toast("Opening…", true);
  const { data, error } = await supabase.from("documents").select("content, updated_at").eq("id", id).single();
  // Returns false rather than bare `undefined` so a caller with NO view painted yet can land the
  // person somewhere. The home-list callers already have the home screen up and ignore it.
  if (error) { hideToast(); toast("Couldn't open that proposal"); return false; }
  // A new import is made into its record by the Plate bundle (hydrateFund), so the bundle is loaded before this
  // proposal becomes the open one: until then no save can reach the pending row. If it does not load, nothing is
  // saved, the row stays pending for the next open, and the open fails as a failed read does.
  let plate = null;
  if (data.content && data.content.fundPending && !data.content.doc) {
    try { plate = await loadPlate(); } catch (e) { console.error("[fund] plate spike failed to load", e); hideToast(); toast("Couldn't open that proposal"); return false; }
    if (currentDocId !== was) return false; // another proposal (or this one, a second time) was opened while it loaded
  }
  currentDocId = id;
  docState = normContent(data.content);
  lastSavedAt = data.updated_at; lastSavedContent = stableStr(docState); // seed the concurrency token before any save (incl. hydrateFund's)
  saveFailed = false; saveConflict = false; // fresh doc — a prior doc's failed-save/conflict flags must not carry over
  if (plate) {                                                              // a new import → materialize once
    // An import the bundle cannot make is not saved either: this proposal stops being the open one, so nothing
    // (going home, another open) can save into the pending row.
    try { await hydrateFund(plate); } catch (e) { console.error("[fund] plate import failed", e); currentDocId = null; hideToast(); toast("Couldn't open that proposal"); return false; }
  }
  hideToast();
  // Every proposal opens on the long page (Stage 6), whatever its format and whatever the URL says; there is no
  // other page (the TipTap page, its section tabs, Notes tab and section panel went in Stage 8). The questions and
  // notes still get their ids, as the section tabs' setup gave them: the margin keeps and resolves a question by its id.
  ensureQIds(); ensureNoteIds();
  setView("app"); syncTitle();
  mountPlate();
  return true;
}

// A password-reset link arrives as `type=recovery` in the URL hash and, crucially, brings a LIVE
// session with it — so without this check boot() treats the visitor as simply signed in.
// An invitation from the dashboard ("Invite user") arrives as type=invite and signs the person in with
// no password set. It lands on the same "choose a password" screen as a reset link, so they leave
// with one — accounts are made only by him (2026-10-14), and this is one of the ways he makes them.
function isRecoveryUrl() { return /[#&]type=(recovery|invite)\b/.test(location.hash || ""); }
// ⛔ STICKY, and it must stay sticky. Tester proved causally (2026-08-20, MutationObserver: recover
// form present at t=148ms, gone at t=172ms) that a recovery URL arriving WITHOUT a live session —
// an expired or already-used link, or an in-app browser that lost its storage — showed the "Save
// password" form for ~25ms before the deferred re-check in onAuthStateChange called showSignin()
// and wiped it back to the hero. The person saw a flash and then nothing, concluded it had not
// worked, and tapped "Email me a reset link" again — spending another message from a quota we are
// told to assume is 2 an hour for the whole project, on the ONE path that costs email at all, and
// the owner's only route out of a lockout.
let recoveryPending = false;
const DEAD_LINK = "That link is no longer valid.";
// Every dead link ends here: an error in the hash, a link that produced no session, or a session that
// is not the link's user. The sign-in form, with a way to ask for a new link.
function showDeadLink(text) {
  recoveryPending = true; // before showSignin: the deferred re-check must not wipe this message
  history.replaceState(null, "", location.pathname); // don't re-fire on refresh
  showSignin(true);
  setFieldError("siEmail", `${text} Enter your email and ask for a new link.`);
}
// "ok" only when the session in hand belongs to the user the link was made for. "dead" for no session
// or another user's. "error" when the session could not be read (a failed refresh): that is not a dead
// link, and the form stays. Returns the session too, so its verified email can name the account.
async function linkSessionState() {
  const { data, error } = await supabase.auth.getSession();
  if (error) return { state: "error" };
  const s = data && data.session;
  if (!s || !s.user || !linkUserId || s.user.id !== linkUserId) return { state: "dead" };
  return { state: "ok", session: s };
}
// Supabase reports a dead link in the hash too — `#error=access_denied&error_code=otp_expired&
// error_description=Email+link+is+invalid+or+has+expired`. That text was being discarded entirely:
// the app just rendered the marketing hero, saying nothing at all. Show it instead of dropping it.
function recoveryHashError() {
  const h = location.hash || "";
  if (!/[#&]error(_code)?=/.test(h)) return "";
  const m = h.match(/[#&]error_description=([^&]*)/);
  const raw = m ? decodeURIComponent(m[1].replace(/\+/g, " ")) : "";
  // Supabase's own wording is already plain enough for a reader; fall back when it is missing.
  // Add the full stop it does not carry — the caller appends a second sentence straight onto this
  // one, which read as "Email link is invalid or has expired Enter your email and ask for a new
  // link." with no break between them.
  const text = raw || DEAD_LINK;
  return /[.!?]$/.test(text) ? text : text + ".";
}
async function boot() {
  // ⛔ RECOVERY MUST WIN OVER EVERY OTHER LANDING, and it must be checked here rather than left to
  // the PASSWORD_RECOVERY handler alone (found in code review, 2026-08-20). supabase-js emits that
  // event from a deferred task AFTER initialization resolves — i.e. after the `await` below has
  // already resumed — so boot() would race ahead, find a leftover fund.pendingShare or
  // fund.lastShare, open that proposal and replace the "Choose a new password" form that had just
  // been shown. The reset token is single-use, so it would be spent with the password never
  // changed. This is the owner's ONLY route out of a lockout, since he has no share link to redeem.
  // A dead or already-used reset link comes back as an ERROR in the hash, with no session. Say so.
  // Silently rendering the marketing hero is what made people tap "Email me a reset link" again and
  // burn another message from a quota assumed to be 2/hour for the whole project.
  const hashErr = recoveryHashError();
  if (hashErr) {
    // ⛔ THE FLAG IS LOAD-BEARING HERE TOO — this branch shipped WITHOUT it and the message was
    // erased 21ms after it rendered. Measured on main after deploy: the branch DID run (lpCta hidden
    // at t=148ms), then onAuthStateChange's deferred showSignin() put the hero back at t=169ms and
    // clearSigninErrors() wiped the text. Proven causally — delaying setTimeout(fn,0) by 30s left the
    // message in place, and the same instrumentation on a plain URL invented nothing.
    // Without it, the person clicks their emailed reset link, waits for it, and is shown the plain
    // marketing hero — so they tap "Email me a reset link" again, on the ONE path that spends email,
    // against a quota assumed to be 2 an hour for the whole project. That loop is what this exists
    // to end. The sibling branch below has always had the flag; this one was simply missed.
    showDeadLink(hashErr);
    return;
  }
  if (isRecoveryUrl()) {
    recoveryPending = true; // sticky — the deferred re-check in onAuthStateChange must not wipe this
    showSignin(true); setSigninMode("recover"); focusSoon("siNew");
    // getSession() waits for supabase-js to finish with the link, so this is decided once.
    const link = await linkSessionState();
    if (link.state === "dead") { showDeadLink(DEAD_LINK); return; }
    if (link.state === "ok") { recoverEmail = link.session.user.email || ""; $("siIntro").textContent = siIntroText("recover"); }
    return;
  }
  const s = await getSession();
  if (!s) { showSignin(); return; }
  showHome();
}
// Owner's report 2026-08-20: "their logins should persist like mine does. they have to sign in
// multiple times."
//
// ⚠️ CORRECTED THE SAME DAY, BY CODE REVIEW — READ THIS BEFORE BELIEVING THE COMMIT MESSAGE.
// This was originally written up as the FIX for that report, on the theory that a token refresh
// failing over flaky mobile data emitted a null session and evicted the user. In the pinned
// @supabase/auth-js that theory is WRONG: a retryable fetch error during refresh never removes the
// session and emits no event at all, and every null-session notification comes from
// _removeSession(), which always carries SIGNED_OUT. So the deferred re-check below only ever fires
// for INITIAL_SESSION with no session — a genuinely signed-out visitor.
//
// The change is kept because the old `if (!sess) showSignin()` was wrong on its own terms: it
// evicted on any null without looking at the event, which is fragile and would break the moment
// that library behaviour changed. But it is NOT a fix for what he reported, and the repeated
// sign-ins remain UNDIAGNOSED. Next candidates, in order: the project's JWT/refresh-token lifetime
// settings, and in-app browser storage (reviewers open these links inside WhatsApp/Telegram, whose
// embedded browsers can discard localStorage between opens).
//
// What it does now: a real SIGNED_OUT evicts immediately, as it must; any other null is re-checked
// before anyone is thrown out.
//
// ⛔ THE setTimeout IS LOAD-BEARING — DO NOT "TIDY" IT INTO A DIRECT await. supabase-js holds a
// navigator lock for the duration of this callback, and any supabase call awaited inside it
// deadlocks against the concurrent getSession() at init: the whole page hangs BLANK. That is the
// curate.html blank-screen saga (2026-07-02), and this file must not repeat it. Deferring to a
// fresh task escapes the lock.
supabase.auth.onAuthStateChange((event, sess) => {
  isAdminCached = null; roleCache = null;
  // A reset link brings them back WITH a live session — so this arrives before the `if (sess)`
  // early return below and must be handled first, or a recovery silently lands on the home screen
  // with the password still unchanged.
  if (event === "PASSWORD_RECOVERY") {
    recoverEmail = (sess && sess.user && sess.user.email) || ""; // so the intro can say WHOSE password this is
    if (sess && sess.user) linkUserId = sess.user.id; // the event is the proof — and the only proof another tab gets
    recoveryPending = true; // same sticky flag as boot()'s branch — nothing may wipe this screen
    setView("signin"); revealSignin(); setSigninMode("recover"); focusSoon("siNew"); return;
  }
  if (sess) return;                                    // signed in (or refreshed) — nothing to do
  if (event === "SIGNED_OUT") { endPlateSession(); showSignin(); return; } // deliberate: evict now, synchronously
  setTimeout(async () => {                              // escape the auth lock before touching supabase
    // ⛔ Do not wipe a recovery screen. This deferred re-check is what erased the "Save password"
    // form ~25ms after a dead reset link rendered it (tester, 2026-08-20, measured causally). The
    // person needs to stay on that screen and read why the link failed.
    if (recoveryPending) return;
    const { data } = await supabase.auth.getSession();
    if (!data.session) showSignin();                    // genuinely gone — only now show the gate
  }, 0);
});

// where every successful sign-in lands.
async function afterSignedIn() {
  showHome();
}
async function onSigninClick() {
  const btn = $("siBtn");
  if (btn.disabled) return; // Enter bypasses the disabled button → guard against a double submit
  if (siMode === "password") return signInWithPasswordStep(btn);
  return saveRecoveredPasswordStep(btn);
}
// EVERYDAY sign-in — stock signInWithPassword, and the whole point of the change: no email, no code,
// nothing to wait for. This is the path the owner's reviewers were failing on when every single
// sign-in meant fetching a fresh code from their inbox.
async function signInWithPasswordStep(btn) {
  const email = $("siEmail").value.trim();
  const password = $("siPass").value;
  if (!email || !email.includes("@")) { setFieldError("siEmail", "Enter your email address"); return; }
  if (!password) { setFieldError("siPass", "Enter your password"); return; }
  btn.disabled = true; btn.textContent = "Signing in…";
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  btn.disabled = false; btn.textContent = "Sign in";
  // One message for a wrong password AND for an account that does not exist — deliberately.
  // Distinguishing them would tell an outsider which addresses hold accounts here.
  // On the field, not in a toast that floats away after ~2.6s — and on a phone a bottom-anchored
  // toast can sit behind the keyboard entirely, which reads as "I tapped it and nothing happened".
  // An account that was never confirmed is not a wrong password: opening a reset link confirms the
  // address. His words, 2026-10-14.
  if (error && (error.code === "email_not_confirmed" || /email not confirmed/i.test(error.message || ""))) {
    setFieldError("siPass", "This address still needs confirming. Tap “Email me a reset link” to confirm it and set a password.");
    return;
  }
  if (error) { setFieldError("siPass", "That email and password didn't match."); return; }
  await afterSignedIn();
}
// FORGOTTEN PASSWORD — stock resetPasswordForEmail. The ONLY path here that sends an email.
async function onForgotClick() {
  const email = $("siEmail").value.trim();
  // Design review, item 6: a toast is the wrong home for a PREREQUISITE — it names something the
  // person cannot see, then floats away after ~2.6s. Carbon's own idiom is invalid + invalid-text
  // on the field itself, which stays on screen until they fix it.
  if (!email || !email.includes("@")) { setFieldError("siEmail", "Enter your email address first, then tap this again"); $("siEmail").focus(); return; }
  const btn = $("siBtn"); btn.disabled = true; btn.textContent = "Sending…";
  const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: location.origin + location.pathname });
  btn.disabled = false; btn.textContent = "Sign in";
  // Same non-disclosure rule as a failed sign-in: never confirm whether that address has an account.
  if (error) { toast("Couldn't send the reset email just now — try again in a few minutes", 6000); return; }
  // On the field as well as the toast. This is the ONE path that spends an email, and someone who
  // cannot see the confirmation taps it again — which is how a 2-an-hour quota gets burned, on the
  // owner's only route out of a lockout.
  setFieldNote("siEmail", "If that email has an account, a reset link is on its way.");
  toast("If that email has an account, a reset link is on its way", 7000);
}
// After a reset link brings them back, supabase-js emits PASSWORD_RECOVERY with a live session, and
// onAuthStateChange puts the form into "recover" mode. Stock updateUser from there.
async function saveRecoveredPasswordStep(btn) {
  const pw = $("siNew").value;
  if (pw.length < SI_MIN_PW) { setFieldError("siNew", `Use at least ${SI_MIN_PW} characters`); return; }
  // ⛔ Never change a password for anyone but the link's user (see linkUserId).
  btn.disabled = true; btn.textContent = "Saving…";
  const link = await linkSessionState();
  if (link.state !== "ok") {
    btn.disabled = false; btn.textContent = "Save password";
    if (link.state === "dead") showDeadLink(DEAD_LINK);
    else setFieldError("siNew", "Couldn't save that password — try again");
    return;
  }
  // ⛔ With exactly the token that passed the check, not supabase.auth.updateUser: that re-reads the
  // stored session (auth-js 2.110.2 takes no JWT for it), so a session swapped in meanwhile — another
  // tab signing in — would get the new password. Same GoTrue call it makes: PUT /auth/v1/user.
  let saved = false;
  try {
    const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
      method: "PUT",
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${link.session.access_token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ password: pw }),
    });
    const user = res.ok ? await res.json() : null;
    saved = !!(user && user.id === linkUserId);
  } catch { saved = false; }
  btn.disabled = false; btn.textContent = "Save password";
  if (!saved) { setFieldError("siNew", "Couldn't save that password — try again"); return; }
  toast("Password saved — use it to sign in next time");
  await afterSignedIn();
}

const SEC_BY_KEY = Object.fromEntries(SECTIONS.map((s, i) => [s.key, { num: i + 1, title: s.title.replace(/^\d+\s*·\s*/, "") }]));
function secTitle(k) { return (SEC_BY_KEY[k] && SEC_BY_KEY[k].title) || "Other"; }

// A Plate value's section-tagged headings → [{key,title,text}]: a fresh import's record (hydrateFund), for its
// sectionsDraft. A list is one entry, as v16's one list
// block was, so its lines are never parted by a blank line, and a heading inside a list run is not a section.
function sectionMap(value) {
  return sectionBlocks(value).map((s) => ({ key: s.key, title: nodeSendText(s.heading).trim(), // a heading holds only text
    text: s.blocks.map(nodeSendText).filter((t) => t.trim()).join("\n\n") })); // incl. fund-sug
}
// The one walk that says which blocks belong to which section: a heading with a `section` starts one, unless it sits
// inside a list run; what comes before the first is no section's. sectionMap and sectionInputs both read it.
function sectionBlocks(value) {
  const out = []; let cur = null;
  printedBlocks(value || []).forEach((e) => {
    if (!isPrintedList(e) && /^h[1-6]$/.test(e.type) && e.section) { if (cur) out.push(cur); cur = { key: e.section, heading: e, blocks: [] }; }
    else if (cur) cur.blocks.push(e);
  });
  if (cur) out.push(cur);
  return out.filter((s) => s.key);
}

// stable per-question id → triage keys (id@section) survive a re-run, which REPLACES the question set and
// would otherwise shift array-index keys onto the wrong questions.
const newQId = () => "q" + (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2, 10) + Date.now().toString(36));
function ensureQIds() { for (const q of (docState && docState.questions) || []) if (q && !q.id) q.id = newQId(); }
// per-section to-do notes get stable ids too. Notes are NOT cross-section → a plain id (no @section).
function ensureNoteIds() { const notes = (docState && docState.notes) || {}; for (const k of Object.keys(notes)) for (const nt of notes[k] || []) if (nt && !nt.id) nt.id = newQId(); }
// merge a fresh pressure-test run into docState.questions; a question matching a prior one (section+text)
// keeps its id so its kept/set-aside triage carries across re-runs.
/**
 * Reflection cards → `docState.questions`.
 *
 * ⛔ THIS IS THE ONLY WRITER OF `docState.questions`, and the whole card surface hangs off it:
 * triage keys on `id@section`, `keptQuestions` reads it, `exportQuestions` gates on it and
 * `exportSidebars` turns it into the PDF's question column. A card therefore has to carry `id`,
 * `question` and `section` — anything else is optional decoration.
 *
 * ⭐ `id` is preserved across re-imports by matching on `section|question`, so a card the owner has
 * already kept or set aside does not lose that triage when the same draft is re-imported. That is
 * the same key the pressure test used, for the same reason.
 *
 * ⚠️ No `anchor`. It used to be re-joined from the rewriter's bets, which are gone — so the export
 * still numbers each card in the column but draws no superscript beside the prose. `proposalData`
 * tolerates a missing anchor by design.
 */
// A card's text: `question` when it has one, `note` otherwise. The import counts cards by this rule
// and ingestCards keeps them by it, so the count it reports is the count that arrives.
function cardText(c) { return String((c && (c.question !== undefined ? c.question : c.note)) || "").trim(); }
function ingestCards(newCards) {
  const idByKey = {}, sugById = {};
  for (const q of (docState && docState.questions) || []) if (q && q.question) {
    idByKey[(q.section || "") + "|" + q.question] = q.id;
    sugById[q.id] = JSON.stringify(q.suggestions || []);
  }
  docState.questions = (newCards || [])
    .map((c) => {
      // Tolerates {question} and {note} alike — the paste intake carries its open items under `note`.
      const text = cardText(c);
      if (!text) return null;
      const section = String((c && c.section) || "");
      return { ...c, question: text, section, origin: "card",
               id: idByKey[section + "|" + text] || newQId() };
    })
    .filter(Boolean);
  // AUDIT: #26 — suggestion keeps are keyed by list position (qid#si). A preserved card whose
  // suggestion list CHANGED on this re-import would re-attach the old keeps to whatever now sits at
  // those positions — and the export then prints the wrong pick as the author's endorsement. Clear that card's suggestion-keeps so the owner re-triages; an identical list keeps
  // its marks.
  const tri = docState.suggestionTriage;
  if (tri) for (const q of docState.questions) {
    if (q.id in sugById && sugById[q.id] !== JSON.stringify(q.suggestions || [])) {
      for (const k of Object.keys(tri)) if (k.startsWith(q.id + "#")) delete tri[k];
    }
  }
}
async function setTriage(key, status) {
  docState.questionTriage = docState.questionTriage || {};
  if (status === "open") delete docState.questionTriage[key]; else docState.questionTriage[key] = status;
  await saveMeta();
}
// Resolve and Reopen, on the long page's margin (plateActions is the only caller): a record of
// its own, docState.questionResolved["<id>@<section>"] = true while resolved and the key gone once reopened,
// beside keep and set aside and never changing them. The PDF's question column reads only questionTriage
// "kept", and the question's comment mark stays either way.
async function setResolved(key, on) {
  docState.questionResolved = docState.questionResolved || {};
  if (on) docState.questionResolved[key] = true; else delete docState.questionResolved[key];
  await saveMeta();
}
// The pair (spec 2026-10-11 §2), from the long page's pair card (plateActions is the only caller): the beneficiary and
// the delivery agent, each the text of a stakeholder-table row's first cell. Two non-empty strings, or it is refused.
async function setPair(pair) {
  const b = pair && typeof pair.beneficiary === "string" ? pair.beneficiary.trim() : "";
  const d = pair && typeof pair.deliveryAgent === "string" ? pair.deliveryAgent.trim() : "";
  if (!b || !d) throw new Error("a pair needs two stakeholders");
  docState.pair = { beneficiary: b, deliveryAgent: d };
  await saveMeta();
}
// a suggestion (a grounded option under a question) carries its own keep/set-aside, keyed `qid#si` (question-scoped,
// NOT per-section — a suggestion is a property of the question). keptQuestions keeps only the kept ones.
// Mirrors setTriage.
async function setSuggestionTriage(qid, si, status) {
  docState.suggestionTriage = docState.suggestionTriage || {};
  const k = qid + "#" + si;
  if (status === "open") delete docState.suggestionTriage[k]; else docState.suggestionTriage[k] = status;
  await saveMeta();
}
// coach inline edits — suggestions and the question stem (#65), from the long page's margin. Edits persist into docState → saveDoc, reach
// the export via keptQuestions, and stamp editedFrom (the pristine original) once → the card shows an "edited"
// marker. ⚠️ An edited stem is LOST on the next import: ingestCards matches old→new on
// (section|question text), so a card whose text the coach changed no longer matches and comes back
// with a new id — which resets its triage. Deliberate ("re-import resets"), no extra code needed.
async function saveSuggestionEdit(qid, si, gist, why) {
  const q = ((docState && docState.questions) || []).find((x) => x.id === qid);
  const sg = q && q.suggestions && q.suggestions[si];
  if (!sg) return;
  if (!gist) { toast("A suggestion needs some text"); return; } // keep the field open so the coach can fix it
  if (!sg.editedFrom) sg.editedFrom = { gist: sg.gist || "", why: sg.why || "" };
  sg.gist = gist; sg.why = why;
  await saveMeta();
}
// AUDIT: #65 — coach reword of a question stem. Owner-only; discarded on the next PT re-run (see the note above saveSuggestionEdit).
async function saveQuestionEdit(id, text) {
  const q = ((docState && docState.questions) || []).find((x) => x.id === id);
  if (!q) return;
  if (!text) { toast("A question needs some text"); return; } // keep the field open so the coach can fix it
  if (!q.editedFrom) q.editedFrom = { question: q.question || "" };
  q.question = text;
  await saveMeta();
}
// The KEPT pressure-test questions, computed live from triage. Its one caller now is exportQuestions(),
// which refuses the empty-triage fallback below; the fallback served the peer-review share payload
// and the owner's review cards, both removed 2026-09-21.
function keptQuestions() {
  const tri = (docState && docState.questionTriage) || {};
  const sTri = (docState && docState.suggestionTriage) || {};
  const qs = (docState && docState.questions) || [];
  // owner decision (2026-08-11): keptQuestions() requires an explicit "kept" entry per question, and
  // an owner who hasn't triaged anything yet has an EMPTY questionTriage — filtering that against
  // "some tri[k]===kept" would silently share/annex NOTHING. Untouched (no keys at all) must fall
  // back to every question, same as before any triage UI existed — tick nothing → show everything;
  // tick some → only those; never a silent blank. A triage that HAS entries but none "kept" (every
  // question explicitly set aside) is a real, deliberate empty set and stays empty — only the
  // genuinely-untouched {} case gets the fallback.
  if (Object.keys(tri).length === 0) return qs.map((q) => ({ ...q }));
  return qs
    .filter((q) => q && q.id && Object.keys(tri).some((k) => k.startsWith(q.id + "@") && tri[k] === "kept")) // kept in ANY section
    .map((q) => ({ ...q, suggestions: (q.suggestions || []).filter((s, si) => sTri[q.id + "#" + si] === "kept") })); // only the author's KEPT suggestions
}
/**
 * The PDF export's own view of the kept questions — deliberately NOT keptQuestions().
 *
 * ⛔ keptQuestions()'s empty-triage fallback above served its two former callers, the peer share
 * payload and the owner's review cards, both removed 2026-09-21. It is
 * also exactly what removed this feature on 2026-08-12: the exported PDF was the artefact the owner
 * sends a funder, and on an untriaged proposal that fallback put every AI-generated pressure-test
 * question into a funder's hands — questions the app already hid from a funder's screen.
 *
 * So the export refuses the fallback. No triage at all means NO questions in the PDF, not every
 * question. An owner who has triaged nothing gets a sidebar saying so, which is recoverable; a
 * funder reading the coach's raw challenges is not.
 */
function exportQuestions() {
  const tri = (docState && docState.questionTriage) || {};
  return Object.keys(tri).length === 0 ? [] : keptQuestions();
}

/** Kept questions and notes bucketed per section, in the shape proposalData()'s question column wants. */
function exportSidebars() {
  const tri = (docState && docState.questionTriage) || {};
  const out = {};
  const bucket = (key) => (out[key] = out[key] || { questions: [], notes: [] });
  for (const q of exportQuestions()) {
    // the triage key is `${id}@${section}` — a question kept in two sections shows in both
    for (const k of Object.keys(tri)) {
      // `anchor` rides along so the walker can place this question's number beside the phrase it is
      // about. Empty for a question whose bet carried none, and for every proposal written before
      // the rewriter learned to emit one — in which case no marker is drawn and the numbered entry
      // still prints. The question TEXT is what a funder reads either way.
      if (tri[k] === "kept" && k.startsWith(q.id + "@")) {
        bucket(k.slice(q.id.length + 1)).questions.push({ text: q.question || "", anchor: q.anchor || "" });
      }
    }
  }
  // ⛔ NOTES ARE NOT EXPORTED. Owner's decision, 2026-09-18: the PDF carries the kept open questions
  // and nothing else — no notes, no sources, no evidence. Two consequences worth knowing: the
  // claim/gap tags have nothing left to tag, and the rewriter's "Some values … would strengthen the
  // proposal" line (a gap note, printed on 22 of 29 pages) no longer reaches a funder.
  return out;
}


  // (same pattern as homeSort, app.js ~3907), not read back from .value at submit time: a cds-dropdown's
  // .value only reliably reflects the trigger after the ITEM's `selected` attribute moved too
  // (carbon-house gotcha) — safest to just remember what the user actually picked.


/* ---- who may author: is_admin, or a coach/admin role in profiles (the Admin view went 2026-09-21) ---- */
let isAdminCached = null;
async function checkAdmin() {
  if (isAdminCached !== null) return isAdminCached;
  try { const { data } = await supabase.rpc("is_admin"); isAdminCached = !!data; } catch { isAdminCached = false; }
  return isAdminCached;
}
// Account-role gate (separate axis from the per-doc reviewMode). New accounts default to `peer`; only
// coach/admin may author. Least privilege: a missing/unreadable role is treated as `peer`. Cached like
// isAdminCached (reset in onAuthStateChange + signout). Reading own role is allowed by the `read own
// profile` RLS policy — the SERVER gate (archive/f9-coach-only-create.sql) is the real enforcement; this is UX.
let roleCache = null;
async function myRole() {
  if (roleCache !== null) return roleCache;
  try {
    const uid = await myUserId();
    if (!uid) return "peer"; // signed out — don't cache
    const { data } = await supabase.from("profiles").select("role").eq("user_id", uid).maybeSingle();
    roleCache = (data && data.role) || "peer";
  } catch { roleCache = "peer"; }
  return roleCache;
}
async function canAuthor() {
  const r = await myRole();
  if (r === "coach" || r === "admin") return true;
  return checkAdmin(); // backstop: admin via is_admin RPC even if the role column disagrees (no double-fetch in the common coach case)
}

/* ---- number-integrity guardrail: source figures must survive the rewrite (client mirror of the audit) ---- */
/* ---- amounts written with no currency sign: "15 lakh", "1,00,000", "1.25 Million" ----
   extractNums counts each of these as rupees, so a page count read as money: IDAF run 4's
   "1.25 Million" pages was listed as a fabricated amount. At import, Jev judges which of them are
   money (judgeFigures); the rest reach numberCheck as `notMoney` and are left out of it. */
const BARE_LAKH = /\b(\d[\d,]*(?:\.\d+)?)\s?(lakh|crore|cr)\b/;
const BARE_GROUPED = /\b(\d{1,2}(?:,\d{2})+,\d{3})\b/;
const BARE_WORDED = /\b(\d[\d,]*(?:\.\d+)?)\s?(million|billion|thousand)\b/;
const CURRENCY_BEFORE = /(?:₹|\$|us\$|usd|€|eur|£|gbp)\s?$/i;
const figureKey = (f) => String(f).toLowerCase().replace(/\s+/g, " ").trim();
// Every figure in `text` that one of the bare patterns reads, and that has no currency sign before it.
function bareFigures(text) {
  const t = String(text || ""), out = [];
  for (const re of [BARE_LAKH, BARE_GROUPED, BARE_WORDED]) {
    const g = new RegExp(re.source, "gi");
    let m;
    while ((m = g.exec(t))) if (!CURRENCY_BEFORE.test(t.slice(0, m.index))) out.push(m[0].trim());
  }
  return [...new Set(out)];
}
// `t` with each bare occurrence of the figures in `figs` blanked. An occurrence with a currency sign
// before it is money whatever was judged, and stays.
function withoutFigures(t, figs) {
  for (const f of figs || []) {
    const pat = String(f).replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, "\\s?");
    t = t.replace(new RegExp(`(?<![\\d.,])${pat}(?!\\w)`, "gi"), (m, off, str) => (CURRENCY_BEFORE.test(str.slice(0, off)) ? m : " "));
  }
  return t;
}
function extractNums(html, notMoney) {
  const t = withoutFigures(String(html || "").replace(/<[^>]+>/g, " "), notMoney);
  const rupees = new Set(), pct = new Set();
  let m;
  // ⛔ A unit is a whole word and a number never ends on its comma: "₹40,00,000, creating" read as
  // ₹40,00,000 CRORE — the comma joined the number and "cr" was the start of "creating".
  const re = /₹\s?(\d(?:[\d,]*\d)?(?:\.\d+)?)\s?(?:(K|k|lakh|crore|cr)\b)?/gi;
  while ((m = re.exec(t))) { let n = parseFloat(m[1].replace(/,/g, "")); const suf = (m[2] || "").toLowerCase(); if (suf === "k") n *= 1e3; else if (suf === "lakh") n *= 1e5; else if (suf === "crore" || suf === "cr") n *= 1e7; if (!isNaN(n)) rupees.add(Math.round(n)); }
  const re2 = new RegExp(BARE_LAKH.source, "gi");
  while ((m = re2.exec(t))) { const n = parseFloat(m[1].replace(/,/g, "")); if (!isNaN(n)) rupees.add(Math.round(n * (/^lakh$/i.test(m[2]) ? 1e5 : 1e7))); }
  // Indian-grouped bare amount (e.g. 41,66,000 / 2,52,70,000) — a rupee even without the ₹ glyph, so a cell
  // that lost its ₹ during rewrite never reads as a dropped/invented figure. The ,dd grouping won't match
  // plain counts (400, 1,000), so this stays scoped to real money.
  const re2b = new RegExp(BARE_GROUPED.source, "g");
  while ((m = re2b.exec(t))) { const n = parseInt(m[1].replace(/,/g, ""), 10); if (!isNaN(n)) rupees.add(n); }
  // foreign / worded amounts ($2M, USD 5,000, €10,000, "3 million") so a hallucinated non-₹ figure can't slip
  // past the invented-figure gate. Folded into the same set — cross-currency magnitude collisions are rare and
  // this only ever ADDS coverage vs the prior INR-only scope.
  const re4 = new RegExp(/(?:\$|us\$|usd|€|eur|£|gbp)\s?(\d(?:[\d,]*\d)?(?:\.\d+)?)\s?(?:(k|thousand|m|mn|million|b|bn|billion|lakh|crore|cr)\b)?|/.source + BARE_WORDED.source, "gi");
  while ((m = re4.exec(t))) { const g = m[1] || m[3]; if (!g) continue; let n = parseFloat(g.replace(/,/g, "")); const s = (m[2] || m[4] || "").toLowerCase(); if (/^(k|thousand)$/.test(s)) n *= 1e3; else if (/^(m|mn|million)$/.test(s)) n *= 1e6; else if (/^(b|bn|billion)$/.test(s)) n *= 1e9; else if (s === "lakh") n *= 1e5; else if (/^(crore|cr)$/.test(s)) n *= 1e7; if (!isNaN(n)) rupees.add(Math.round(n)); }
  const re3 = /(\d{1,3})\s?%/g;
  while ((m = re3.exec(t))) pct.add(m[1]);
  return { rupees, pct };
}
// Catch URLs both as href="…" AND as bare text — Docling often leaves a URL unlinked in a table cell,
// which the LLM then correctly linkifies; that must NOT read as "invented". We reduce each URL to an
// alphanumeric key (protocol/punct/whitespace stripped) and match by prefix, so a Docling line-break
// space, a trailing slash, or href-vs-plaintext never causes a false mismatch.
function extractLinks(html) {
  const keys = new Set(); const s = String(html || ""); let m;
  const key = (u) => u.replace(/^https?:\/\//i, "").replace(/[^a-z0-9]/gi, "").toLowerCase().slice(0, 40);
  const href = /href\s*=\s*["']([^"']+)["']/gi;
  while ((m = href.exec(s))) { if (/^https?:/i.test(m[1])) { const k = key(m[1]); if (k.length > 10) keys.add(k); } }
  const bare = /https?:\/\/[^\s"'<>]+/gi; const text = s.replace(/<[^>]+>/g, " ");
  while ((m = bare.exec(text))) { const k = key(m[0]); if (k.length > 10) keys.add(k); }
  return keys;
}
// links in A not "covered" by any link in B (prefix match tolerates truncation from the space artifact)
function linksMissing(a, b) {
  const bk = [...b];
  return [...a].filter((x) => !bk.some((y) => y.startsWith(x.slice(0, 20)) || x.startsWith(y.slice(0, 20))));
}
// `trimmed` (v3+) is the compress-out drawer — a number/link moved there is PRESERVED, not dropped, so it
// counts toward the output just like a section does. Older docs pass no trimmed (undefined → "").
// remove augmentor-added content (any fund-sug element: span/td/tr/table/p) from an HTML string
function stripFundSugHtml(html) {
  try { const d = new DOMParser().parseFromString("<div>" + String(html || "") + "</div>", "text/html"); d.querySelectorAll(".fund-sug").forEach((e) => e.remove()); return d.body.innerHTML; }
  catch { return String(html || ""); }
}
/* Html with each diagram block's Mermaid code set out as text. extractNums strips tags, and a diagram's
   code lives in its data-viz attribute, so without this a figure drawn in a diagram — the one place a
   funder sees it at full page width — escaped the invented-figure check entirely. */
function diagramText(html) {
  return String(html || "").replace(/<figure data-viz="([^"]*)"><\/figure>/g, (m, attr) => {
    try {
      const b = JSON.parse(attr.replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&"));
      return b && b.type === "mermaid" && typeof b.code === "string" ? " " + b.code + " " : m;
    } catch { return m; }
  });
}
function numberCheck(sourceHtml, sections, trimmed, notMoney) {
  const outHtml = diagramText(Object.values(sections || {}).map((v) => String(v || "")).join(" ") + " " + String(trimmed || ""));
  // fund-sug = augmentor-ADDED content (a suggested benchmark / target). Its numbers are INTENTIONAL additions,
  // not fabrications — exclude them from the "invented" check; count them for the send gate. `[X]` = a gap to fill.
  const outClean = stripFundSugHtml(outHtml);
  const suggested = (outHtml.match(/class=['"]?fund-sug/gi) || []).length;
  const blanks = (outHtml.replace(/<[^>]+>/g, " ").match(/\[X\]/g) || []).length;
  const src = extractNums(sourceHtml, notMoney), out = extractNums(outHtml, notMoney), outC = extractNums(outClean, notMoney);
  const fabricated = [...outC.rupees].filter((x) => !src.rupees.has(x));  // in output (excl. suggestions), NOT in source → dangerous
  const dropped = [...src.rupees].filter((x) => !out.rupees.has(x));      // in source, NOT in output → review
  const srcL = extractLinks(sourceHtml), outL = extractLinks(outHtml);
  const linksDropped = linksMissing(srcL, outL);   // a source link the rewrite lost
  const linksAdded = linksMissing(outL, srcL);    // a URL in output that matches nothing in source → possibly invented
  return { srcCount: src.rupees.size, fabricated, dropped, linkSrcCount: srcL.size, linksDropped, linksAdded, suggested, blanks };
}
/* ---- PDF export: the Plate value → the proposal as data ------------------------------------------------------------
 *
 * The PDF is laid out by a Typst template that lives with the service, `proposal.typ` in aikyamspace/typst-service
 * (~/typst-service), and compiled there by the real `typst` binary — not in the browser, which would need full
 * 'unsafe-eval' (FUND.md "Why it is not compiled in the browser"). Fundable sends the proposal's CONTENT as JSON: every
 * word a string, never Typst markup, so there is nothing to escape and no text that can break the compile. The shape
 * is the one proposal.typ's header states for PROPOSAL_TEMPLATE; the service refuses a version it does not have, and
 * deploy.sh refuses to ship while typst.folktaler.com/health does not list it.
 *
 * What stays here decides the content: which blocks print and in what order (plate/printed.mjs), the honesty marks each
 * run carries, where a question's number goes, which section a block belongs to, and which diagram is drawn from its
 * code and which from its picture. How any of it looks is the template's.
 *
 * Every function in this block takes JSON and returns JSON, touching no DOM, editor or docState, so the tests lift them
 * out of this file and run them in plain Node.
 */
const PROPOSAL_TEMPLATE = 1;

/**
 * Whitespace-normalised, for anchor matching only. Never used for output.
 *
 * The anchor is a span the rewriter copied out of the section's HTML; by the time it reaches here the same words may
 * be broken across several Plate leaves (the walker's runs), because a `fundSug` or `sugg` mark on a figure inside the
 * phrase splits the leaf. Comparing on a normalised running concatenation is what lets a match span those splits.
 */
const anchorNorm = (s) => String(s || "").replace(/\s+/g, " ").trim().toLowerCase();

/** A printed run's mark as the template reads it. Only what prints: a mark's `basis` ("kept for the coach, never shown
 * to the applicant" — the PDF goes to a funder) and a link's target, rel and class are not sent. */
function pdfMark(m) {
  const a = m.attrs || {};
  if (m.type === "link") return { type: "link", href: String(a.href || "") };
  if (m.type === "sugg" || m.type === "fundSug") return { type: m.type, kind: String(a.kind || "") };
  return { type: m.type };
}

/**
 * A block's inline children as printed runs (plate/printed.mjs printedRuns), with question numbers placed where their
 * anchors land.
 *
 * `pending` is the section's not-yet-placed questions, each `{n, anchor}`. After each run the running plain text is
 * tested against every outstanding anchor; the first run that completes one is followed by `{sup: n}`, and that
 * question is struck off. So the number sits at the end of the phrase the rewriter pointed at, not at the start of
 * the paragraph. A run is the leaves printed.mjs merges (equal marks), so the number follows the whole merged run.
 *
 * ⛔ NO MATCH MEANS NO NUMBER. The question still prints in the column with its number. The owner edits on the long
 * page after the rewriter runs, so a stale anchor is ordinary — and a number against the wrong phrase is worse than none.
 */
function pdfRuns(children, pending) {
  const out = [];
  let acc = "";
  for (const run of printedRuns(children)) {
    out.push(run.br ? { br: true } : { text: run.text, marks: run.marks.map(pdfMark) });
    if (!pending || !pending.length) continue;
    acc += run.text || "";
    const hay = anchorNorm(acc);
    for (let i = pending.length - 1; i >= 0; i--) {
      const a = anchorNorm(pending[i].anchor);
      if (a && hay.includes(a)) { out.push({ sup: pending[i].n }); pending.splice(i, 1); }
    }
  }
  return out;
}

/**
 * A Plate table: every child a row, every row's child a cell (th and td alike), the first row the header.
 *
 * ⚠️ The column count is the sum of colspan across the FIRST row, not the cell count. The budget (7 categories × 3
 * years) and ask (6 rows × 3 years) tables both grow a merged total row, and Typst does not error on a wrong count —
 * it wraps cells onto the next row.
 * ⛔ `fundSug` rides on the ROW (`tr`) as well as the cell: the rewriter emits whole marked rows and the import keeps
 * the mark on the row. Reading it off cells alone printed an augmentor-invented budget line as a figure the coach had
 * stated. The row's ▲ goes on its first cell only (`target`).
 * `pending` is threaded into the cells: six of the eight sections are table-first, and an anchor is "usually the
 * number", which lives in a cell.
 */
function pdfTable(node, pending) {
  const rows = node.children || [];
  if (!rows.length) return null;
  const span = (n) => (Number(n) > 1 ? Number(n) : 1);
  const columns = (rows[0].children || []).reduce((n, c) => n + span(c.colSpan), 0);
  const cell = (c, rowSug, first) => {
    const sug = c.fundSug || rowSug;
    return {
      blocks: printedBlocks(c.children).map((b) => pdfBlock(b, pending)).filter(Boolean),
      colspan: span(c.colSpan), rowspan: span(c.rowSpan),
      marked: !!sug, target: sug === "target" && (c.fundSug ? true : first),
    };
  };
  return { type: "table", columns, rows: rows.map((r) => (r.children || []).map((c, i) => cell(c, r.fundSug || "", i === 0))) };
}

/** A list rebuilt by printed.mjs. An item's own honesty mark (its first line's listItemMark) is read here: the item is
 * built from its blocks, never passed to pdfBlock whole, so a list item the import marked whole would print plain. */
function pdfList(list, pending) {
  return {
    type: "list", ordered: list.ordered,
    items: list.items.map((item) => ({ marked: !!item.fundSug, blocks: item.blocks.map((b) => pdfBlock(b, pending)).filter(Boolean) })),
  };
}

/** One entry of printedBlocks: a Plate block, or a list. null for anything with no printed form. */
function pdfBlock(node, pending) {
  if (!node) return null;
  if (isPrintedList(node)) return pdfList(node, pending);
  let out = null;
  switch (node.type) {
    case "p": { const runs = pdfRuns(node.children, pending); out = runs.length ? { type: "par", runs } : null; break; }
    case "h1": case "h2": case "h3": case "h4": case "h5": case "h6": {
      // Level 1 is the document title (the cover owns it), and a level-2 heading that STARTS a section is placed by
      // proposalData. Any heading here is a sub-heading: an H1 or H2 prints at level 2, one step above an H3 and
      // deeper at level 3. No `pending`: a number never sits in a heading. An empty heading prints nothing.
      const runs = pdfRuns(node.children);
      const blank = runs.every((r) => !r.br && !r.marks.length && !r.text.trim());
      out = blank ? null : { type: "heading", level: Number(node.type[1]) <= 2 ? 2 : 3, runs };
      break;
    }
    case "section_lead": return { type: "lead", runs: pdfRuns(node.children, pending) };
    case "blockquote": out = { type: "quote", blocks: printedBlocks(node.children).map((c) => pdfBlock(c, pending)).filter(Boolean) }; break;
    case "code_block": return { type: "code", text: (node.children || []).map(plateNodeText).join("\n") };
    case "hr": return { type: "rule" };
    case "table": return pdfTable(node, pending);
    case "viz": case "systems_map": case "img": return null;   // only a top-level diagram prints (proposalData)
    // Any element the PDF has no form for: a paragraph of its words, its honesty mark kept.
    default: { const runs = pdfRuns([{ text: plateNodeText(node) }], pending); out = runs.length ? { type: "par", runs } : null; }
  }
  // A block's own honesty mark (`fundSug` on the element) is not a leaf mark — reading only the runs' marks loses the
  // mark on every augmentor-added paragraph.
  if (out && node.fundSug) out.marked = true;
  return out;
}

/** A question as the column prints it. Tolerates a bare string as well as {text, anchor}. */
function pdfQuestion(q) { const t = q && q.text !== undefined ? q.text : q; return t == null ? "" : String(t); }

/**
 * The whole proposal, as proposal.typ reads it.
 *
 * ⚠️ Walks in DOCUMENT ORDER, not by iterating SECTIONS. The import parks a diagram whose key is no section at the end of
 * the value, outside every section (plate/import.mjs insertViz) — iterating the section list would drop it without a
 * word, which is the silent-loss shape this feature already died of once.
 *
 * ⛔ A section starts at an H2 carrying `section`. An H2 typed from the toolbar has none: it is a sub-heading of the
 * section it sits in, and is printed as a block like any other, its honesty mark drawn. An untagged H2 still starts a
 * section where there is no section for it to belong to: in a document with no tagged H2 at all (an older proposal),
 * and above the first tagged one.
 *
 * opts = { title, dateStr, images: {diagram number: filename}, sidebars: {section: {questions}}, evidence }
 */
function proposalData(value, opts) {
  const o = opts || {};
  const images = o.images || {};
  const sidebars = o.sidebars || {};
  const top = printedBlocks(value || []);
  const sections = [];
  const questionsOf = (key) => ((sidebars[key] && sidebars[key].questions) || []);
  let cur = null;          // the section being filled; blocks before the first heading get one with no heading
  let key = "";            // its section key
  // The section's questions still waiting for their number, numbered exactly as the column numbers them. pdfRuns
  // strikes one off when its anchor lands; whatever is left never gets a number, which is the designed outcome for a
  // stale or absent anchor rather than a failure.
  let pending = [];
  let vizN = 0;
  const add = (b) => {
    if (!cur) sections.push((cur = { section: "", heading: null, questions: questionsOf("").map(pdfQuestion), blocks: [] }));
    cur.blocks.push(b);
  };
  const tagged = top.some((e) => !isPrintedList(e) && e.type === "h2" && e.section);
  for (const node of top) {
    if (isPrintedList(node)) { add(pdfList(node, pending)); continue; }
    if (node.type === "h1") continue;                                   // the cover owns the title
    if (node.type === "h2" && (node.section || !tagged || !key)) {
      key = node.section || "";
      const runs = pdfRuns(node.children);                              // no pending: a number never sits in a heading
      const qs = questionsOf(key);
      pending = qs.map((q, i) => ({ n: i + 1, anchor: (q && q.anchor) || "" })).filter((x) => x.anchor);
      sections.push((cur = { section: key, heading: runs.length ? runs : key ? [{ text: key, marks: [] }] : null, questions: qs.map(pdfQuestion), blocks: [] }));
      continue;
    }
    if (node.type === "viz" || node.type === "systems_map") {
      const block = node.type === "viz" ? (node.block ?? null) : null;
      const file = images[vizN];
      vizN += 1; // counted for every diagram, a Mermaid one too, so picture N stays with diagram N
      const label = key ? `Summary: ${SECTIONS.findIndex((s) => s.key === key) + 1} · ${secTitle(key)}` : "Summary";
      // What a screen reader announces for the picture: its own caption, else the label a Mermaid diagram prints.
      const alt = String((block ? block.caption : node.data && node.data.caption) || "").trim() || label;
      // ⭐ A Mermaid diagram (NotebookLM's own) is drawn by Typst from its code, under the label.
      if (block && block.type === "mermaid" && typeof block.code === "string" && block.code.trim()) {
        add({ type: "mermaid", code: nbFixMermaid(block.code.trim()), caption: label, alt });
      } else if (file) {
        // A flow or the problem mindmap takes a page of its own, at the full text width (proposal.typ says why).
        add({ type: "picture", file, alt, page: !!block && (block.type === "flow" || block.type === "dendrogram") });
      } else add({ type: "missing" });
      continue;
    }
    const b = pdfBlock(node, pending);
    if (b) add(b);
  }
  return {
    title: String(o.title || "Proposal"),
    date: String(o.dateStr || ""),
    mermaid: { theme: MERMAID_THEME, css: MERMAID_CSS },
    sections,
    evidence: (o.evidence || []).map(pdfEvidence),
  };
}

/** One evidence card's page, as data: why it was picked, then the card as aikyam.school holds it. */
function pdfEvidence(e) {
  const c = e.card || {};
  const s = (v) => (v == null ? "" : String(v));
  return {
    section: secTitle(e.section), title: s(c.title || e.slug), why: s(e.why), says: s(e.proposalSays),
    findings: s(c.exactFindings), who: s(c.sampleSize), design: s(c.studyDesign), level: s(c.evidenceLevel),
    words: s(c.verbatimProof), paper: s(c.source), url: cardUrl(e.slug),
  };
}
/** The picked findings in section order, best-scoring first within a section. */
function evidenceInOrder() {
  const rank = (k) => { const i = SECTIONS.findIndex((s) => s.key === k); return i < 0 ? SECTIONS.length : i; };
  return ((docState && docState.evidence) || []).map((e, i) => ({ e, i }))
    .sort((a, b) => rank(a.e.section) - rank(b.e.section) || a.i - b.i).map((x) => x.e);
}

/* ---- PDF export: the run ---------------------------------------------------------------------- */

let exporting = false;   // NOT `exportBusy` — that name belonged to the deleted window.print() export

/**
 * Readies the long page for the capture, runs `fn`, and puts the view back exactly as it was.
 *
 * A diagram is rasterized from the LIVE DOM, and an element with a zero-size box captures as a blank
 * image rather than failing. The long page (Stage 6: the capture view, as exportPDF pictures its
 * diagrams) shows every section at once. `body.charts-hidden`, the Show-Visuals class, is cleared for the
 * capture as a safeguard; nothing on the long page reads it on screen. (A collapsed problem-driver
 * group went on 2026-09-24 — the problem is a mindmap now, and a mindmap has nothing to collapse.) The two
 * frames are waited for, and Highlight view is on for the capture: it is what colours a diagram's gaps,
 * assumed links and measures in its picture (app.html `body.fund-review .viz-block …`), and the pictures
 * are the PDF's.
 *
 * ⭐ None of this dirties the document. The PDF's honesty glyphs come from the template, not from CSS.
 */
// The width TipTap's diagrams were drawn and captured at up to v15 (fund/v15): .doc-inner's content width in the document
// column `col` — its max-width 780 px less its 28 px sides, 724 at desktop width. .doc-inner itself is hidden since
// Stage 6, so a bare one is laid out at the end of the column for this one read and removed before anything paints.
function pictureWidth(col) {
  const probe = document.createElement("div");
  probe.className = "doc-inner";
  col.appendChild(probe);
  const cs = getComputedStyle(probe);
  const width = probe.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
  probe.remove();
  return width;
}
async function withExportView(fn) {
  const col = document.querySelector(".doc-col");
  const prev = {
    review: document.body.classList.contains("fund-review"),
    charts: document.body.classList.contains("charts-hidden"),
    scroll: col ? col.scrollTop : 0,
  };
  // The pictures are captured at the width TipTap's diagrams had up to v15 (pictureWidth): the long page's holders take
  // it while the export runs (app.html #plateRoot.fund-export) and give it back after, so the page on screen is
  // unchanged outside the export.
  const root = $("plateRoot"), width = col && root ? pictureWidth(col) : 0;
  let widened = false;
  try {
    setChartsHidden(false);
    setHighlightView(true);
    if (width > 0) { root.style.setProperty("--fund-export-width", `${width}px`); root.classList.add("fund-export"); widened = true; }
    await new Promise(requestAnimationFrame);
    await new Promise(requestAnimationFrame);   // two frames: style, then layout
    return await fn();
  } finally {
    if (widened) { root.classList.remove("fund-export"); root.style.removeProperty("--fund-export-width"); }
    setHighlightView(prev.review);
    setChartsHidden(prev.charts);
    if (col) col.scrollTop = prev.scroll;
  }
}

/**
 * Picture numbers for the PDF export: painter → N, where `viz-N.png` is diagram N.
 *
 * `painters` is in document order (the long page's diagrams, page.handle.diagrams). Only TOP-LEVEL diagrams get a
 * number, because the walker walks only the document's top-level nodes and counts only those diagrams; one
 * nested in a list item, quote or table cell is never printed, and numbering it would shift every
 * later picture onto the wrong diagram.
 */
function vizImageIndices(painters, isTopLevel) {
  const idx = new Map();
  for (const p of painters) if (isTopLevel(p)) idx.set(p, idx.size);
  return idx;
}

/** Paints every diagram in `painters` (the long page's). Returns the ones still empty afterwards,
 * which must not be captured.
 * ⛔ Each paint is bounded. The problem mindmap waits on a network fetch (Mermaid, from vendor/), and a
 * stalled fetch would otherwise hold the export — and its button — for good. A paint that runs past
 * 20 s counts as missed, so the export finishes with that diagram marked as not drawn. */
async function paintAllViz(painters) {
  const missed = [];
  for (const p of painters) {
    if (p.kind() === "mermaid") continue; // drawn by Typst in the PDF, so it need not be painted for it
    await Promise.race([p.paint(), new Promise((r) => setTimeout(r, 20000))]);
    const first = p.holder && p.holder.firstElementChild; // a long-page diagram with no component has no holder
    if (!first || first.classList.contains("viz-empty")) missed.push(p);
  }
  return missed;
}

/**
 * One diagram → PNG bytes.
 *
 * `domToBlob`, not `domToPng`: the latter returns a base64 data URL we would immediately decode
 * again. `font: false` because the PDF's text comes from Typst, not from the raster — and it also
 * stops modern-screenshot fetching webfonts, which `connect-src 'self'` would block silently.
 */
async function rasterizeViz(holder) {
  const { domToBlob } = await import("modern-screenshot");
  // ⛔ Never hand an <svg> element to domToBlob. With an SVG as the target, modern-screenshot
  // serialises it into a data URL and embeds it as a bare <image>, and that serialisation carries no
  // styles at all: every `.fl-*` rule lives in a stylesheet, so the clone gets `fill` and
  // `text-anchor` and `font-family` from the SVG defaults instead. The flow diagram came out with
  // solid BLACK node boxes, their labels invisible inside them, the visible overflow in Times, and
  // the column heads neither uppercase nor grey. The HTML wrapper takes the foreignObject path,
  // which inlines each element's computed style — which is why the four goal grids (since removed), all HTML,
  // were correct in the same PDF. Measured side by side against this markup, 2026-09-19.
  //
  // The wrapper also carries a transparent layer of Carbon <cds-tooltip> elements over the picture
  // for its hit targets. They are Lit custom elements with shadow roots, they contribute nothing
  // visible, and cloning one mid-lifecycle is a good way to break the capture — so `filter` drops
  // that layer rather than the whole wrapper. `.fl-legend` sits outside `.fl-wrap`, so it stays out
  // of the raster the way it always has.
  const flow = holder.querySelector(".fl-wrap");
  const target = flow || holder;
  // A flow diagram is printed across the full 249mm text width, not the 145mm column, so the same
  // capture spreads over 1.7× the paper and its effective resolution falls with it. Measured on the
  // real export: 254 ppi at column width, 143 ppi full width — under the 150 ppi floor for print.
  // Doubling the capture scale for that case alone puts it back at ~286 ppi. The HTML diagrams stay at
  // 2; they are already 254 ppi where they sit, and there is no reason to pay for pixels twice.
  // The problem mindmap prints at the same full width as the flow, so it gets the same capture scale.
  const scale = flow || holder.querySelector(".mm-wrap") ? 4 : 2;
  // ⛔ Web fonts ARE embedded (the library's default). An earlier version passed `font: false` on the
  // reasoning that the PDF's text comes from Typst anyway — but the text INSIDE a diagram is part of
  // the picture, and a rasterizer renders its clone through an <img>, which cannot reach the page's
  // webfonts. With embedding off, every diagram label came out in the -apple-system fallback unless
  // IBM Plex happened to be installed at OS level. That is the same silent-wrong-typeface failure
  // typst-service's server.js refuses to boot with, so it is not acceptable here either. The fonts are
  // same-origin (vendor/fonts/*.woff2), which `connect-src 'self'` permits.
  const blob = await domToBlob(target, {
    type: "image/png",
    scale,
    backgroundColor: "#ffffff",
    timeout: 20000,
    filter: (node) => !(node.classList && node.classList.contains("fl-tips")),
  });
  return new Uint8Array(await blob.arrayBuffer());
}

const b64 = (bytes) => {
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(s);
};

/** Owner-only. Builds the proposal's data, rasterizes the diagrams, and downloads what the service returns. */
async function exportPDF() {
  if (exporting) return;
  // No viewer check: every signed-in viewer of a document is its owner since peer review went
  // (2026-09-21), and RLS decides who can open a document at all. The admin condition that used
  // to sit here is GONE (owner's decision, 2026-09-18) — it was
  // explicitly a first cut while the output was unproven, and it had the effect of hiding the
  // feature from the owner's own coach account, which is the account that writes the proposals.
  // The PDF prints from the long page (Stage 6), whatever the row's format. With no page mounted for this proposal
  // (its bundle still loading, or failed) it is refused: there is no other page to picture the diagrams from.
  const exportable = () => !!docState && !!mountedPage();
  if (!exportable()) { toast("Nothing to export yet"); return; }

  // ⛔ The busy flag is claimed BEFORE the first await, not after it. `flushSave()` yields, so a
  // second click landing while a save is in flight used to pass the `if (exporting)` check above and
  // run the whole export again — two compiles on the service, two downloads. Everything from here on
  // is inside the try/finally that clears it.
  exporting = true;
  try {
    // The PDF renders docState.doc, which is only refreshed inside saveDoc() (:175). Typing debounces
    // that by 800ms (onDocUpdate, :212), so exporting mid-debounce would silently render the PREVIOUS
    // text — in the one artefact that leaves the app and reaches a funder. Same guard every other
    // leave-this-state path uses (:1362, :1422, :1663, :3724, :4090); on a failed save flushSave()
    // toasts and returns false, which aborts the export rather than handing over a stale file. It is
    // INSIDE the try so that abort still clears the busy flag on the way out.
    if (!(await flushSave())) return;
    // The page is picked after the save: it can come down while the save is out (home, the session's end), and
    // then the check above is made again rather than printing a value that is no longer there.
    const page = mountedPage();
    if (!exportable()) { toast("Nothing to export yet"); return; }

    toast("Preparing the PDF…", true);
    const { data, images, missed } = await withExportView(async () => {
      // The long page: its value, printed by the walker over Plate, and its own diagrams for that value, top-level
      // only and in the walker's order.
      const value = page.handle.getValue();
      const painters = page.handle.diagrams(value).map((d) => d.painter);
      const missedPainters = await paintAllViz(painters);
      const skip = new Set(missedPainters);
      const imgs = {};
      // must match the walker's own count: top-level diagrams only (the page lists no other), in document order.
      const topLevel = () => true;
      const indices = vizImageIndices(painters, topLevel);
      for (const [p, idx] of indices) {
        if (p.kind() === "mermaid") continue; // Typst draws it from its code — no picture needed
        if (skip.has(p)) continue;   // a blank holder captures as a blank PNG — leave it to the placeholder
        try { imgs[idx] = { name: `viz-${idx}.png`, bytes: await rasterizeViz(p.holder) }; }
        catch (e) { console.error("[fund] viz capture failed", e); }
      }
      const names = {};
      for (const k of Object.keys(imgs)) names[k] = imgs[k].name;
      return {
        data: proposalData(value, {
          title: docState.name || "Proposal",
          dateStr: new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }),
          shortId: String(currentDocId || "").slice(0, 8),
          images: names,
          sidebars: exportSidebars(),
          evidence: evidenceInOrder(),
        }),
        images: imgs,
        missed: missedPainters.filter(topLevel).length, // a nested diagram is never printed, so it is not "missing" from the PDF
      };
    });

    window.__lastProposalData = data;   // so a rejected document can be compiled by hand against proposal.typ
    toast("Building the PDF…", true);

    const payload = { version: PROPOSAL_TEMPLATE, data, images: {} };
    for (const k of Object.keys(images)) payload.images[images[k].name] = b64(images[k].bytes);

    const res = await fetch("/_typst/proposal", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const detail = await res.text();
      console.error("[fund] typst rejected the document:", detail, "\ndata on window.__lastProposalData");
      let first = "";
      try { first = String(JSON.parse(detail).error || "").split("\n")[0]; } catch { first = ""; }
      toast(first ? "The PDF could not be built — " + first : "The PDF could not be built. Try again in a moment.", true);
      return;
    }

    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${String(docState.name || "proposal").replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "-")}-${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 10000);

    // A missing diagram is named, never swallowed — the PDF shows a marked placeholder in its place.
    toast(missed ? `Exported — ${missed} diagram(s) could not be drawn` : "Exported");
  } catch (e) {
    console.error("[fund] export failed", e);
    toast("The PDF could not be built. Try again in a moment.", true);
  } finally {
    exporting = false;
  }
}

// build the 8-section doc from the synth result — section HTML carried verbatim (TipTap sanitizes on parse)
function assembleSynthDoc(name, parsed) {
  const sec = parsed.sections || {};
  let html = `<h1>${esc(name)}</h1>`, missing = [];
  const summaries = parsed.sectionSummaries || {};
  for (const s of SECTIONS) {
    const body = String(sec[s.key] || "").trim();
    if (!body) missing.push(s.key);
    const lead = String(summaries[s.key] || "").trim(); // the import's one-line summary of what this section says
    html += `<h2 data-section="${esc(s.key)}">${esc(s.title)}</h2>`
      + (lead ? `<p class="section-lead">${esc(lead)}</p>` : "")
      + (body || "<p></p>");
  }
  // NOTE: `unused_source_notes` is deliberately NOT added to the editable doc — it may carry quarantined
  // injection from the source file. It is kept read-only on the record (docState.unused), so it never reaches the LLM payloads.
  return { html, missing };
}

/* ---- File menu (opens on the logo; consolidates nav + Sign out) ---- */
async function signout() {
  isAdminCached = null; roleCache = null;
  // The long page and its session drafts go with the account (endPlateSession says in what order). The
  // SIGNED_OUT that signOut() fires runs it again, which finds nothing left to do.
  endPlateSession();
  await supabase.auth.signOut(); showSignin(); toast("Signed out");
}
function fileMenuItems() {
  const inApp = !$("app").classList.contains("hidden");
  const items = [
    { label: "Your proposals", icon: "c-document", fn: showHome },
  ];
  // Export as PDF — any owner with a proposal open.
  // ⛔ No admin condition. There was one while the output was unproven, and it hid the feature from
  // shemeer@folktaler.com — the `coach` account the proposals are actually written in — while only
  // shemeer@aikyamhq.com could see it. Removed 2026-09-18, owner's decision, after reading four
  // drafts of the output.
  if (inApp) {
    items.push({ sep: true }, { label: "Find Card", icon: "c-document", fn: findCards },
      { label: "Export as PDF", icon: "c-document", fn: exportPDF });
  }
  items.push({ sep: true });
  items.push({ label: "Sign out", icon: "c-close", fn: signout });
  return items;
}
// Highlight view: lights every suggestion/placeholder/coach-added mark in one --warn colour; clean when off.
// Its one caller is the PDF export (withExportView). The topbar's #hlToggle went with TipTap (Stage 8); a toggle that
// is not in the page is left alone.
function setHighlightView(on) {
  document.body.classList.toggle("fund-review", on);
  const t = $("hlToggle"); if (t && !!t.toggled !== on) t.toggled = on;
}
// Show Visuals, the #chartsToggle counterpart of setHighlightView: `hidden` hides the diagrams. Its one caller is
// the PDF export, which clears it for the capture and puts it back; #chartsToggle went with TipTap (Stage 8).
function setChartsHidden(hidden) {
  document.body.classList.toggle("charts-hidden", hidden);
  const t = $("chartsToggle"); if (t && !!t.toggled === hidden) t.toggled = !hidden;
}
function openFileMenu() {
  const m = $("fileMenu"); m.innerHTML = "";
  fileMenuItems().forEach((it) => {
    if (it.sep) { const d = document.createElement("div"); d.className = "menu-sep"; m.appendChild(d); return; }
    const b = document.createElement("button");
    b.className = "menu-item"; b.setAttribute("role", "menuitem");
    b.innerHTML = `<svg class="ico"><use href="#${it.icon}"/></svg>${esc(it.label)}`;
    b.onclick = () => { closeFileMenu(); it.fn(); };
    m.appendChild(b);
  });
  m.classList.remove("hidden");
  $("brand").setAttribute("aria-expanded", "true");
  m.querySelector(".menu-item")?.focus(); // move focus into the menu so keyboard users land on the first item
}
function closeFileMenu() {
  $("fileMenu").classList.add("hidden");
  const b = $("brand"); if (b) b.setAttribute("aria-expanded", "false");
}
function toggleFileMenu() {
  if ($("fileMenu").classList.contains("hidden")) openFileMenu(); else closeFileMenu();
}

/* ---- account affordance (topbar, top-right): avatar + email + caret → account menu (spec: _authmock.html option A) ---- */
let acctEmail = "";
function acctInitials(email) {
  const local = String(email || "").split("@")[0].replace(/[^a-zA-Z0-9]/g, "");
  return (local.slice(0, 2) || "?").toUpperCase();
}
// read the session and paint the topbar account widget: signed-in → avatar+email; signed-out → Sign in button
async function renderAccount() {
  const wrap = $("acctWrap"); if (!wrap) return;
  const s = await getSession();
  if (s && s.user) {
    acctEmail = s.user.email || "";
    $("acctEmail").textContent = acctEmail;
    $("acctAvatar").textContent = acctInitials(acctEmail);
    $("acctBtn").classList.remove("hidden");
    $("acctSignin").classList.add("hidden");
  } else {
    acctEmail = "";
    $("acctBtn").classList.add("hidden");
    $("acctSignin").classList.remove("hidden");
  }
}
function openAcctMenu() {
  const m = $("acctMenu"); m.innerHTML = "";
  const head = document.createElement("div"); head.className = "menu-id";
  head.innerHTML = `<div class="who">Signed in as</div><div class="em">${esc(acctEmail)}</div>`;
  m.appendChild(head);
  [{ label: "Your proposals", icon: "c-document", fn: showHome }, { label: "About Fundable", icon: "c-idea", fn: showAbout }, { label: "Sign out", icon: "c-close", fn: signout }].forEach((it) => {
    const b = document.createElement("button");
    b.className = "menu-item"; b.setAttribute("role", "menuitem");
    b.innerHTML = `<svg class="ico"><use href="#${it.icon}"/></svg>${esc(it.label)}`;
    b.onclick = () => { closeAcctMenu(); it.fn(); };
    m.appendChild(b);
  });
  m.classList.remove("hidden");
  $("acctBtn").setAttribute("aria-expanded", "true");
}
function closeAcctMenu() {
  const m = $("acctMenu"); if (m) m.classList.add("hidden");
  const b = $("acctBtn"); if (b) b.setAttribute("aria-expanded", "false");
}
function toggleAcctMenu() {
  if ($("acctMenu").classList.contains("hidden")) openAcctMenu(); else closeAcctMenu();
}

/* ---- wire up ---- */
syncTitle();
setInterval(paintSaveSign, 250); // the save sign follows the page's own save state (paintSaveSign)
// What the editor's own change callback can miss or see late: DOM input, and input-method composition (display only).
// Only events inside the editor ([data-slate-editor]) count; nothing here paints inside the event's own handler.
const signEditor = (e) => (e.target instanceof Element ? e.target.closest("[data-slate-editor]") : null);
const signBlock = () => { const n = getSelection().anchorNode; const at = n && (n.nodeType === 1 ? n : n.parentElement); return at && at.closest("[data-slate-node='element']"); };
for (const [type, note] of [
  ["input", () => { signStamp(); }],
  ["compositionstart", () => { const block = signBlock(); signCompose = { live: true, changed: false, endedAt: 0, handle: (mountedPage() || {}).handle, block, text: block ? block.textContent : null }; }],
  ["compositionend", (e) => {
    signStamp(); // a change even when its composition's wait has ended (a compositionend after a blur)
    if (!signCompose) return;
    const { block, text } = signCompose;
    // Only the composing block is read: a cancelled composition leaves its text as it was.
    Object.assign(signCompose, { live: false, endedAt: signTypedAt, changed: block && block.isConnected ? block.textContent !== text : !!e.data });
  }],
  ["focusin", () => { if (signCompose) signCompose.blurAt = 0; }], // back in the editor: the blur no longer ends the wait
  ["focusout", (e) => { if (signCompose && !signEditor(e).contains(e.relatedTarget)) signCompose.blurAt = performance.now(); }], // a blur ends the wait, 300 ms on (Slate may still apply)
  // A key pressed outside any composition while one still reads live: its compositionend never came, so it is over.
  ["keydown", (e) => { if (signCompose && signCompose.live && !e.isComposing && e.keyCode !== 229) { signStamp(); Object.assign(signCompose, { live: false, endedAt: signTypedAt, changed: true }); } }],
]) document.addEventListener(type, (e) => { if (signEditor(e)) { note(e); setTimeout(paintSaveSign, 0); } }, true);
$("brand").onclick = (e) => { e.stopPropagation(); toggleFileMenu(); };
$("acctBtn").onclick = (e) => { e.stopPropagation(); toggleAcctMenu(); };
$("acctSignin").onclick = () => showSignin();
$("homeSearch").addEventListener("cds-search-input", (e) => { homeQuery = e.detail.value; renderHomeList(); });
$("homeSort").addEventListener("cds-dropdown-selected", (e) => { homeSort = e.detail.item?.value ?? e.target.value; renderHomeList(); });
$("cfCancel").onclick = closeConfirm;
$("cfDelete").onclick = doDelete;
$("confirmDel").addEventListener("cds-modal-closed", () => { pendingDelId = null; }); // ESC / backdrop / close-button
$("npErr").open = false; // Carbon's own default is open=true — hold it closed until an error actually fires (spec §2)
$("btnNew").onclick = () => openNewProp();
$("npCancel").onclick = closeNewProp;
$("npSubmit").onclick = submitNewProp;
// ⛔ Debounced, not direct. npHelperFor parses the paste and renders all eight sections, and at
// NP_MAX that is megabytes of work — running it per keystroke made the textarea unusable after a
// large paste. The count is a report on the paste, so it can settle a moment after typing stops.
$("npBody").addEventListener("input", npUpdateCountSoon);
$("lpTopSignin").onclick = revealSignin;
$("lpBigSignin").onclick = revealSignin;
$("abBack").onclick = () => showHome();
// AUDIT: #32 — the only warning-free loss window before this was the ~1s autosave debounce; when saving
// is FAILING (offline, or the session died mid-edit) that window stretched to minutes with no signal but
// a stale toast. Warn on close whenever there's something that hasn't made it to the server yet.
window.addEventListener("beforeunload", (e) => { if (unsavedPending()) e.preventDefault(); });
// The click target is the inner <a href="#">, not the surrounding <p> — a bare anchor with no href
// is not Tab-reachable and is not announced as a link (design review). preventDefault keeps the "#"
// out of the URL.
$("siBtn").onclick = onSigninClick;
$("siForgot").onclick = onForgotClick;
$("siEmail").addEventListener("keydown", (e) => { if (e.key === "Enter") onSigninClick(); });
$("siPass").addEventListener("keydown", (e) => { if (e.key === "Enter") onSigninClick(); });
$("siNew").addEventListener("keydown", (e) => { if (e.key === "Enter") onSigninClick(); });
// Esc closes the File menu (the results panel is always-open — not closeable by design)
document.addEventListener("keydown", (e) => {
  if (e.key !== "Escape") return;
  if ($("confirmDel").open) { closeConfirm(); return; }
  if ($("newProp").open) { closeNewProp(); return; }
  if (!$("fileMenu").classList.contains("hidden")) { closeFileMenu(); return; }
  if (!$("acctMenu").classList.contains("hidden")) { closeAcctMenu(); return; }
});
// click anywhere outside closes the File menu / account menu
document.addEventListener("click", (e) => {
  if (!$("fileMenu").classList.contains("hidden") && !$("fileMenu").contains(e.target) && e.target !== $("brand")) closeFileMenu();
  if (!$("acctMenu").classList.contains("hidden") && !$("acctMenu").contains(e.target) && !$("acctBtn").contains(e.target)) closeAcctMenu();
});

boot();

// console-poking surface — NOT exposed in prod (ships the live client). Enable for debugging with
// localStorage.setItem("fund.debug","1") then reload; on localhost it's always on.
if (location.hostname === "localhost" || location.hostname === "127.0.0.1" || localStorage.getItem("fund.debug") === "1") {
  window.__fund = { supabase, renderVizBlock, showHome, openDoc, get docState() { return docState; }, get currentDocId() { return currentDocId; } };
}
